/**
 * Help Center Guide Definitions
 *
 * Each guide is a structured object with steps, screenshots, and tips.
 * Screenshots are stored in /public/help/ and referenced by path.
 */

import type { UserRole } from "@/types/auth";

/** The school office: the default audience of a guide. */
export const OFFICE_ROLES: readonly UserRole[] = [
  "super_user",
  "admin",
  "principal",
  "staff",
];
const OFFICE_AND_TEACHERS: readonly UserRole[] = [...OFFICE_ROLES, "teacher"];
const OFFICE_AND_PARENTS: readonly UserRole[] = [...OFFICE_ROLES, "parent"];
const EVERYONE: readonly UserRole[] = [...OFFICE_ROLES, "teacher", "parent"];

export interface GuideStep {
  title: string;
  description: string;
  screenshot?: string;
  tip?: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface TroubleshootingItem {
  problem: string;
  solution: string;
}

export interface Guide {
  slug: string;
  title: string;
  description: string;
  category: string;
  /**
   * Roles the guide is written for, and the only roles that see it in the
   * Help Center, search, contextual help and at its URL. Leave it out for
   * an office guide (OFFICE_ROLES). A teacher or parent guide lists the
   * office roles too, so staff can see what their teachers and parents read.
   */
  audience?: readonly UserRole[];
  icon: string;
  estimatedTime: string;
  steps: GuideStep[];
  /**
   * Slugs of related guides shown at the bottom of the article.
   * Curated, not auto-generated — a good "next read" beats a random list.
   */
  relatedGuides?: string[];
  /**
   * What the user should do if the page is empty / has no data yet.
   * Shown as a callout BEFORE the steps. Use for any guide where a brand-new
   * tenant wouldn't see anything to do.
   */
  emptyState?: string;
  /** Common problems and their fixes. Rendered as an accordion. */
  troubleshooting?: TroubleshootingItem[];
  /** Frequently asked questions about this feature. */
  faq?: FaqItem[];
  /**
   * Last meaningful update to this guide's content. Surfaced as
   * "Last updated <date>" in the article header so readers know how
   * fresh the screenshots and steps are.
   */
  lastUpdated?: string;
}

export interface GuideCategory {
  key: string;
  label: string;
  description: string;
  icon: string;
}

export const categories: GuideCategory[] = [
  {
    key: "getting-started",
    label: "Getting Started",
    description: "Find your way around IlmIQ and learn the everyday basics.",
    icon: "RocketLaunch",
  },
  {
    key: "campus-schedule",
    label: "Campus & Schedule",
    description:
      "Campuses, the school calendar, academic years, and terms. Campuses and School Calendar are under Operations in the sidebar.",
    icon: "LocationOn",
  },
  {
    key: "classes",
    label: "Classes & Teachers",
    description: "Create classes, assign teachers, and manage schedules.",
    icon: "Class",
  },
  {
    key: "curriculum",
    label: "Curriculum",
    description:
      "Build your school's course catalog: subjects, units, lessons, and resources.",
    icon: "AutoStories",
  },
  {
    key: "academics",
    label: "Academics",
    description:
      "Track lesson progress, run assignments and quizzes, log behavior, and freeze report cards.",
    icon: "School",
  },
  {
    key: "community",
    label: "Community",
    description: "Run fundraising campaigns and potluck sign-up sheets.",
    icon: "Campaign",
  },
  {
    key: "messaging",
    label: "Portals & Messaging",
    description:
      "What teachers and parents see in their portals, and how to message between school and home.",
    icon: "Forum",
  },
  {
    key: "students",
    label: "Students & Families",
    description: "Enroll students, manage families, and track guardians.",
    icon: "People",
  },
  {
    key: "admissions",
    label: "Admissions",
    description: "Configure applications and manage the admissions process.",
    icon: "Assignment",
  },
  {
    key: "billing",
    label: "Billing & Payments",
    description: "Set up fees, generate invoices, and process payments.",
    icon: "Receipt",
  },
  {
    key: "attendance",
    label: "Attendance",
    description: "Take daily attendance and set up attendance sessions.",
    icon: "EventNote",
  },
  {
    key: "hifdh",
    label: "Hifdh & Quran",
    description: "Set each student's plan, rate the day's Sabaq, Sabqi and Manzil, and publish monthly report cards.",
    icon: "MenuBook",
  },
  {
    key: "reports",
    label: "Reports & Analytics",
    description:
      "Financial, academic, and operational reports, plus Analytics. Both are under Insights in the sidebar.",
    icon: "Assessment",
  },
  {
    key: "settings",
    label: "Settings & Admin",
    description:
      "Manage users and roles, notifications, school settings, and staff time.",
    icon: "Settings",
  },
];

// =============================================
// GETTING STARTED
// =============================================

const gettingStartedGuides: Guide[] = [
  {
    slug: "dashboard-overview",
    lastUpdated: "2026-10-13",
    relatedGuides: ["search-and-shortcuts", "navigating-with-detail-drawers"],
    emptyState:
      "First time? The dashboard fills in as your school operates. A \"Get Started with IlmIQ\" checklist walks you through five setup steps: create a campus, set up a class, add a family & student, configure fee types, and take your first attendance. Steps tick themselves off from your school's real data: \"Set up a class\" ticks once your school has a class, and \"Add a family & student\" once a family has at least one student, whether or not anyone is enrolled yet. You can dismiss the checklist with the X at any time. It then stays hidden for this school on this browser.",
    faq: [
      {
        question: "How fresh is the data?",
        answer:
          "The metric cards refresh on their own every 5 minutes, and straight away when you reload the page. The Recent Activity feed doesn't refresh on a timer. It updates when you come back to the tab or reopen the dashboard, so a brand-new event can take a couple of minutes to appear.",
      },
      {
        question: "Who sees the dashboard?",
        answer:
          "Admins and principals. It shows the school's financial figures, so staff don't get it: staff sign in to Attendance, with today's sessions ready to mark, and the Dashboard link isn't in their sidebar.",
      },
      {
        question: "Can I customize which cards appear?",
        answer:
          "No. The dashboard layout is fixed, so you can't add, hide or move cards.",
      },
    ],
    title: "Understanding Your Dashboard",
    description:
      "Learn how to read your dashboard metrics and use it to stay on top of your school operations.",
    category: "getting-started",
    icon: "Dashboard",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Quick Stats",
        description:
          "Under the greeting, the top row shows your key numbers at a glance: active Students, Families, Staff, and Classes. The Classes count is active classes with at least one enrolled student — empty classes aren't counted (hover the card for the exact definition). Click a card to jump to that section. If your school has more than one campus, Students, Families, and Classes follow the campus selected at the top of the screen (Staff is always school-wide).",
        screenshot: "/help/dashboard-quick-stats.png",
        tip: 'To go straight to a student, family, or invoice, use the search box at the top of the page. See "Search and keyboard shortcuts".',
      },
      {
        title: "Financial Health",
        description:
          "Financial Health shows four cards: Outstanding (with a 6-month sparkline of invoiced revenue), Overdue (with a clickable aging bar — 1-30, 31-60, 61-90, and 90+ days — that drills into the matching invoices), Collection Rate (switchable between 30-day, month-to-date, year-to-date, and all-time windows), and On-Time Rate (of invoices issued in the last 60 days that are now past their due date, the share paid in full on time; it reads \"Not enough data yet\" until there are some). Schools with sponsor funding enabled also see a sponsor Receivable card. A healthy school should aim for 90%+ collection rate.",
        screenshot: "/help/dashboard-financial.png",
      },
      {
        title: "Fundraising",
        description:
          "While a fundraising campaign is running, admins and principals see a Fundraising band showing the all-time amount raised, a progress bar per active campaign, and a \"Manage campaigns\" link. Campaign gifts are tracked separately from tuition, so they never distort your revenue or collection figures. No active campaign — no band.",
      },
      {
        title: "Action Items",
        description:
          "Action Items highlight things that need your attention: pending applications to review, today's attendance (the card names how many sessions are still to mark, and says \"No classes to mark today\" when there are none), and families with overdue invoices. When there's nothing pending, the section header reads \"All Clear\" and the band dims. Click any card to jump directly to that section.",
        screenshot: "/help/dashboard-actions.png",
      },
      {
        title: "School Growth",
        description:
          "The School Growth table compares this month, last month, this year, last year and all time. Every period follows your school's time zone, so a payment made late on the last evening of a month counts in that month (the Collection Rate card uses the same calendar). Tap or hover the info button next to a column for its definition. Class enrollments counts the class enrollments created in the period, whatever their status now (active, paused, completed or withdrawn); a student in two classes counts twice, so the number of students is shown underneath, and a transfer counts once. Collected is the family payments received in the period minus refunds and chargebacks, dated by payment date; sponsor payments such as TEFA are not included. It uses the same rule as Net on the Collections report, but that report counts UTC days, so a payment late on a month's last evening can land in the next month there and totals can differ at month ends. It includes partner fees collected on behalf of a partner institution, which the school passes on, shown underneath as \"incl. $… in partner fees\". Discounts counts discount lines, sibling discounts, mid-month proration, student discounts such as Zakat and discounts added with Apply Discount, on invoices issued in the period: a discount added today to a September invoice counts in September. Billing corrections, withdrawal credits and late-fee removals are credits, not discounts, unless the credit was given as a discount (linked to a discount rule). The Billed vs Collected report counts money by the month it was billed for, not the month it was paid, so its months differ from these; over all time its Partner collected matches the partner fees here.",
        screenshot: "/help/dashboard-growth.png",
      },
      {
        title: "Recent Activity",
        description:
          "The Recent Activity feed shows the last 5 noteworthy events — enrollments, payments, invoices issued or paid, refunds, application decisions, and similar. Admins and principals can click any row to open the full Audit Log.",
        screenshot: "/help/dashboard-activity.png",
      },
    ],
  },
  {
    slug: "navigating-with-detail-drawers",
    lastUpdated: "2026-10-13",
    relatedGuides: ["dashboard-overview", "add-family-students", "generate-invoices"],
    faq: [
      {
        question: "Why drawers instead of opening a new page?",
        answer:
          "Drawers preserve the list you came from — your filters, scroll position, and selection are untouched. You can peek at a related record without losing your place. For sustained work on a single record, every drawer has a footer button that takes you to the full detail page.",
      },
      {
        question: "How do I close the drawer?",
        answer:
          "Click \"Close\" in the drawer's footer, the X in its header, or anywhere outside the drawer, or press Escape. Your list stays as you left it.",
      },
      {
        question: "Do drawers work on mobile?",
        answer:
          "Yes. On a phone the drawer fills the whole screen. On a tablet or computer it slides in as a side panel, and you can still see the list behind it.",
      },
      {
        question: "Can I open a drawer from inside another drawer?",
        answer:
          "Not today. Links inside a drawer take you to the full page for that record. If you need to look at two records side by side, middle-click or Ctrl-click (Cmd-click on a Mac) the drawer's footer button to open the full page in a new tab.",
      },
    ],
    title: "Navigating with Detail Drawers",
    description:
      "How to use the in-place detail drawers that appear across students, families, classes, payments, and invoices — and when to use them vs. opening a full page.",
    category: "getting-started",
    icon: "ViewSidebar",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Where drawers appear",
        description:
          "Drawers open in place when you click a related record. Some examples: on the Payments list, clicking a payment opens the Payment drawer. On the Students list, clicking a family name opens the Family drawer. On a class's Enrolled Students card, the behavior incidents page, or a family's page, clicking a student opens the Student drawer. On a family statement, clicking an invoice number opens the Invoice drawer. On an application, clicking the student, family, or class opens its drawer, and on an enrollment, the student or class does. The page underneath stays exactly as you left it.",      },
      {
        title: "What you see inside the drawer",
        description:
          "The drawer surfaces the same key facts as the full detail page — status chips, amounts, related records, recent activity — but it's primarily a reference view. Editing fields, processing refunds, marking invoices paid, and other write actions still happen on the full detail page; the drawer's job is to let you confirm something quickly without losing your place.",      },
      {
        title: "Jumping to the full page",
        description:
          "Every drawer has footer buttons that open the full detail page for the record you're looking at. Family drawer says 'View Family' (plus a 'View Statement' shortcut); Student drawer says 'View Full Profile'; Invoice drawer says 'View Full Invoice'; Payment drawer says 'View Full Payment'; Class drawer says 'View Full Class'. Use this when you need to act on the record (edit, refund, enroll, etc.) — the drawer is read-mostly.",
        tip: "The footer button opens the full page in the same tab. Use your browser's Back button to return to your list. To keep the list open, middle-click the button or hold Ctrl (Cmd on a Mac) while you click it, and the full page opens in a new tab.",
      },
      {
        title: "Drawer vs. full page",
        description:
          "Drawer for quick reference: 'Did this family pay?', 'What's the address for this student?', 'Which classes is this student enrolled in?'. Full page for sustained edits or when you need every related tab (curriculum, gradebook, etc.). The footer button makes the switch one click whenever the drawer view isn't enough.",
      },
    ],
  },
];

// =============================================
// CAMPUS & SCHEDULE
// =============================================

const campusScheduleGuides: Guide[] = [
  {
    slug: "create-campus",
    lastUpdated: "2026-10-12",
    emptyState:
      "Brand-new school? Start here. Every class is associated with a campus, so create at least one campus before adding classes.",
    faq: [
      {
        question: "We only have one location — do we need multiple campuses?",
        answer:
          "No. One campus is fine. Multi-campus support is for schools with multiple physical buildings or programs that need separate rosters, schedules, and finances.",
      },
      {
        question: "What's the campus code for?",
        answer:
          "Internal reference — useful for matching campuses against existing accounting systems, or for short labels in reports. Optional and free-form when creating, but it can't be changed after the campus is created.",
      },
      {
        question: "Can I delete a campus?",
        answer:
          "Campuses are archived, not deleted. Choose \"Archive Campus\" from the campus's row menu. To see archived campuses, use the status filter on the Campuses list (All Campuses / Active Only / Archived Only).",
      },
    ],
    title: "Creating a Campus",
    description:
      "Set up your first campus with its address and contact information. Multi-campus schools can add as many as needed.",
    category: "campus-schedule",
    icon: "LocationOn",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Campuses",
        description:
          'Click "Campuses" in the left sidebar under Operations. You\'ll see a list of all your campuses (empty if this is your first time).',
        screenshot: "/help/campus-list.png",
      },
      {
        title: "Click Add Campus",
        description:
          'Click the "Add Campus" button in the top-right corner. This opens the campus creation form.',
        screenshot: "/help/campus-add-button.png",
      },
      {
        title: "Fill in Campus Details",
        description:
          "Enter the campus name and the address — street, city, state, and postal code are all required, and the country is a 3-letter code (defaults to USA). Optionally add a campus code (up to 20 characters — note it's permanent once saved), a second address line, phone, email, and website.",
        screenshot: "/help/campus-form.png",
        tip: "Use a descriptive name like 'Main Campus' or 'Austin Campus' if you have multiple locations. The campus code is useful for internal reference.",
      },
      {
        title: "Save and Verify",
        description:
          'Click "Create Campus". You\'ll return to the campuses list where the new campus appears — click it any time to review or edit its details.',
        screenshot: "/help/campus-detail.png",
      },
    ],
  },
  {
    slug: "setup-school-calendar",
    lastUpdated: "2026-10-12",
    emptyState:
      'Empty calendar? Add your first event — typically the academic year start, then holidays for the term. Any event with "Affects Attendance" enabled is auto-skipped by bulk session generation.',
    faq: [
      {
        question: "Does the calendar reset every year?",
        answer:
          "No — events stay in place. Add new events for each academic year. Recurring events (weekly assemblies, monthly events) need to be added once per occurrence today.",
      },
      {
        question: "Can parents see the calendar?",
        answer:
          "Not today — the School Calendar is visible to admins and principals only. Share key dates with families via Messaging or notifications.",
      },
    ],
    title: "Setting Up Your School Calendar",
    description:
      "Create your academic calendar with holidays, breaks, exam periods, and important dates.",
    category: "campus-schedule",
    icon: "CalendarToday",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Open School Calendar",
        description:
          'Click "School Calendar" in the left sidebar under Operations. You\'ll see a full-year view: twelve mini-months at a glance, with arrows to move between years, a "Today" button, and an Academic Year filter that starts on your current year (choose "All Events" to see everything). The header also has "Manage Events" (the full event list) and "Academic Years" shortcuts.',
        screenshot: "/help/calendar-overview.png",
      },
      {
        title: "Add a Calendar Event",
        description:
          'Click "Manage Events" to open the Calendar Events list, then click "Add Event". A form will appear where you can enter the event details. (On the year view, clicking a date shows that day\'s existing events in the sidebar.)',
        screenshot: "/help/calendar-add-event.png",
      },
      {
        title: "Set Event Details",
        description:
          'Enter the event name, select the event type (Holiday, Break, Exam Period, Program, Graduation, First Day, Last Day, or Custom), set the start and end dates, and optionally link it to an academic year. Toggle "Affects Attendance" for days when attendance shouldn\'t be tracked.',
        screenshot: "/help/calendar-event-form.png",
        tip: '"Affects Attendance" works on any event type, not just holidays: bulk session generation skips those dates automatically, while creating a single session on such a date shows a warning you can override.',
      },
      {
        title: "View Your Calendar",
        description:
          "Your calendar now shows all events color-coded by type, and clicking a highlighted day lists its events in the sidebar.",
        screenshot: "/help/calendar-with-events.png",
      },
    ],
  },
  {
    slug: "academic-years",
    lastUpdated: "2026-10-12",
    emptyState:
      "Empty academic years? Add at least the current year — calendar events and terms hang off it. Add next year ahead of time so you can plan early.",
    faq: [
      {
        question: "Can two academic years overlap?",
        answer:
          "No — the system blocks overlapping date ranges on create and edit, with an error naming the conflicting year.",
      },
      {
        question: "What does the \"Current\" year do?",
        answer:
          'Exactly one year can be flagged Current (shown with a star). It drives defaults elsewhere — like the calendar\'s year filter and the default on the event form. Use "Set as Current" in a year\'s row menu when the new year starts.',
      },
    ],
    title: "Setting Up Academic Years",
    description:
      "Define your school's academic years and terms. They anchor your calendar events, the terms report cards are issued for, and the \"School year\" range on the Billed vs Collected report.",
    category: "campus-schedule",
    icon: "CalendarToday",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Academic Years",
        description:
          'Go to School Calendar > Academic Years. You\'ll see your existing academic year definitions.',
        screenshot: "/help/academic-years-list.png",
      },
      {
        title: "Create an Academic Year",
        description:
          'Click "Add Academic Year", enter the name (for example "2025-2026"), the start date, and the end date, then click "Add". Years can\'t overlap. When the year begins, open its row menu and choose "Set as Current".',
        screenshot: "/help/academic-year-form.png",
        tip: "Add next year's academic year before the current one ends so you can add its calendar events and terms early.",
      },
      {
        title: "Add Terms (optional)",
        description:
          'Use "Manage Terms" on this page to break a year into quarters, semesters, or trimesters. A term\'s dates must fall inside its academic year and can\'t overlap another term in that year. Each term can be marked Current, and report cards are issued against terms.',
      },
      {
        title: "Academic Year in Use",
        description:
          "Once created, the academic year is used across the calendar: events can be linked to it and the year view can filter by it. Classes themselves are bounded by their own start and end dates.",
        screenshot: "/help/academic-year-detail.png",
      },
    ],
  },
];

// =============================================
// CLASSES & TEACHERS
// =============================================

const classesGuides: Guide[] = [
  {
    slug: "create-class",
    lastUpdated: "2026-10-13",
    emptyState:
      "Empty classes list? You need at least one campus first. From there, Add Class lets you create your first one. If you have a similar class from last term, use \"Clone existing\", \"Templates\", or the \"Start from template\" action on the form instead of starting from scratch.",
    faq: [
      {
        question: "What's the difference between a Hifdh class and a Regular class?",
        answer:
          "Hifdh classes unlock Hifdh Progress: each student's memorization plan, daily ratings, monthly Hifdh report cards and the parents' daily update. Regular classes use the standard assignment + gradebook flow. The Hifdh type is only offered to schools set up as Islamic schools.",
      },
      {
        question: "Can I change a class's gender restriction after students enroll?",
        answer:
          "You can — but existing enrollments aren't auto-removed and there's no automatic mismatch check, so review the current roster case-by-case before changing it.",
      },
      {
        question: "What is \"Require enrollment in another class\"?",
        answer:
          "A co-enrollment requirement: families can only apply for this class if the child is also enrolled in, or applying at the same time for, one of the classes you list (any campus counts). The rule is checked whenever an application is submitted or an enrollment is created, and every application is checked again when you approve it. Parents can't get past it. Staff see which class is required and can make an exception: tick \"Enroll without …\" and click \"Enroll Anyway\" on the enrollment form, tick \"Submit without the required class\" and click \"Submit Anyway\" on the application form, or check \"Approve anyway (requirement not met)\" when approving. Example: requiring a core class alongside an elective.",
      },
    ],
    title: "Creating a Class",
    description:
      "Set up classes with schedules, capacity limits, and fee assignments. Classes are the core organizational unit of your school.",
    category: "classes",
    icon: "Class",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Navigate to Classes",
        description:
          'Click "Classes" in the left sidebar under Academics. You\'ll see all existing classes with their enrollment counts and capacity, plus a Setup column that flags anything still missing (schedule, teacher, or fees).',
        screenshot: "/help/classes-list.png",
      },
      {
        title: "Click Add Class",
        description:
          'Click the "Add Class" button to open the class creation form. If a saved template fits, click "Start from template" at the top of the form instead. It takes you to Class Templates, where you can create the class from a template.',
        screenshot: "/help/class-add-button.png",
      },
      {
        title: "Basic Information",
        description:
          "Enter the class name, select the campus, and add an optional description. Set the start and end dates for when the class runs, and the maximum capacity. You can also pick grade levels (leave empty for \"Ungraded\") and a minimum age.",
        screenshot: "/help/class-form-basic.png",
        tip: "Choose a clear naming convention like 'Quran Level 1 - Sunday' to help parents identify the right class.",
      },
      {
        title: "Class Options",
        description:
          'Configure additional options: Class Type (Regular, or Hifdh for Quran memorization at Islamic schools), Gender Restriction (No Restriction (Mixed), Boys Only, or Girls Only), Require Upfront Payment (on by default: families pay during the application, by card/ACH or Zakat), Admissions Open, Active, and, if needed, a co-enrollment requirement ("Require enrollment in another class") listing the classes that satisfy it. The requirement is checked whenever an application or enrollment is created for the class; staff can override it with "Enroll Anyway" or "Submit Anyway".',
        screenshot: "/help/class-form-options.png",
        tip: "Boys Only and Girls Only classes only accept students whose gender is on their record. When you enroll a student, classes they can't join are greyed out with the reason, and parents applying only see sections that match their child. Hifdh classes unlock Quran memorization tracking such as daily progress and report cards.",
      },
      {
        title: "Teachers and Schedule",
        description:
          "The create form includes Teacher and Schedule sections inline — assign the teacher(s) and build the weekly schedule right here so the class is fully operational the moment you save. (When editing later, teachers and schedules are managed from the class detail page.)",
      },
      {
        title: "Review and Save",
        description:
          'Review all the details and click "Create Class". You\'ll return to the Classes list, where the Setup column shows anything still missing.',
        screenshot: "/help/class-detail.png",
      },
      {
        title: "Assign Fees",
        description:
          'Open the new class from the list and use the "Add Fee" button on the Class Fees card. Select which fee types apply to this class — these fees will be automatically included when generating invoices for enrolled students. (Adding fees requires fee-management permission.)',
        screenshot: "/help/class-form-fees.png",
        tip: "Make sure your fee types are set up first (under Billing > Fee Types) before assigning fees to classes.",
      },
    ],
  },
  {
    slug: "assign-teachers",
    lastUpdated: "2026-10-12",
    emptyState:
      "No teachers in the dropdown? You need to create teacher user accounts first (the Teachers page under People, or Users > Add User > role=Teacher). Then they'll appear when assigning to classes.",
    faq: [
      {
        question: "Can a class have multiple teachers?",
        answer:
          "Yes — typically one Primary Teacher plus optional Co-Teachers and Assistants. Every assigned teacher sees the class in their Teacher Portal and can take attendance for it.",
      },
      {
        question: "Can I assign the teacher while creating the class?",
        answer:
          "Yes — the class creation form has an inline Teacher section, so most classes get their teacher at creation time. This guide covers assigning or changing teachers afterwards.",
      },
    ],
    title: "Assigning Teachers to Classes",
    description:
      "Assign primary teachers, co-teachers, and support staff to your classes.",
    category: "classes",
    icon: "Badge",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open Class Detail",
        description:
          "Navigate to Classes and click on the class you want to assign a teacher to. The class detail page shows all current information including enrolled students, fees, and teachers.",
        screenshot: "/help/class-detail-teachers.png",
      },
      {
        title: "Add a Teacher",
        description:
          'On the Teachers card, click "Add Teacher". In the "Add Teacher to Class" dialog, search for the teacher. Only active accounts with the Teacher role appear, and teachers already on the class are left out. Admins, principals and office staff can add, change and remove a class\'s teachers. Teachers see the Teachers card without these buttons.',
        screenshot: "/help/class-teachers-section.png",
      },
      {
        title: "Select Teacher and Role",
        description:
          "Choose a teacher from the search dropdown and assign their role: Primary Teacher, Co-Teacher, or Assistant. The role defaults sensibly — Primary if the class has none yet, otherwise Co-Teacher. Each class should have at least one Primary Teacher.",
        screenshot: "/help/class-assign-teacher-form.png",
        tip: "A teacher can be assigned to multiple classes. Their Teacher Portal will show all assigned classes.",
      },
      {
        title: "Confirm Assignment",
        description:
          "The teacher now appears in the class's teacher list with their role. They'll be able to see this class in their Teacher Portal and take attendance for it. You can change their role or remove them at any time.",
        screenshot: "/help/class-teachers-assigned.png",
      },
    ],
  },
  {
    slug: "clone-class",
    lastUpdated: "2026-10-13",
    emptyState:
      "First class? Clone is most useful from term 2 onward. For your very first class, use a Template (Classes > Templates) or build from scratch.",
    faq: [
      {
        question: "Does cloning copy enrollments?",
        answer:
          "No. Enrollments are never copied, so the clone starts empty. Everything else about the class comes across. Subjects, description, capacity, class type, minimum age, and upfront-payment and admissions settings are always copied. Fees, schedules, teachers, and grade levels have toggles that start switched on. A \"Require enrollment in another class\" rule is copied while the required class is still running when the new class starts. A rule pointing at a class that has ended by then, or has been archived, is left out, and the new class's page tells you which ones so you can pick this year's class from its Edit form. If every rule was left out, admissions on the new class stay closed until you set the requirement and reopen them. Templates keep every rule.",
      },
      {
        question: "What about teachers?",
        answer:
          'Teacher assignments DO carry over by default. Untick the "Teachers" checkbox in the clone dialog (or switch it off under "What to Copy" on the clone page) if a different teacher takes over the new section.',
      },
    ],
    title: "Cloning a Class",
    description:
      "Quickly create a copy of an existing class with all its settings, fees, and schedules.",
    category: "classes",
    icon: "ContentCopy",
    estimatedTime: "2 min",
    steps: [
      {
        title: "Two ways to clone",
        description:
          'Quick clone (classes that haven\'t ended): open the class detail page and click "Clone" in the header. A dialog opens where you set the new name, check the gender restriction, and tick what to include (Schedules, Class Fees, Teachers). Full clone (any class, including ones that have ended): on the Classes list, click "Clone existing" to open the Clone Class page, which gives you more control.',
        screenshot: "/help/class-clone-dialog.png",
        tip: "When the class name ends in \"(Girls)\" or \"(Boys)\", the quick-clone dialog assumes you're making the other section: the name is swapped and the gender restriction flips (Girls Only becomes Boys Only, and the reverse). Any other class keeps its gender restriction and gets \"(Copy)\" added to its name. Check both before you click Clone.",
      },
      {
        title: "Shift the dates (clone page)",
        description:
          'The Clone Class page adds a Date Shift control. It starts on +1 academic year (365 days); you can also pick Same dates, +1 week, +1 month (30 days), or type a custom number of days. The preview shows the new start and end dates, so check that they land on the right days. When you shift the dates, weekly schedules move with them: each time slot starts on the new class\'s start date (a mid-term time change keeps its place in the new term), and slots that would end before the new class starts, or that were removed from the schedule, are left out. It also has an Output Type switch to create a template instead of a real class, and "What to Copy" toggles.',
        screenshot: "/help/class-clone-result.png",
        tip: "This is perfect for a new semester or academic year — clone last term's classes with a +1 academic year shift.",
      },
      {
        title: "Create the clone",
        description:
          'Click "Clone" (dialog) or "Clone Class" (page). Enrollments are never copied — the new class starts empty and lands you on its detail page.',
      },
    ],
  },
  {
    slug: "class-templates",
    lastUpdated: "2026-10-12",
    emptyState:
      "If your Templates list is empty, open a current class and click \"Save as template\" in its header, or use \"New template from class\" on the Templates page (this one works for ended classes too). Check the details and click \"Save Template\". The original class isn't changed.",
    faq: [
      {
        question: "Can I turn a template into a real class?",
        answer:
          'Not by editing it — use "Use template" instead, which clones the template into a real, enrollable class. The template stays a template and can be reused for every new term.',
      },
      {
        question: "Do templates show up in the parent portal?",
        answer:
          "No. Templates never appear in the regular classes list and never accept enrollments — they exist only to be cloned into real classes.",
      },
    ],
    title: "Using Class Templates",
    description:
      "Spin up new classes from a curated set of reusable templates instead of starting from a blank form.",
    category: "classes",
    icon: "FileCopy",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open Class Templates",
        description:
          'From the Classes page, click the "Templates" button (or navigate to Classes > Templates). Templates are class shapes that never accept enrollments and never appear in the regular class list - they exist only to be cloned.',
        screenshot: "/help/class-templates-list.png",
      },
      {
        title: "Pick a Template",
        description:
          'Each template row shows its type, default capacity, and shape — how many subjects, schedule slots, teachers, and fees it carries. Open the row menu and choose "Use template" (or just click the row).',
        screenshot: "/help/class-templates-pick.png",
        tip: 'If no template matches, open an existing class and choose "Save as template", or use "New template from class" here. Templates you no longer need can be archived from the row menu — classes already cloned from them are unaffected.',
      },
      {
        title: "Customize and Save",
        description:
          'Using a template opens the Clone Class page filled in from it. Set the new class name, check the gender restriction, and choose a date shift. It starts at +1 academic year, so check the new dates preview. Then click "Clone Class". The result is a real class that students can enroll in, and the template itself isn\'t changed.',
        screenshot: "/help/class-templates-customize.png",
      },
    ],
  },
];

// =============================================
// STUDENTS & FAMILIES
// =============================================

const studentsGuides: Guide[] = [
  {
    slug: "add-family-students",
    lastUpdated: "2026-10-14",
    emptyState:
      "First family? Most schools build their roster from approved applications, so head to Applications first. Add Family by hand is best for staff children, mid-year transfers, or imports.",
    faq: [
      {
        question: "What if two children share guardians?",
        answer:
          "Add them under the same family. Sibling discounts and one-invoice-per-family rules require all siblings to be in a single family record.",
      },
      {
        question: "Can a guardian belong to two families?",
        answer:
          "Yes — common with divorced parents who each maintain a household. Add the guardian to both family records via the Family > Guardians section (the Add Guardian page has a tab for linking an existing guardian).",
      },
      {
        question: "How does the guardian get Parent Portal access?",
        answer:
          'Creating the family does NOT create a portal login. From the family detail page, open the guardian\'s row menu and click "Invite to Portal" — the guardian receives an email to set their password. You can resend the invitation later from the guardian\'s detail page; it always goes to the email the parent signs in with. A guardian needs their own email: if it belongs to a staff, teacher or admin login, the invite is refused until you change it.',
      },
    ],
    title: "Adding Families and Students",
    description:
      "Register a new family with its primary guardian and children in one form, then enroll the children in classes.",
    category: "students",
    icon: "FamilyRestroom",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Navigate to Families",
        description:
          'Click "Families" in the left sidebar under People. The list shows active families; switch the Status filter to see archived ones.',
        screenshot: "/help/families-list.png",
      },
      {
        title: "Add a New Family",
        description:
          'Click "Add Family" to open the unified registration form. This lets you create the family, guardian, and students all at once.',
        screenshot: "/help/family-form.png",
      },
      {
        title: "Family and Guardian Info",
        description:
          "Enter the family name (usually the last name), then fill in the primary guardian's first name, last name, email, and phone number — email and phone are both required. There's also an optional Notes field for anything worth remembering about the family.",
        screenshot: "/help/family-guardian-form.png",
        tip: 'The primary guardian\'s email receives all invoices and notifications, so make sure it\'s accurate. If the domain looks mistyped (for example gmial.com), the form shows "Did you mean …?" under the field — click the suggestion to fix it. If the address ever starts bouncing, the family page shows a red "Email bouncing" chip next to it — fix the address promptly or billing emails die silently.',
      },
      {
        title: "Add Students",
        description:
          'In the same form, add one or more students. For each student, enter their first name, last name, date of birth, and gender. Click "Add Student" to add a row for each sibling. Students are optional: a row left completely blank is skipped when you save, but once you start a row, all four fields are required. Remove a row you don\'t need with its trash icon.',
        screenshot: "/help/student-form.png",
        tip: "You can also add students later from the family detail page or the Students section.",
      },
      {
        title: "Save and Enroll",
        description:
          'Click "Create Family" to save the family, its primary guardian, and its students. To put the children in classes, go to Enrollments and click "New Enrollment" (or click "Enroll" next to the student on the Students page). On the family page, open each guardian\'s row menu and click "Invite to Portal" for anyone who should get Parent Portal access.',
        screenshot: "/help/family-detail.png",
      },
    ],
  },
  {
    slug: "manage-enrollments",
    lastUpdated: "2026-10-13",
    emptyState:
      "Empty enrollments page? Either no students are enrolled yet, or your filter is hiding everyone. Click \"New Enrollment\" to add your first one.",
    faq: [
      {
        question: "Pause vs. Unenroll — when do I use which?",
        answer:
          "Pause for temporary breaks (travel, medical, personal, financial) — the enrollment suspends and can be resumed with one click (or resumes on its own the day after its end date, if it has one). Unenroll ends the enrollment; it can be reversed by re-enrolling the student. Pause asks for a start date (an end date and a reason are optional); Unenroll asks for an effective date and a reason. Note that a paused enrollment does NOT hold the seat: capacity counts only active enrollments, so a full class may fill the spot while a student is paused.",
      },
      {
        question: "What happens to invoices for a paused enrollment?",
        answer:
          "A month's invoice is skipped only when the pause covers that ENTIRE billing month (1st through last day) — the pause dialog says this too. A mid-month pause still bills the full month, and so does the month a student resumes in (automatically after the end date, or with Resume). Existing invoices remain unchanged, and after unenrolling you're prompted to review any open invoices that still bill the student (issue credit memos manually if needed).",
      },
      {
        question: "Why does an enrollment show a co-enrollment warning?",
        answer:
          "Some classes require enrollment in a core class first (e.g. Bayaan Academy K-12 students must take a core class). When the student isn't in one of the required classes, the enrollment form shows the requirement and won't enroll them until you decide. Enroll them in the required class first, or, to make an exception, tick \"Enroll without …\" and click \"Enroll Anyway\". The staff application form works the same way, with \"Submit Anyway\". Parents can't apply for such a class on its own: the child must already be enrolled in a core class, or be applying for one in the same submission.",
      },
    ],
    title: "Managing Student Enrollments",
    description:
      "Enroll students in classes, pause enrollments, and manage withdrawals.",
    category: "students",
    icon: "SwapHoriz",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Navigate to Enrollments",
        description:
          'Click "Enrollments" in the left sidebar under Academics. You\'ll see all enrollments with student name, class, campus, and status (the list defaults to Active).',
        screenshot: "/help/enrollments-list.png",
      },
      {
        title: "Create an Enrollment",
        description:
          'Click "New Enrollment" to start the enrollment flow. First, select the family, then choose a student from that family. The system shows the student\'s current enrollments so you can see what they\'re already in.',
        screenshot: "/help/enrollment-form.png",
      },
      {
        title: "Select a Class",
        description:
          "Choose from available classes, filtered by the campus selected in the top banner. The list shows active classes with open admissions that haven't ended and that the student isn't already in. Full classes show \"FULL\", and boys-only or girls-only classes the student can't join show \"boys only\" or \"girls only\"; neither can be selected. If the student's gender isn't recorded, set it on the student record first. Then pick when tuition starts — \"This month\" or \"Next month\" (one-time fees bill at enrollment either way) — and click \"Enroll Student\".",
        screenshot: "/help/enrollment-class-select.png",
        tip: "Students can be enrolled in multiple classes at once; each enrollment bills its own class's fees. After saving, the message names the invoice the fees went on. If it says \"No new invoice was created\", that month is already on an existing invoice — open that invoice and check it before billing anything by hand.",
      },
      {
        title: "Pause or Unenroll",
        description:
          'From the enrollment detail page, click "Pause Enrollment" to suspend it temporarily. Pick a start date; the pause is open-ended until you turn off "Indefinite (until manually resumed)" and set a Pause End Date (it can\'t be before the start). With an end date, the enrollment resumes automatically the day after and is billed for that month if it hasn\'t been billed yet. A reason and notes are optional. Click "Unenroll Student" to end the enrollment — this needs an effective date and a reason. The enrollment page keeps a Pause History card, and the family page has Pause All / Resume All buttons for whole-family breaks (they read Pause (N) / Resume (N) when only some are paused).',
        screenshot: "/help/enrollment-detail.png",
      },
      {
        title: "View Paused Enrollments",
        description:
          'From the Enrollments list, click the "Paused Enrollments" button (admins and principals) to see all active pauses and upcoming resumes in two tabs. A pause with an end date resumes automatically the day after it (except for a withdrawn or graduated student or an archived family — resume those yourself if they return); click "Resume" to reactivate one early (that month is billed right away if its invoices have already gone out).',
        screenshot: "/help/enrollments-paused.png",
      },
    ],
  },
  {
    slug: "spotting-family-fragmentation",
    lastUpdated: "2026-10-12",
    relatedGuides: ["add-family-students", "manage-applications", "manage-discounts"],
    emptyState:
      "If your sibling discount report looks right (siblings ranked correctly across the family), you don't have a fragmentation problem and can skip this guide. Come back if a parent reports their second child wasn't discounted as a sibling.",
    faq: [
      {
        question: "What is family fragmentation?",
        answer:
          "When two parents independently submit applications for siblings — say, Mom applies for Child A from her phone and Dad applies for Child B from his — the public application form can create two separate family records. Sibling discounts are worked out per family record, so Child B is treated as a first child instead of a second. Two defenses now reduce this: a returning guardian email is routed to the parent portal instead of the public form, and application approval runs a duplicate-household check (matching guardian phone, guardian name, and child name + date of birth) that warns you before a separate family is created.",
      },
      {
        question: "How will I notice this?",
        answer:
          "The most common signal: a parent contacts you saying 'we have two kids enrolled but only one is getting the sibling discount.' Less obvious: the Discount Analysis report under-counts sibling discounts vs. what you'd expect from your enrollment data.",
      },
      {
        question: "When are sibling discounts actually applied?",
        answer:
          "On the next invoice generation. When invoices are built, the system looks up each family's active enrollments at that moment and ranks the children by when they enrolled. So if you fix fragmentation today, the next monthly invoices pick up the correct sibling tier — there's nothing separate to run.",
      },
      {
        question: "Can I merge two family records myself?",
        answer:
          "Yes. From the canonical family's detail page, click 'Merge Duplicate' (admin only). Search for the duplicate by family or guardian name / email and pick it from the list; the system moves all students, guardians, invoices, payments, payment methods, and applications onto the canonical family in one step and archives the duplicate. The canonical family keeps its primary guardian; the duplicate's primary joins as an additional guardian, and a guardian already on both records is kept only once. The duplicate's statements are discarded; the family's next monthly statement (created on your billing day) covers the merged history. Its AutoPay settings only carry over if the canonical family had none. Merging a family into itself, or merging an archived family, is rejected.",
      },
    ],
    troubleshooting: [
      {
        problem: "Two families with similar names — are they fragmented or just similar?",
        solution:
          "Compare the guardians and children. It's the same household if the records share a guardian phone number or name, list the two parents of the same children, or hold the same child (same name and date of birth). Two unrelated 'Khan' families with different parents and children are normal — don't merge them.",
      },
      {
        problem: "I merged the families — when does the sibling discount kick in?",
        solution:
          "On the next invoice generation. Sibling ranking is recomputed every time the invoice generator runs, based on the family's active enrollments at that moment. There is no separate ranking job to trigger.",
      },
      {
        problem: "The merge is rejected saying the family is archived.",
        solution:
          "You can't merge into an archived family (restore it first), and an archived duplicate is usually an already-merged shell — there's nothing left to merge. Check the family's status before retrying.",
      },
    ],
    title: "Spotting and Merging Family Fragmentation",
    description:
      "When independent applications create separate family records for siblings, sibling discounts stop computing correctly. Here's how to identify the duplicates and use the admin Merge action to consolidate them.",
    category: "students",
    icon: "MergeType",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Identify the fragmented family",
        description:
          "From the Families list, search by the family name, a guardian's name, email, or phone number. Two family rows that share a guardian phone number or name, or whose guardians are the two parents of the same children, are likely fragmented. Open both family detail pages in separate tabs to confirm.",      },
      {
        title: "Confirm the overlap",
        description:
          "On each family detail page, compare the Guardians table (names, emails, phone numbers) and the Students list. Signs of the same household: a shared guardian phone or name, the other parent listed on the second record, or the same child (same name and date of birth) on both.",        tip: "If both records have invoice history, keep the older record as the canonical one so payment history stays chronological.",
      },
      {
        title: "Pick the canonical family",
        description:
          "Decide which record will survive the merge. Usually the one with more billing history (older invoices, more guardians on file). Open that family's detail page; the merge runs FROM here, pulling the duplicate's records INTO this one.",
      },
      {
        title: "Click Merge Duplicate",
        description:
          "From the canonical family's detail page, click 'Merge Duplicate' in the header actions (admin only). In the dialog, search for the duplicate by family or guardian name / email — each option shows the guardian's email, student count, and balance. Selecting one shows a review card ('Will be merged and archived: …' with its students and outstanding balance). Verify it's the right family, then click Merge.",        tip: "Archived families and the current family don't appear in the picker. The system also rejects merging a family into itself or merging an archived family, with a clear error.",
      },
      {
        title: "Review the merge summary",
        description:
          "On success, the dialog shows per-table counts of what moved (e.g. 'students: 2', 'invoices: 4') plus what was archived or discarded. The duplicate is now archived and won't appear in active views. The canonical family detail page refetches automatically with the merged children.",
      },
      {
        title: "Verify the next invoice run",
        description:
          "After the merge, wait for the next monthly invoice generation. Siblings are re-ranked when invoices are generated, so the corrected sibling discount appears automatically on the new invoices. To check it beforehand, click \"Preview Invoice\" on the canonical family's page and choose the Billing Month — it shows what that month's invoice would look like without creating anything. Don't use Generate Invoice to check: it creates a real invoice.",      },
    ],
  },
];

// =============================================
// ADMISSIONS
// =============================================

const admissionsGuides: Guide[] = [
  {
    slug: "manage-applications",
    lastUpdated: "2026-10-13",
    emptyState:
      "No applications yet? Your public application form lives at your school URL + /apply (the school URL is shown in Admin > School Settings > General). Share that link and applications start showing up here as parents submit.",
    relatedGuides: ["spotting-family-fragmentation", "understanding-autopay", "manage-discounts"],
    troubleshooting: [
      {
        problem: "Approving an application says \"capacity exceeded\".",
        solution:
          'The class is full. Either approve with "Override capacity limit" checked (which exceeds capacity intentionally), waitlist the student, or pick a different class. If the application shows a "Payment refunded" chip, the family paid at checkout while the class was full and was refunded automatically. Approving it does not use that payment: on an upfront-payment class the family is emailed a payment link first, otherwise the invoice is billed as unpaid.',
      },
      {
        problem: "Approving warns about a possible duplicate family.",
        solution:
          "The applicant matches an existing family on guardian phone, guardian name, or a child's name + date of birth. If it IS the same household, deny the application and enroll the child under the existing family instead (or have the parent apply from the portal). If it's genuinely a different household, check \"Approve anyway (deliberately create a separate family)\" and approve.",
      },
      {
        problem: "Approving says the student must also be in another class.",
        solution:
          'Some classes need the student to also be in a core class (for example, a K-12 program). Enroll the student in the required class first. If you\'ve decided to allow it anyway, check "Approve anyway (requirement not met)" in the approve dialog and approve again.',
      },
      {
        problem: "After approving, the family didn't get an email.",
        solution:
          'Check the family page first — a red "Email bouncing" chip next to the guardian\'s email means the address is bad. Otherwise check Notifications > History for that family\'s email; a FAILED or BOUNCED status means it never arrived — verify the address and resend manually.',
      },
      {
        problem: "Two parents applied separately for siblings — second child isn't getting the sibling discount",
        solution:
          "Independent applications create separate family records, so siblings end up on different family records and the sibling discount can't compute. See 'Spotting Family Fragmentation' for how to confirm it and merge them.",
      },
    ],
    faq: [
      {
        question: "What happens to a withdrawn application?",
        answer:
          "It stays in the system for audit history but doesn't appear in active queues (the list defaults to Pending). Withdrawing can't be undone, but the family can submit a new application — returning families do that from the parent portal.",
      },
      {
        question: "Can I require an upfront payment with the application?",
        answer:
          'Yes. On the class form, toggle "Require Upfront Payment" (it\'s ON by default for new classes). Parents are prompted to pay before submitting via Stripe (card or ACH). Approved applications then auto-create the invoice with the upfront payment already applied. Parents are never charged for a class that is already full: they are offered "Join the waitlist — no payment now" instead. If the class fills up in the moment between paying and enrolling, the payment is refunded automatically and the application goes on the waitlist; approving it later sends the family a payment link.',
      },
      {
        question: "Does an upfront payment auto-enroll the family in AutoPay?",
        answer:
          "Only if Admin > School Settings > AutoPay > 'Auto-Enroll Families' is ON. When that's enabled, the upfront payment method is saved and reused for AutoPay so future invoices charge automatically. When it's OFF, nothing is saved from the upfront payment — the family adds a payment method later if they want AutoPay.",
      },
      {
        question: "What is a sponsor-funded (e.g. TEFA) application?",
        answer:
          "If your school has sponsor funding enabled, parents can select the sponsor program as their payment method and supply their approval reference. The application shows a sponsor chip and a banner; approving it requires you to verify the award (a mandatory attestation checkbox) and creates the funding award plus the annual sponsor invoice. The family is never billed for the covered period.",
      },
      {
        question: "What do \"Awaiting Payment\" and \"Expired\" mean?",
        answer:
          'When you approve a discount or Zakat request for a class that requires upfront payment, and the discount doesn\'t cover the whole fee, the application moves to Awaiting Payment. The family is emailed a payment link, and, when you approve from the application\'s page, the link is copied to your clipboard ("Payment link copied to clipboard"). While the application is Awaiting Payment, its page shows the payment link with "Copy link" and "Open" buttons, so you can send it to the family again at any time. If the family hasn\'t paid within 14 days, the application expires and the link stops working. Pending applications that are still undecided after 90 days also expire.',
      },
    ],
    title: "Managing Student Applications",
    description:
      "Review, approve, and manage incoming student applications through the admissions pipeline.",
    category: "admissions",
    icon: "Assignment",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Navigate to Applications",
        description:
          'Click "Applications" in the left sidebar under People. The list defaults to Pending; use the status filter to view Approved, Awaiting Payment, Denied, Waitlisted, Withdrawn, or Expired applications.',
        screenshot: "/help/applications-list.png",
      },
      {
        title: "Review an Application",
        description:
          "Click on an application to view the full details: student information, guardian contacts, and any notes from the family. If the applicant looks like an existing household, a 'Possible duplicate' panel lists the matching families with the reasons — review it before approving.",
        screenshot: "/help/application-detail.png",
      },
      {
        title: "Take Action",
        description:
          "Use the action buttons — Approve, Deny, Waitlist, or Withdraw (availability depends on the current status; Review Notes are required to Deny). Approving creates the family, guardian, student, AND the enrollment and first invoice in one transaction. The approve dialog also lets you override class capacity, choose when tuition starts (as requested / this month / next month), and shows an Approval Summary of the resulting invoice.",
        screenshot: "/help/application-actions.png",
        tip: "For Zakat and discount-request applications, the approve dialog requires a decision: pick a discount rule to apply, or check \"Approve at full fee — decline the zakat request\" (or \"… decline the discount request\").",
      },
      {
        title: "Share Your Application Link",
        description:
          "Share your public application URL (your school URL + /apply) with prospective families. The form supports multiple children with multiple classes each (up to 10 class requests per submission), and an adult can apply for themselves via the \"I'm applying for myself\" checkbox. If a guardian's email is already registered at your school, the form sends them to log in to the parent portal instead. There, they click \"Apply for a Program\" on their child's card (or on the dashboard) to apply for another class. The class list covers every campus and only shows classes that child can join. Those applications arrive here like any other.",
        screenshot: "/help/application-public-link.png",
      },
    ],
  },
];

// =============================================
// BILLING & PAYMENTS
// =============================================

const billingGuides: Guide[] = [
  {
    slug: "setup-fee-types",
    lastUpdated: "2026-10-13",
    emptyState:
      "Empty fee types? Start with the basics: Monthly Tuition (Recurring), Registration Fee (One-Time). Add others as needed (book fees, materials, field trips). Each fee type can be assigned to one or more classes.",
    faq: [
      {
        question: "Can I delete a fee type after invoices use it?",
        answer:
          "Fee types are never fully deleted. \"Delete\" archives the fee type: it's marked Inactive, can't be added to classes, and invoices that already charged it don't change. Turning off \"Active\" does the same. Both only work once no class still bills the fee (an active class that hasn't ended; templates don't count). If any does, you're told which classes, so remove the fee from each one first (open the class, then Class Fees). Turning a fee type off also removes it from classes that no longer run, while templates keep it. To undo, click Activate; classes it was removed from won't charge it again until you add it back. The list filter defaults to Active, so switch the Status filter to see inactive types.",
      },
      {
        question: "How do sibling discounts stack with other discounts?",
        answer:
          "It depends on Settings → Discounts → \"Allow Discount Stacking\". When it's on, the sibling discount comes off the fee first, then the student's other discount (Zakat, scholarship, etc.) comes off what is left. For each class, only one other discount applies: the largest. When it's off, the family gets whichever is larger, the sibling discount or the other discount, never both. Partner fees are never discounted. Sponsor-funded students are left out of sibling ranking entirely, so a sponsored child doesn't change their siblings' ranks.",
      },
      {
        question: "Why can't I save a new fee type with this name?",
        answer:
          "Fee type names are unique — you'll get a clear \"name already exists\" error. Reactivate or edit the existing one instead of creating a duplicate.",
      },
    ],
    title: "Setting Up Fee Types",
    description:
      "Create tuition fees, registration fees, and other charges with built-in sibling discount support.",
    category: "billing",
    icon: "RequestQuote",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Navigate to Fee Types",
        description:
          'Click "Fee Types" in the left sidebar under Billing. You\'ll see your fee types with Search, Type, Status, and Sibling Discount filters (the Status filter defaults to Active).',
        screenshot: "/help/fee-types-list.png",
      },
      {
        title: "Add a Fee Type",
        description:
          'Click "New Fee Type" to create a new fee. Enter the name (e.g., "Monthly Tuition", "Registration Fee", "Book Fee") and an optional description. Only admins can create, edit or archive fee types; principals and staff can view them.',
        screenshot: "/help/fee-type-form.png",
      },
      {
        title: "Set Amount and Type",
        description:
          "Enter the fee amount and select the fee type: Recurring (automatically included each billing cycle) or One-Time (charged once at enrollment).",
        screenshot: "/help/fee-type-amount.png",
        tip: "Create separate fee types for tuition and materials. This gives families clear breakdowns on their invoices.",
      },
      {
        title: "Partner Fees (optional)",
        description:
          'If you collect this fee for another institution and pass the money on, tick "This fee is collected on behalf of a partner institution" in the "Collected for a partner institution" box. A partner fee is never discounted or waived. Sibling discounts, Zakat aid and scholarships all skip it, so the family always owes it in full. It shows a "Partner fee" chip in the fee types list.',
        tip: "Ticking it turns off the sibling discount for this fee.",
      },
      {
        title: "Configure Sibling Discounts",
        description:
          'Check "Apply sibling discount" to enable automatic sibling pricing. Set the discount for the 2nd child (e.g., 10% off) and 3rd+ child (e.g., 20% off). You can choose percentage (up to 100%) or a fixed amount (validated to never exceed the fee itself) for each tier.',
        screenshot: "/help/fee-type-sibling.png",
        tip: "Sibling discounts are calculated automatically based on how many students from the same family are enrolled. The system handles ranking by enrollment date.",
      },
      {
        title: "Assign to Classes",
        description:
          'After creating the fee type, assign it from each class\'s detail page: use "Add Fee" on the Class Fees card. To charge a different price in one class, tick "Override amount for this class". These fees are then included automatically when invoices are generated for enrolled students.',
        screenshot: "/help/fee-type-assign.png",
      },
      {
        title: "See Where a Fee Is Used",
        description:
          "Click a fee type to open its page. Usage Information shows how many active classes and students use it, how much it has billed to date, the discounts and credits against it, and how much is still on open invoices. A table lists every class that uses it, with that class's amount and status.",
      },
    ],
  },
  {
    slug: "generate-invoices",
    lastUpdated: "2026-10-13",
    emptyState:
      "First batch run? You need: (1) at least one fee type marked as Recurring, (2) at least one class with that fee assigned, and (3) students enrolled in that class. The preflight check tells you exactly which families will be invoiced.",
    relatedGuides: ["record-payments", "correcting-invoices", "late-fees-and-dunning"],
    troubleshooting: [
      {
        problem: "Preflight shows 0 \"Invoices to Generate\" but we have students.",
        solution:
          "Usually one of these: (a) the students' classes have no active recurring fee; (b) the families already have this month's batch invoice; (c) the month is already billed on an enrollment invoice or a hand-made invoice, so there is nothing left to bill; or (d) the students are sponsor-covered, so the sponsor pays through the sponsor invoice instead. The notes under the summary and \"Show Family Details\" tell you which.",
      },
      {
        problem: "An invoice was generated with the wrong amount.",
        solution:
          "Don't edit the invoice directly — invoices are immutable by design. Issue a credit memo for the difference, or have an admin void the invoice and bill the month again with \"Generate Invoice\" after fixing the fee setup (the monthly batch won't bill that family again for that month). You can't void an invoice that has ever had a payment, even one that was later refunded, so use a credit memo for those. If a credit memo is active on the invoice, void it first.",
      },
      {
        problem: "Generation got stuck partway through.",
        solution:
          'The completion summary shows Created / Skipped / Errors tiles, with a "Show Error Details" list naming each failed family. Fix the listed errors (usually a missing student field) and re-run — the second run skips families already invoiced this month.',
      },
    ],
    faq: [
      {
        question: "Can I generate invoices for one family only?",
        answer:
          "Yes. From Invoices > \"Generate Invoice\" (the singular form), pick the family and billing month, then add the lines. It's useful for late enrollments or one-off charges. If a line bills a class's monthly fee (pick the Student and the class's fee under \"Fee Type (Optional)\"), the monthly batch won't bill that class again for that month. If the student has two classes with the same fee type, the line can't be matched to a class, so check the batch preview. The same goes for a partner fee. If you void the invoice before the monthly batch runs, the batch bills them as usual.",
      },
      {
        question: "What happens if I run the batch twice in the same day?",
        answer:
          "Re-running is safe. A family that already has this month's batch invoice is skipped. A family billed for the month another way (an enrollment invoice, or one made with \"Generate Invoice\") is still billed for anything that invoice didn't cover. A class whose monthly fee is already on one of those invoices isn't billed again, and neither is a partner fee already on a hand-made invoice. If you void a batch invoice, the batch treats that family as settled for that month and won't bill them again; the preflight marks them \"Skipped: invoice voided\". To bill them for that month, use \"Generate Invoice\".",
      },
      {
        question: "How do I find a specific set of invoices on the list page?",
        answer:
          "The Invoices list has a search box that matches family name and invoice number, a class multi-select filter, and — for schools with sponsor funding — a Funding filter (Family vs. Sponsor). Column sorting spans all pages, and your filters and sort are remembered when you navigate away and come back.",
      },
      {
        question: "What's the green \"Sponsor\" chip on some invoices?",
        answer:
          "Those invoices are payable by your sponsor program (e.g. TEFA), not the family. Record money against them via Sponsor Fund → Record Remittance — never as a family payment. Pay Now and Resend Email are hidden on them, and the list's summary cards include both family and sponsor money.",
      },
    ],
    title: "Generating Monthly Invoices",
    description:
      "Generate invoices for all enrolled students, with fees and discounts worked out automatically.",
    category: "billing",
    icon: "Receipt",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Navigate to Batch Generation",
        description:
          'Click "Batch Generation" in the left sidebar under Billing (admins only). This is where you generate invoices for an entire billing period at once.',
        screenshot: "/help/batch-generation.png",
        tip: 'You don\'t have to run this every month. Once "Billing Status" at the top of School Settings is Live, invoices and statements are created automatically early in the morning (around 4–5 AM Eastern) on your school\'s "Monthly Billing Day" (Settings → Billing & Late Fees), and families missed that day are picked up on the following days of the same month. Use Batch Generation to bill a different month, re-run after fixing a problem, or check the preflight.',
      },
      {
        title: "Select Billing Month",
        description:
          'Make sure the mode toggle is on "Invoice Generation" (the same page can batch-generate statements), then choose the billing month and year.',
        screenshot: "/help/batch-select-month.png",
      },
      {
        title: "Review the Preflight",
        description:
          'The preflight runs as soon as you pick a month. It shows Families with Active Enrollments, Already Invoiced (Will Skip), Invoices to Generate, and the Estimated Total Amount (before discounts). Read the notes under the summary, then click "Show Family Details". Each family is marked "Will Generate", "Already Invoiced", "Skipped: invoice voided" (this month\'s batch invoice was voided, so the batch won\'t bill them again; use "Generate Invoice" if they should still be billed), or "No Billable Items" (nothing left to bill: the month is already on an enrollment or hand-made invoice, or the class has no recurring fee).',
        screenshot: "/help/batch-preview.png",
        tip: "Always review the preflight first. It catches issues like families that were already invoiced or students without fee assignments.",
      },
      {
        title: "Generate and Monitor",
        description:
          'Click "Generate N Invoices" to start. You can "Cancel" partway through. Each new invoice is emailed to the family\'s primary guardian (sponsor invoices are not), and each monthly fee line ends with the month it covers, for example "- October 2026". When the run finishes you\'ll see Created / Skipped / Errors tiles, with "Show Error Details" if anything failed.',
        screenshot: "/help/batch-progress.png",
      },
      {
        title: "Generate a Single Invoice",
        description:
          'To create an invoice for just one family, go to Invoices > "Generate Invoice". Select the family and billing month, then add line items with student, description, quantity, and price — or pick a Fee Type to autofill a line.',
        screenshot: "/help/invoice-generate-single.png",
      },
    ],
  },
  {
    slug: "record-payments",
    lastUpdated: "2026-10-12",
    emptyState:
      "Empty payments? Online payments via Stripe land here automatically. For cash and check, click \"Record Payment\" and enter the family + amount. The system finds the right invoices to apply it to.",
    relatedGuides: ["issuing-refunds", "understanding-autopay", "family-statements"],
    faq: [
      {
        question: "What's the FIFO allocation rule?",
        answer:
          "Cash and check payments you record are applied to the family's unpaid invoices oldest first (by due date), then the next-oldest, and so on. \"FIFO\" = First In, First Out. Sponsor invoices are never included.",
      },
      {
        question: "Can I refund a payment?",
        answer:
          "Admins can, full or partial, up to the payment's remaining refundable amount. Refunds appear in the payments list as separate return rows (negative amount with a Refund chip). See the 'Issuing Refunds' guide for the full walkthrough.",
      },
      {
        question: "When does a payment auto-enroll AutoPay?",
        answer:
          "When Settings → AutoPay → 'Auto-Enroll Families' is ON, a family's first successful online payment (card or bank) also turns on AutoPay. Cash and check payments you record don't. If the family has no default payment method yet, the one they paid with becomes it. If AutoPay was ever set up for the family before, whether it is on, off or paused, auto-enroll leaves it alone. See the 'Pausing and Resuming AutoPay' guide for why this is intentional.",
      },
    ],
    title: "Recording and Tracking Payments",
    description:
      "Record cash and check payments, track payment history, and manage refunds.",
    category: "billing",
    icon: "Payment",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Payments",
        description:
          'Click "Payments" in the left sidebar under Billing. The list opens on this month\'s payments, so change "Date From" and "Date To" to see older ones. Each row shows the amount, method and status, and refunds appear as separate rows. Search by family name or filter by Status, Method or date. Filters live in the URL, so you can bookmark or share a filtered view. "Export CSV" downloads the filtered list.',
        screenshot: "/help/payments-list.png",
      },
      {
        title: "Record a Payment",
        description:
          'Click "Record Payment" to log a manual payment. Select the family (the system shows their outstanding balance), enter the amount, and choose the payment method (cash or check — a Check Number field appears for checks). Note: sponsor-funded invoices never take family payments; record sponsor money via Sponsor Fund → Record Remittance instead.',
        screenshot: "/help/payment-form.png",
        tip: 'The system allocates payments to invoices using FIFO (oldest invoice first). If the payment exceeds the outstanding balance, you must tick "I understand this payment will have unallocated funds" before you can submit.',
      },
      {
        title: "View Payment Details",
        description:
          'Click any payment row to peek at it in a side drawer — which invoices it was applied to, the method, date, and notes. To act on it, click "View Full Payment" to open the full detail page, where admins can "Process Refund".',
        tip: "You don't mark disputes yourself. When a family disputes a card or bank payment (a chargeback), Stripe tells IlmIQ: the payment shows as Disputed with the dispute date and reason, the family's AutoPay is paused, and admins are notified. When the dispute closes, the payment's status updates on its own.",
        screenshot: "/help/payment-detail.png",
      },
    ],
  },
  {
    slug: "manage-discounts",
    lastUpdated: "2026-10-13",
    relatedGuides: ["zakat-fund", "correcting-invoices", "setup-fee-types"],
    emptyState:
      "Empty discounts? Most schools start with: Sibling Discount (auto-applied via fee types), Scholarship (manual), and Zakat-Based Aid (manual, reconciled on the Zakat Fund page).",
    faq: [
      {
        question: "Is there a discount approval workflow?",
        answer:
          "No — applying a discount rule to a student takes effect immediately. There is no approval step for discounts an admin applies. Discount requests made on the application form (Zakat / discount request) ARE reviewed: the admin decides during application approval.",
      },
      {
        question: "Can I limit how much a discount gives?",
        answer:
          "Not automatically. A discount rule has no cap. Track Zakat on the Zakat Fund page and review all discounts regularly with Reports > Discount Analysis.",
      },
      {
        question: "Where does sponsor funding (e.g. TEFA) fit in?",
        answer:
          "Sponsor funding isn't a discount — the sponsor pays the school directly and the family owes nothing. It's managed on the Sponsor Fund page (visible when sponsor funding is enabled for your school) and via Funding Awards on the student's page. Use discounts for reductions the family still pays against.",
      },
      {
        question: "What if a student has two discounts?",
        answer:
          "They don't add up. For each class, the student gets only the largest one. A discount for one class and a discount for all classes compete, and the bigger one wins. Sibling discounts are separate; see the \"Allow Discount Stacking\" setting in Settings → Discounts.",
      },
      {
        question: "Why does a 100% discount still leave something owed?",
        answer:
          "Fees your school collects for a partner institution (fee types marked \"collected on behalf of a partner institution\") can't be discounted or waived. Every discount, 100% Zakat included, covers only your school's own fees, so the family still owes the partner fee.",
      },
    ],
    title: "Creating and Applying Discounts",
    description:
      "Set up scholarships, Zakat-based aid, and custom discounts, and apply them to students.",
    category: "billing",
    icon: "LocalOffer",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Navigate to Discounts",
        description:
          'Click "Discounts" in the left sidebar under Billing. You\'ll see existing discount rules with a search box (it matches the name and description), Status and Category filters (the list defaults to Active rules), plus an Export CSV action.',
        screenshot: "/help/discounts-list.png",
      },
      {
        title: "Create a Discount Rule",
        description:
          'Click "Create Discount" to set up a new rule. Enter the name, choose the type (Percentage or Fixed Amount), set the value, and select a category: General, Sibling, Zakat, or Scholarship.',
        screenshot: "/help/discount-form.png",
      },
      {
        title: "Apply to a Student",
        description:
          'Go to a student\'s detail page and click "Apply Discount" in the Discounts section (or use "Apply to Student" on the discount\'s own page). Pick the discount, set Valid From and an optional Valid Until, and add notes. A discount applied here covers all of the student\'s classes. A discount granted when you approve an application covers only that class, and the student page shows it as "for <class name>". Discounts take effect on the next invoice; invoices already issued don\'t change. To stop one, use the End icon (it ends today, and a discount that hasn\'t started yet is cancelled) or the Remove icon. Turn on "Show history" to see ended and removed discounts.',
        screenshot: "/help/discount-apply.png",
      },
    ],
  },
  {
    slug: "zakat-fund",
    lastUpdated: "2026-10-14",
    emptyState:
      "Nothing here yet? The Zakat Fund page fills in once Zakat-category discounts are applied to students and their invoices are generated. Set up a Zakat discount rule first (Discounts), apply it to qualifying students, and come back after the next invoice run.",
    faq: [
      {
        question: "What counts toward the Zakat fund?",
        answer:
          "Any discount in the Zakat category, in both forms it reaches an invoice: the Zakat discount lines on a student's invoice, and a Zakat discount applied to a whole invoice with \"Apply Discount\" on the invoice page. Voided invoices and voided credits never count. The total is exactly the sum of the Student Coverage rows: the money the Zakat account owes your operations account, with no manual bookkeeping. A Zakat discount applied to a whole invoice with several students is split between them in proportion to each one's remaining charges on that invoice (their charges minus their Zakat discount lines), to the cent. Amounts are counted as they were given, even if a rule's percentage was changed later. Zakat discount lines on a student's invoice never cover fees collected for a partner institution; a Zakat discount applied with Apply Discount is a percentage of the invoice's open balance, partner fees included, and is counted as given. Give Zakat with Apply Discount and pick the Zakat rule, not with Issue Credit: a credit issued by hand is not counted as Zakat.",
      },
      {
        question: "Which students are listed?",
        answer:
          "Every student who has received Zakat, most recent Zakat first (click a column heading to sort differently). Last Zakat is the most recent invoice date of a Zakat discount, or the day a Zakat discount was applied to an invoice. Zakat Rule shows the student's own Zakat rule (marked \"inactive\" if it has ended). \"No Zakat rule assigned\" means the student was given Zakat without having a Zakat rule; review those. A student with a Zakat rule who hasn't been given any Zakat yet isn't listed, and the student count on the first card counts only students who received Zakat.",
      },
      {
        question: "What is Pending, and how does the date filter work?",
        answer:
          "Pending = all Zakat given minus all transfers recorded. Clear the date filter before recording a transfer; a filtered view shows only Zakat given in that period. The filter is on the invoice date, and while it is set the Pending card and each student's Covered, Pending and Status are hidden, because transfers are not tied to a period. If a transfer covered Zakat that was later voided (a Zakat credit or invoice), the page names those students: that money still counts as transferred, so Pending Balance can be lower than the students' pending amounts added up. \"Not linked to a student\" is Zakat applied to an invoice with no student lines; it counts in the total but a transfer can't be allocated to it.",
      },
      {
        question: "Can I report Zakat distributions to donors?",
        answer:
          "Yes — use Export CSV on the Zakat Fund page for per-student coverage rows. Reports > Discount Analysis adds a category breakdown of all discounts over a date range.",
      },
      {
        question: "What if I recorded a transfer wrong?",
        answer:
          "Delete it from Transfer History — its allocations are reversed and affected students may go back to Pending. Then record the corrected transfer.",
      },
    ],
    title: "Managing the Zakat Fund",
    description:
      "Reconcile Zakat-based aid: see what's been given, record transfers from your Zakat account, and track per-student coverage.",
    category: "billing",
    icon: "VolunteerActivism",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Zakat Fund",
        description:
          'Click "Zakat Fund" in the left sidebar under Billing. Admins and principals can view it. Only admins see "Record Transfer" and the "Delete" action on a transfer; principals get the same page without them. The page reconciles Zakat aid you\'ve granted with actual money moved from your Zakat account.',
        screenshot: "/help/zakat-fund-overview.png",
      },
      {
        title: "Review Fund Status",
        description:
          "Three cards summarize the position: Total Zakat Given (Zakat discount lines plus Zakat discounts applied to a whole invoice, with the number of students who received Zakat), Total Transferred (from the Zakat account to operations), and Pending Balance (all Zakat given minus all transfers recorded: what still needs a transfer). Below them, the Student Coverage table lists every student who received Zakat, most recent first, as Covered, Partial, or Pending. Filter by invoice date to see only the Zakat given in a period; clear the filter before recording a transfer.",
        screenshot: "/help/zakat-fund-status.png",
      },
      {
        title: "Record a Transfer",
        description:
          'When you move money from the Zakat account, click "Record Transfer". Enter the amount (pre-filled with the pending balance when no date filter is set), date, reference, and notes. The transfer auto-allocates to students oldest-first, flipping each to Covered or Partial — the success banner tells you how many students were covered.',
        screenshot: "/help/zakat-fund-distribute.png",
        tip: "Transfer History keeps every transfer with a per-student allocation breakdown (View), who recorded it, and a Delete action that reverses the allocations if you made a mistake.",
      },
    ],
  },
  {
    slug: "family-statements",
    lastUpdated: "2026-10-14",
    emptyState:
      "Empty statements? Statements are generated automatically on your billing day, right after that month's invoices. To create them on demand, use Batch Generation in Statement Generation mode, after the month's invoices exist.",
    faq: [
      {
        question: "Statement vs. Invoice — what's the difference?",
        answer:
          "Invoices charge for fees. Statements summarize all activity (invoices + payments + refunds + credits + balance) over a period. Parents typically want statements for their records; the school keeps both.",
      },
      {
        question: "Why don't the statement totals match the Invoices page?",
        answer:
          "Statement summaries deliberately exclude voided invoices and all sponsor-funded activity (sponsor invoices on the charge side, sponsor remittances on the payment side) — they reflect what families themselves were charged and paid. The Invoices page includes sponsor money.",
      },
    ],
    title: "Generating Family Statements",
    description:
      "Generate and send account statements to families showing all charges, payments, and balances.",
    category: "billing",
    icon: "Description",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Statements",
        description:
          'Click "Statements" in the left sidebar under Billing (admins). The Family Statements page shows summary cards for the selected period — statement count, total charges, total payments (with refunds and net paid underneath when there were refunds), and families with a balance — plus a period selector, family search, and sortable columns that span all pages.',
        screenshot: "/help/statements-list.png",
      },
      {
        title: "Generate Statements",
        description:
          'Statements generate automatically on your billing day, right after the month\'s invoices. To generate on demand, open Batch Generation in the sidebar and switch the mode toggle to "Statement Generation" for the month you want. Generate invoices first, or the statements will show $0.00 in charges.',
        screenshot: "/help/statement-generate.png",
      },
      {
        title: "Review and Send",
        description:
          "Each new statement is emailed to the family's primary guardian automatically. Open a statement to see all invoices, payments, refunds, credits, and the running balance, or use the row menu on the list: View Details, Download PDF, or Resend Email to send it again. The summary shows Total payments, Refunds and Net paid as positive amounts; in the transaction list a refund is a '+' line on the day it happened, because money handed back adds to the balance. A refunded payment still shows as a payment on the day it was made. Export CSV saves every statement that matches your search and period, not just the current page, with Late Fees, Total Payments, Refunds and Credits columns so each row adds up to its closing balance; the button shows its progress, and clicking it again cancels the export. Voided credit memos aren't listed on statements.",
        screenshot: "/help/statement-preview.png",
        tip: "The summary cards on the statements page show live data from invoices and payments, so they always reflect the latest financial picture even if individual statements haven't been regenerated.",
      },
    ],
  },
  {
    slug: "payment-methods",
    lastUpdated: "2026-10-12",
    emptyState:
      "Empty payment methods? Tell the family to log into the parent portal and add a card — it's secure (Stripe-vaulted) and faster than emailing card details. Or admin-add one on their behalf if they prefer.",
    relatedGuides: ["understanding-autopay", "pausing-autopay", "record-payments"],
    troubleshooting: [
      {
        problem: "A parent sees \"Online payments aren't set up for your school yet.\"",
        solution:
          "Your school hasn't finished connecting online payments, so cards and bank accounts can't be saved or charged. An admin needs to complete the Stripe tab in Settings. Until then, record cash and check payments from Payments → \"Record Payment\".",
      },
    ],
    faq: [
      {
        question: "Where are card numbers stored?",
        answer:
          "Stripe. We never see, store, or transmit card numbers. We only get a token + last4 + brand for display.",
      },
      {
        question: "Does adding a method auto-enroll AutoPay?",
        answer:
          "It depends on your school's setting. If 'Auto-Enroll Families' is OFF (Settings → AutoPay), saving a method and enabling AutoPay are separate actions — the family can opt in from their portal or you can enable it on their behalf. If 'Auto-Enroll Families' is ON, the family's first successful online payment turns AutoPay on. It uses their default method, or, if they have none, the method they just paid with. Parents can't turn off AutoPay that was switched on this way, and auto-enroll never switches back on an AutoPay that was turned off or paused.",
      },
      {
        question: "Can ACH (bank account) be saved for AutoPay?",
        answer:
          "Yes. Both card and bank-account payment methods support AutoPay — the Stripe form offers card and US bank account, and both save a reusable payment method that AutoPay can charge.",
      },
      {
        question: "Does adding a card pay the family's balance?",
        answer:
          'No. This page only saves the method for later. Parents settle a balance with the "Pay $… Now" button, which the parent portal\'s Payment Methods page shows next to their outstanding balance for exactly this reason.',
      },
      {
        question: "A saved card disappeared from the list. Why?",
        answer:
          "When a card is removed through Stripe, our payment processor, it's removed here too, and AutoPay stops trying to charge it. Ask the family to add a new card and set it as default so AutoPay can continue.",
      },
    ],
    title: "Managing Family Payment Methods",
    description:
      "Help families save payment methods for online payments and AutoPay enrollment.",
    category: "billing",
    icon: "CreditCard",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Payment Methods",
        description:
          'Open a family\'s detail page and, next to Payment Methods, click "Manage". (Parents manage their own methods on the portal\'s Payment Methods page, which also shows any balance with a "Pay $… Now" button, and their AutoPay setting.)',
        screenshot: "/help/payment-methods-list.png",
      },
      {
        title: "Add a Payment Method",
        description:
          'Click "Add Payment Method" — it opens a page with the Stripe-powered form where the family (or admin on their behalf) can securely add a credit card or bank account. Card details are stored by Stripe — no sensitive data touches your servers.',
        screenshot: "/help/payment-method-add.png",
        tip: "Saved payment methods enable AutoPay, which automatically charges the family's default method when invoices come due.",
      },
      {
        title: "Manage Saved Methods",
        description:
          "The list shows each method's brand, last 4 digits, expiration, nickname, status, and a Default chip (expired cards are flagged). Use the row menu to Set as Default, Edit Nickname, or Delete. The system automatically notifies families when cards are nearing expiration.",
        screenshot: "/help/payment-methods-manage.png",
      },
    ],
  },
  {
    slug: "understanding-autopay",
    lastUpdated: "2026-10-12",
    emptyState:
      "If you don't see any AutoPay-enrolled families yet, that's expected on day 1. Families enroll either by saving a payment method and toggling AutoPay on, or — when school policy is set — automatically after their first successful online payment.",
    relatedGuides: ["payment-methods", "pausing-autopay", "record-payments"],
    faq: [
      {
        question: "When does AutoPay actually charge the family?",
        answer:
          "Starting on the invoice's due date. AutoPay runs daily and picks up invoices in 'issued', 'partially_paid', or 'overdue' status whose due date has arrived, for AutoPay-enrolled families — so an invoice that couldn't be charged on its due date is picked up again on later runs (subject to the retry schedule).",
      },
      {
        question: "What happens if a charge fails?",
        answer:
          "The retry schedule kicks in (configured in Settings → AutoPay). Once the first attempt and every scheduled retry have all failed, AutoPay is paused for that family. Your admins are notified, and parents who use the mobile app get an \"AutoPay paused\" alert asking them to update their card. Staff can resume from the family page. Parents can also resume it from their portal by clicking \"Disable\" and then \"Enable AutoPay\", unless their AutoPay is \"Managed by school\" (enrolled by school policy).",
      },
      {
        question: "Does AutoPay work with bank accounts (ACH)?",
        answer:
          "Yes — both card and US bank account methods work. Note that for ACH the AutoPay auto-enrollment step usually waits for settlement: while the payment is still 'processing', the system generally can't safely determine the payment method type yet, so enrollment completes once the payment succeeds (~3 business days later for ACH). Card payments enroll immediately because the charge settles instantly.",
      },
      {
        question: "Can a family enroll without making a payment first?",
        answer:
          "Yes. From their parent portal, the family can save a payment method and toggle AutoPay on at any time — they don't need to wait for an invoice.",
      },
      {
        question: "What if 'Auto-Enroll Families' is on in School Settings?",
        answer:
          "Then any family that successfully pays their first invoice online by card or bank (in the parent portal or the application upfront-payment flow; cash and checks recorded by staff don't count) is automatically enrolled into AutoPay using the same payment method. The school controls this from Settings → AutoPay.",
      },
    ],
    troubleshooting: [
      {
        problem: "A family's card vanished and AutoPay stopped charging",
        solution:
          "If a card is removed at the bank or by the payment provider, it's removed here too, so AutoPay never charges a card that no longer exists. If it was the family's only card, AutoPay stays on but has nothing to charge, and the family page shows \"AutoPay is enabled but no default payment method is set.\" Ask the family to add a card from their portal, or add one from the family page. AutoPay picks it up on the next daily run.",
      },
      {
        problem: "AutoPay charge failed with 'PaymentMethod does not belong to customer'",
        solution:
          "The family's saved card is linked to an older payment record. AutoPay fixes this by itself: it re-links the card and retries the charge in the same run. If the same failure shows up again the next day, ask the family to remove the card and add it again from their portal.",
      },
      {
        problem: "Family is enrolled but AutoPay never runs",
        solution:
          "Check, in order: (1) the family has an ACTIVE, non-expired payment method (a default is preferred but the most recent active card is used if none is flagged default), (2) there's an invoice in 'issued', 'partially_paid', or 'overdue' status with a balance, whose due date has arrived, (3) the family has added a card online at least once, so they're set up with the payment provider, (4) AutoPay isn't paused for the family — a pause comes from repeated failed charges OR from a payment dispute (chargeback) on any of the family's payments, and the family page shows a yellow \"AutoPay paused since …\" banner with a Resume button, and (5) there's no in-flight payment already pending on that invoice — the batch skips invoices with a pending/processing payment attempt so it never double-charges. Also note sponsor-funded invoices are never auto-charged (see below).",
      },
    ],
    title: "Understanding AutoPay",
    description:
      "How AutoPay enrolls families, when it charges, what happens on failure, and how to keep it healthy across your school.",
    category: "billing",
    icon: "Payment",
    estimatedTime: "6 min",
    steps: [
      {
        title: "How a family becomes enrolled",
        description:
          "There are three paths. (1) Manual: from the family detail page, click the AutoPay toggle. The family must already have a default payment method on file, otherwise you'll be prompted to add one first. (2) Parent self-service: a parent can enroll from their portal after saving a payment method. (3) Auto-enroll: if 'Auto-Enroll Families' is on in Settings → AutoPay, every family is enrolled automatically after their first successful online payment (card or bank) — including the upfront payment they made on their application. Cash and check payments you record don't count.",        tip: "Auto-enrolled families see AutoPay as \"Managed by school\" in their portal and can't turn it off themselves — only school staff can. This is the right default for schools that depend on predictable cash flow.",
      },
      {
        title: "When AutoPay actually charges",
        description:
          "AutoPay runs once per day overnight (around 1–2 AM Eastern), against all enrolled families that have due, outstanding invoices in 'issued', 'partially_paid', or 'overdue' status. Per family per run, only the OLDEST unpaid invoice is charged — and the charge is exactly that invoice's remaining balance, so a partially-paid invoice is charged for its remainder and cleared. Sponsor-funded invoices are excluded entirely — the sponsor pays those via remittances, never the family's card.",        tip: "If a family has 6 months of arrears (6 invoices), it will take 6 successful daily runs to fully catch them up — one invoice per run.",
      },
      {
        title: "What happens when a charge fails",
        description:
          "Failed charges are retried per the schedule configured in Settings → AutoPay — by default day 3, day 7, and day 14 after the first failure. Each retry uses the family's CURRENT default payment method, so if you update the card between attempts, the next retry picks it up. Once all retry slots are exhausted without a successful charge, the family is auto-paused and shows up in the Failed Payments report. Resuming clears the per-invoice failure counter so the family gets a fresh retry budget.",        tip: "Every morning the system also emails the family's primary guardian about any saved card that expires this month or next (once per card per month, plus a phone alert if they use the app), so parents can update it before AutoPay starts failing.",
      },
      {
        title: "Monitoring AutoPay health",
        description:
          "Two reports help you stay on top of AutoPay: the Autopay Enrollment report shows enrollment status per family and flags already-expired cards, and the Failed Payments report lists failed AutoPay charges and affected families so you can act on them. The dashboard's overdue invoice card is your fastest signal.",      },
    ],
  },
  {
    slug: "pausing-autopay",
    lastUpdated: "2026-10-13",
    relatedGuides: ["understanding-autopay", "payment-methods"],
    faq: [
      {
        question: "Who can clear a pause?",
        answer:
          "Admins, principals and staff can resume from the family page. Parents can resume from Payment Methods in their portal: the paused banner there says why it's paused and has a \"Resume AutoPay\" button (they need a default payment method first). That works after declined charges, once they've fixed the card, and after a dispute has been settled. While one of their payments is disputed, parents can't resume AutoPay or turn it on; their portal tells them to contact the school, and only staff can resume it. A pause usually means a real problem, like an expired card, insufficient funds or a dispute, so check the cause before resuming.",
      },
      {
        question: "How does the system auto-pause AutoPay?",
        answer:
          "AutoPay follows the retry schedule in Settings → AutoPay (default: day 3, day 7, day 14 after the first failure). Each scheduled retry uses the family's current default payment method. After every retry has failed, AutoPay pauses the family. The family stays enrolled, but no further charges happen until someone resumes it.",
      },
      {
        question: "Will making a manual payment automatically un-pause?",
        answer:
          "No. A manual payment from the family does not clear the pause. Only Resume (on the family page, or \"Resume AutoPay\" in the parent portal) clears it and lets charges start again.",
      },
      {
        question: "Where can I see which families are paused?",
        answer:
          "The Autopay Enrollment Report shows how many families are paused and marks each one with a Paused chip. The Failed Payments Report shows why charges failed: attempts, amounts, last attempt date, payment method, and a breakdown of failure reasons. A family paused by a chargeback has no failed charges, so it appears only in the Autopay Enrollment Report. The family detail page also shows a yellow \"AutoPay paused since …\" banner with a Resume button, and the banner says whether declined charges or a payment dispute caused the pause.",
      },
    ],
    troubleshooting: [
      {
        problem: "I disabled AutoPay for a family but it re-enabled itself the next day",
        solution:
          "This shouldn't happen anymore: auto-enroll treats any existing AutoPay record — enabled, disabled, or paused — as 'a human has touched this, leave it alone', so a later successful payment never silently re-enables. If you see it, contact support with the family's name.",
      },
      {
        problem: "A family is paused but their payment history is clean",
        solution:
          "A payment dispute (chargeback) auto-pauses the family's AutoPay immediately, with no failed charges involved. The banner on the family page says when a dispute caused the pause, and whether it's still open. Wait until the dispute is settled before resuming — charging a disputing family again usually makes things worse. Parents can't resume while it's open. Closing the dispute doesn't resume AutoPay; you (or the parent) still have to click Resume. If the school loses the dispute, the disputed amount comes back off the invoice, so it shows a balance again and overdue reminders start again.",
      },
      {
        problem: "I want to keep AutoPay paused but still bill the family",
        solution:
          "That's already how it works. Pausing AutoPay only stops automatic charges. Invoices still get generated on the normal schedule, late fees still apply per your school's policy, and the family can still pay manually from their portal. They just won't get auto-charged.",
      },
    ],
    title: "Pausing and Resuming AutoPay",
    description:
      "Why AutoPay pauses, who can resume it, and how to handle families paused after failed charges or a payment dispute.",
    category: "billing",
    icon: "PauseCircle",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Three ways AutoPay stops charging",
        description:
          "First: school staff (or the parent, unless AutoPay is \"Managed by school\") turn AutoPay off for the family. This disables AutoPay outright. Second: AutoPay automatically PAUSES the family after the initial charge plus every scheduled retry all fail — the family stays enrolled, but charges stop until someone resumes. Third: a payment dispute (chargeback) auto-pauses the family immediately, even with a clean payment history — so a paused banner doesn't always mean failed cards.",      },
      {
        title: "What stays the same while paused",
        description:
          "Invoices keep generating on schedule. Late fees keep being assessed per your school's rules. Dunning notifications keep being sent. The family can pay manually from their portal at any time. The ONLY thing that stops is the automatic charge on the due date.",
      },
      {
        title: "Why downstream payment flows respect the pause",
        description:
          "Anything that can sign a family up for AutoPay, such as a successful online payment or an application's upfront payment, first checks whether the family has ever had AutoPay set up. If they have, whether it's on, off or paused, automatic sign-up leaves it alone. So a family you paused or switched off stays that way after their next payment.",
        tip: "Once a family has had AutoPay set up, they're never signed up automatically again. To turn it back on, use the AutoPay switch or Resume on the family page.",
      },
      {
        title: "Resuming a paused family",
        description:
          "On the family detail page, a paused family shows a yellow \"AutoPay paused since <date>\" banner that names the cause: repeated declined charges, or a payment dispute (and whether it's still open). Click Resume in that banner. It clears the pause and gives every previously stuck invoice a fresh set of retries, and the family is back in the next nightly run. If the family has no default payment method, Resume asks you to add one first.",        tip: "Update the family's payment method first if the original card was the reason for the failures — otherwise the next charge fails for the same reason and you'll bounce right back to paused.",
      },
    ],
  },
  {
    slug: "issuing-refunds",
    lastUpdated: "2026-10-14",
    relatedGuides: ["record-payments", "understanding-autopay", "family-statements"],
    faq: [
      {
        question: "How much can I refund?",
        answer:
          "Up to the original payment amount, minus any prior refunds against the same payment. The Process Refund dialog enforces this — the Amount field is capped at the remaining refundable amount (shown under the field), and the system refuses any refund larger than that.",
      },
      {
        question: "What's the difference between refunding card and cash?",
        answer:
          "Card payments are refunded through Stripe — the money goes back to the card on file (typically 5–10 business days). Cash and check refunds are recorded in the system as a return (a linked refund row shown with a minus and a Refund chip); you handle the actual cash return outside the platform.",
      },
      {
        question: "Does refunding a payment reopen the invoice?",
        answer:
          "Yes — the refund reduces the invoice's paid amount and increases its outstanding balance. If the refund equals the full payment, the invoice goes back to 'issued' (or 'overdue' depending on dates). Partial refunds leave the invoice in 'partially_paid'.",
      },
      {
        question: "Can I refund a payment that's already partially refunded?",
        answer:
          "Yes — as long as something is left to refund. The system adds up every earlier refund on the payment, and the Process Refund dialog shows what's still refundable.",
      },
      {
        question: "What if the refund fails on Stripe's side?",
        answer:
          "The refund record is created as 'pending' before Stripe confirms, then finalized or marked failed. On failure the original payment and invoice stay untouched and you'll see an error. Common causes: the original charge was already disputed, the card was canceled, or the refund window expired (Stripe limits refunds to ~120 days for most card types). A failed refund can simply be retried once the cause is fixed. If Stripe doesn't answer in time, the refund shows as processing and the message says not to refund again: it will finish on its own.",
      },
      {
        question: "I resubmitted a refund and it said the refund is already being processed — did it fail?",
        answer:
          "No — that's the double-refund protection. Your first refund of that amount is still awaiting the payment provider's confirmation, so the resubmit returned that same refund and nothing was refunded twice. If it's still processing after a few minutes, submitting it again checks with Stripe and finishes it (or, if Stripe never received it, makes it once). If you're told it needs a manual check, contact support with the payment.",
      },
    ],
    troubleshooting: [
      {
        problem: "Refund button is disabled or missing",
        solution:
          "Three reasons: (1) the payment status isn't 'succeeded' or 'partially_refunded' — only those statuses can be refunded; (2) the payment is itself a refund — refunds can't be refunded; (3) nothing is left to refund (the remaining refundable amount is zero). Only admins see \"Process Refund\", so principals and staff won't see it at all.",
      },
      {
        problem: "My refund was rejected as too large",
        solution:
          "The payment has already been partly refunded. The Process Refund dialog shows \"Remaining refundable\" under the amount, and you can't refund more than that. Earlier refunds appear in the Payments list as rows marked Refund.",
      },
      {
        problem: "After refunding, the family statement still shows the old balance",
        solution:
          "A Stripe refund is applied to the invoice as soon as it's processed, so the invoice balance updates right away. Family statements are snapshots, though: a statement created before the refund won't show it, and generating again for the same month returns that same statement. The refund appears as its own 'Refund' line on the statement for the month it was processed, adding back to the balance; the parent portal's Billing Statements page shows it right away as \"Refund to you\". If the invoice balance itself is wrong after a completed refund, contact support.",
      },
    ],
    title: "Issuing Refunds",
    description:
      "Process full or partial refunds on card and cash payments, understand the impact on invoices, and avoid the most common refund mistakes.",
    category: "billing",
    icon: "Undo",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Open the payment to refund",
        description:
          "Open the payment from the Payments list (click the row, then \"View Full Payment\") or from the family's Statement. The payment status must be 'succeeded' or 'partially_refunded' — fully-refunded and disputed payments can't be refunded again.",      },
      {
        title: "Click Process Refund",
        description:
          "Click the \"Process Refund\" button at the top right of the payment detail page (admins only). A dialog opens with required Refund Amount and Reason fields. The amount is capped at the remaining refundable amount (original minus any prior refunds), shown right in the dialog — you can't type more.",        tip: "Always include a Reason — it's required and kept in the payment's audit trail.",
      },
      {
        title: "Card vs. cash behavior",
        description:
          "If the original payment was a card or ACH (Stripe), the refund goes back to the same payment method automatically and shows up in Stripe within seconds. If the original was cash or check, the system records a linked return row against the payment — you're responsible for handing back the cash or voiding the check externally.",      },
      {
        title: "Verify the invoice balance updated",
        description:
          "After the refund completes, click through to the original invoice. The original payment's status moves to 'refunded' (or 'partially_refunded' for partials), and the invoice's outstanding balance and status are updated automatically. If you don't see the change, hard-refresh.",      },
    ],
  },
  {
    slug: "late-fees-and-dunning",
    lastUpdated: "2026-10-13",
    relatedGuides: ["tenant-settings", "generate-invoices", "family-statements"],
    faq: [
      {
        question: "When do late fees get assessed?",
        answer:
          "Every morning the system picks up overdue invoices whose due date is older than today minus the grace period. It applies the configured late fee — either a flat amount or a percentage of the outstanding balance, clamped to the min/max bounds when set (so a 5% fee on a $2000 invoice with max=$50 ends up at $50). Each invoice is assessed AT MOST ONCE, ever — a long-overdue invoice never accrues a second late fee. Sponsor-funded invoices are never assessed.",
      },
      {
        question: "Can a family avoid late fees?",
        answer:
          "By paying before the grace period ends. If a fee is assessed in error, an admin can click Remove next to the late fee line item on the invoice detail page. That posts a credit memo for the fee, or for the balance due if that's smaller. Once the whole fee is credited the line shows \"Removed\" (\"Partly removed\" if only some of it was), and Remove goes away, so a fee can't be credited twice. Removals made earlier, including ones from Issue Credit Memo with the \"Late fee removal\" category, count too. Remove only appears while the invoice still has a balance and isn't void or written off; on a fully-paid invoice, refund the fee from the payment instead.",
      },
      {
        question: "What is dunning?",
        answer:
          "Dunning is the automated cadence of reminder emails sent on invoices that are past due. The cadence runs at days 7, 14, 30, and 45 past the due date — four stages, hardcoded today. The email's tone and styling escalate automatically as the invoice ages; the templates aren't editable in-app. Parents who use the mobile app also get a phone alert with each reminder.",
      },
      {
        question: "Does dunning stop automatically in any case?",
        answer:
          "Yes. A paid-off invoice simply drops out of the cadence. An active payment dispute pauses dunning for the invoice (recorded as 'dispute_active'). And an invoice with an in-flight payment attempt — including a bank payment still processing or an authorized card awaiting capture — is silently skipped so the family isn't dunned while their money is on the way. Families without a primary guardian email are also skipped.",
      },
    ],
    troubleshooting: [
      {
        problem: "A family was charged a late fee but they paid on time",
        solution:
          "Check the payment date vs. the invoice's due_date and your grace-period setting. If the payment is genuinely on time and the invoice still has a balance, open the invoice detail page and click Remove next to the late fee line item (a reason of up to 82 characters is required; the system posts a credit memo). If part of the fee is already paid, Remove credits only the unpaid part and the dialog says so; refund the rest from their payment. If they've paid the whole fee, refund the fee amount from their payment instead.",
      },
      {
        problem: "Late fees keep being assessed every day on the same invoice",
        solution:
          "This shouldn't happen: the system allows one late fee per invoice, ever, and checks twice before adding one. If you're seeing daily duplicates, contact support.",
      },
      {
        problem: "Percentage fees coming through smaller / larger than expected",
        solution:
          "The system clamps percentage fees to the configured minimum and maximum. A low maximum caps every assessment at that amount; a high minimum floors small-invoice fees up to it. To get raw uncapped percentage fees, clear both bounds in Settings → Billing & Late Fees.",
      },
    ],
    title: "Late Fees and Dunning",
    description:
      "How automatic late fees are assessed, how the dunning email cadence works, and how to handle exceptions.",
    category: "billing",
    icon: "Schedule",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Configure late fee policy",
        description:
          "Go to School Settings → Billing & Late Fees. In the Late Fee Policy card, turn on \"Enable Late Fees\", then choose \"Flat Amount\" or \"Percentage of Balance\" and enter the fee. It starts at a flat $25. For a percentage fee you can also set an optional Minimum Fee and Maximum Fee: a 5% fee on a $2000 invoice with a $50 maximum comes to $50. The grace period is in the Billing card on the same tab (\"Late Fee Grace Period (days)\", 0–30). Settings apply to the whole school.",        tip: "Most schools use a flat $25 fee with a 5-day grace period. Percentage fees with a sensible maximum work well for schools where invoice sizes vary widely.",
      },
      {
        title: "How the assessment runs",
        description:
          "Every morning (around 6–7 AM Eastern), the system first marks newly overdue invoices, then adds the late fee as a line item to any overdue invoice whose due date is more than the grace period ago. Each invoice gets a late fee at most once, ever, so running it again never charges twice. Sponsor-funded invoices never get one.",
      },
      {
        title: "What the family sees",
        description:
          "The late fee shows up as a separate 'Late Fee' line item on their invoice, raising the outstanding balance. On the family statement it's folded into the period's charges, appearing as a 'Late Fee' row in the transaction detail.",      },
      {
        title: "Dunning cadence",
        description:
          "Dunning emails fire on a fixed cadence — days 7, 14, 30, and 45 past the due date — with the message tone escalating automatically as the invoice ages. The cadence days are hardcoded and not currently configurable. After an outage or pause, the cadence catches up from the lowest unsent stage rather than skipping ahead.",      },
      {
        title: "Automatic dunning pause",
        description:
          "The dunning batch won't keep emailing when the situation doesn't warrant it: a cleared balance drops the invoice from the cadence, an active payment dispute pauses it (recorded as 'dispute_active'), and an invoice with an in-flight payment attempt — a processing bank payment, or an authorized card awaiting capture — is skipped until it settles.",
      },
      {
        title: "Excluding a family from dunning",
        description:
          "On the family detail page, toggle 'Pause dunning emails' on. The next cadence run skips them entirely — no overdue reminder emails, regardless of how old their invoices are. Late fees still accrue per your school's policy; only the email reminders stop. Toggle off to resume.",
        tip: "Use for families on a payment plan or special billing arrangement. Changes to the toggle are logged.",
      },
    ],
  },
  {
    slug: "sponsor-funding",
    lastUpdated: "2026-09-03",
    relatedGuides: ["manage-applications", "generate-invoices", "record-payments"],
    emptyState:
      "Don't see a Sponsor Fund page? Sponsor funding is enabled per school — contact IlmIQ support to set up your program (sponsor name, program name, and annual rate). Once enabled, the Sponsor Fund page appears under Billing for admins and principals.",
    faq: [
      {
        question: "Is sponsor funding a discount?",
        answer:
          "No. A discount reduces what the family pays; sponsor funding means the sponsor pays the school directly and the family owes NOTHING for the covered period. The student's tuition is billed to the sponsor on an annual sponsor invoice instead of to the family.",
      },
      {
        question: "Can a family accidentally pay a sponsor invoice?",
        answer:
          "No — the system enforces the separation. Sponsor invoices hide the family payment actions, AutoPay skips them, dunning never emails about them, and family money can't be recorded against them. The only way money lands on a sponsor invoice is a Sponsor Fund remittance.",
      },
      {
        question: "What happens if the sponsor stops covering a student?",
        answer:
          "Revoke the award from the student's Funding Awards card (a reason is required, plus the last covered day). Family billing resumes for months AFTER the effective end, and you issue a credit memo on the sponsor invoice for the unearned remainder — the revoke dialog walks you through it.",
      },
      {
        question: "Why do coverage dates have to be full months?",
        answer:
          "Family billing is monthly — a month is either sponsor-covered (skipped in family billing) or family-billed. A mid-month coverage start leaves that whole month family-billed, which is why the grant dialog warns about it.",
      },
    ],
    title: "Sponsor-Funded Students",
    description:
      "Run a sponsor program (like TEFA): grant funding awards, bill the sponsor annually, record remittances, and keep families at zero owed.",
    category: "billing",
    icon: "Handshake",
    estimatedTime: "6 min",
    steps: [
      {
        title: "How parents apply",
        description:
          "On the public application form's payment step, eligible families choose your sponsor program as the payment method and enter their approval reference. Their fee quote drops to $0 for covered children (shown with a Sponsored chip), and no payment is collected. The application arrives flagged with a sponsor chip and banner.",
      },
      {
        title: "Approve with verification",
        description:
          "On a sponsor-funded application, the approve dialog adds a sponsor block: the parent's reference (verify it with the sponsor!), school year, coverage start/end dates, annual amount (blank = your program rate), and the sponsor invoice due date. You must check the \"I verified this student's award\" attestation before Approve unlocks. Approval creates the funding award AND the annual sponsor invoice in one step — the family is never billed.",
      },
      {
        title: "Grant or revoke awards directly",
        description:
          'For already-enrolled students, use the "Funding Awards" card on the student\'s detail page: "Grant Award" creates the award and sponsor invoice (and lists any existing family invoices inside the covered period so you can void or credit them); the revoke action shortens coverage when sponsorship ends.',
      },
      {
        title: "The Sponsor Fund page",
        description:
          'Click "Sponsor Fund" under Billing. Four cards summarize the program — Active Awards, Invoiced, Collected, Outstanding — above the Open Sponsor Invoices table (what the sponsor still owes, oldest due first) and the Remittance History.',
      },
      {
        title: "Record sponsor money as remittances",
        description:
          'When the sponsor\'s check or wire arrives, click "Record Remittance". Enter the amount, unique reference, received date, and method, then split it across open sponsor invoices — "Auto-fill (oldest due first)" allocates it for you, and the split must equal the remittance amount exactly (no credit-on-account). Recording creates one payment per family behind the scenes.',
        tip: "Reports keep the money separate too: family metrics (revenue, collections, AR aging) track family money, with the sponsor receivable shown as its own figure.",
      },
    ],
  },
  {
    slug: "fundraising-campaigns",
    lastUpdated: "2026-10-12",
    relatedGuides: ["potluck-signups", "dashboard-overview"],
    emptyState:
      'No campaigns yet? Click "New Campaign", give it a title and an optional goal, add sponsorship items (or just allow open donations), and publish — the public sponsor link goes live immediately.',
    faq: [
      {
        question: "Do campaign gifts show up in my revenue reports?",
        answer:
          "No — deliberately. Campaign gifts are tracked on a separate ledger so they never distort tuition revenue or collection metrics. The dashboard's Fundraising band shows the amount raised while a campaign is active.",
      },
      {
        question: "Can sponsors pay by bank (ACH)?",
        answer:
          "No — campaign checkout is card-only, to keep small one-time gifts friction-free (bank verification and multi-day settlement cause abandonment). Bank payments remain available for tuition.",
      },
      {
        question: "Can I edit items after people have sponsored them?",
        answer:
          "Yes, with guardrails: an item with sponsors can't be deleted (deactivate it instead — the Active switch hides it from the public page), and a quantity limit can't be lowered below what's already sponsored or sitting in a live checkout. Limits never oversell.",
      },
      {
        question: "What happens if a sponsor disputes their card charge?",
        answer:
          "The gift is marked Disputed in the Sponsors list, it stops counting toward the amount raised, and admins get an alert. Respond to the dispute promptly, because the payment provider sets a deadline. If the school wins, the gift goes back to Paid and counts again. If it loses, the gift is marked Refunded.",
      },
    ],
    title: "Fundraising Campaigns",
    description:
      "Run a fundraising campaign with a public, no-login sponsor link — sponsorship items, open donations, goals, and a supporters wall.",
    category: "community",
    icon: "Campaign",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Create the Campaign",
        description:
          'Go to Campaigns under Community and click "New Campaign". Only the title is required; optionally add a description, a fundraising goal (shown as a progress bar), and an end date (the page stops accepting sponsorships after it). You land in the campaign builder.',
      },
      {
        title: "Add Sponsorship Items",
        description:
          'In the builder, click "Add item" for each sponsorship (e.g. "Sponsor a trophy" at $40): name, price each, optional description, and an optional quantity limit that never oversells. Or skip items entirely and rely on open donations.',
      },
      {
        title: "Tune the Public Page",
        description:
          'In Settings, control the public experience: show or hide the amount raised and progress, enable a public supporters wall, and allow an open donation amount with a custom label (default "Generous Donation" — sponsors get quick-pick amounts plus a free entry).',
      },
      {
        title: "Publish and Share",
        description:
          "Click Publish — the public sponsor link goes live at a readable URL (based on the campaign title) with your school's branding. Use Copy and Preview in the builder's \"Public sponsor link\" card. Sponsors pick items and/or a donation, enter their details, and pay by card; receipts are emailed automatically.",
        tip: "You can Pause a campaign any time (the page shows a friendly closed message) and Resume later. Ending a campaign is permanent.",
      },
      {
        title: "Track Sponsors",
        description:
          "The Sponsors card lists every contribution with amount, status, and date, filterable by status, with a CSV export of paid sponsors. The Raised card tracks progress toward your goal, and the admin dashboard shows a Fundraising band while any campaign is active.",
      },
    ],
  },
  {
    slug: "potluck-signups",
    lastUpdated: "2026-10-12",
    relatedGuides: ["fundraising-campaigns", "messaging-overview"],
    emptyState:
      'No sign-up sheets yet? Click "New Sign-up Sheet", fill in the event details, and add at least one item people can bring. Publish when ready — the public link needs no login.',
    faq: [
      {
        question: "Do volunteers need an account?",
        answer:
          "No — the public sign-up link works without any login. Volunteers pick what to bring (or contribute cash toward), enter their name and email, and they're done. The footer even says so.",
      },
      {
        question: "How do cash contributions work?",
        answer:
          'Items can be set to "Bring it", "Contribute cash", or "Either". For cash, you set the per-unit amount (e.g. per pizza) and volunteers pay by card through Stripe — they get an emailed receipt. For items with slots, bringing and paying draw from the same remaining pool, so an item is never over-covered.',
      },
      {
        question: "Can I remove or edit a sign-up?",
        answer:
          "You can Remove a sign-up from the builder's Sign-ups table — their claimed slots are freed. There's no edit; remove and have them sign up again. An item that already has sign-ups can't be deleted — deactivate it instead.",
      },
    ],
    title: "Potluck Sign-up Sheets",
    description:
      "Organize volunteer sign-up sheets with a public, no-login link — item slots, cash contributions, and coverage tracking.",
    category: "community",
    icon: "Restaurant",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Create a Sign-up Sheet",
        description:
          'Go to Potlucks under Community and click "New Sign-up Sheet". Enter the title, event date, time, and location (an address enables one-tap directions on the public page), optional notes and coordinator contact, and at least one item with how many slots you need.',
      },
      {
        title: "Build the Item List",
        description:
          'In the builder, refine items with "Add item": name, optional description, and how parents can help — Bring it (slots), Contribute cash (a per-unit dollar amount), or Either. The item cards track coverage live: slots claimed, cash raised, and a Covered chip when an item is fully spoken for.',
      },
      {
        title: "Publish and Share the Link",
        description:
          "Click Publish to activate the public link (it needs at least one active item), then Copy it from the \"Public sign-up link\" card and share it with families — anyone with the link can sign up, no login required. The public page carries your school branding, an add-to-calendar button, and a live coverage bar.",
        tip: 'Sheet settings include "Show who signed up on the public page" — nice for community events, off by default.',
      },
      {
        title: "Track Sign-ups",
        description:
          "The Sign-ups table lists every volunteer with what they're bringing and any cash contributed, with a CSV export. Use Pause to stop accepting sign-ups temporarily; Close is permanent and freezes the sheet.",
      },
    ],
  },
];

// =============================================
// ATTENDANCE
// =============================================

const attendanceGuides: Guide[] = [
  {
    slug: "take-attendance",
    lastUpdated: "2026-10-13",
    emptyState:
      "If today's sessions are empty, either today isn't a scheduled day for any class, or sessions haven't been generated yet. Sessions are created automatically every morning for the coming week; to create them further ahead, use \"Sync\" on the class detail page and pick a date range.",
    troubleshooting: [
      {
        problem: "I can't find a student in the attendance list.",
        solution:
          "Only currently-enrolled students show up. Check that the student's enrollment in this class is Active (not Paused or Withdrawn). If the student was just enrolled, refresh the page.",
      },
      {
        problem: "Yesterday's session isn't there.",
        solution:
          "Sessions are generated based on the class schedule. If yesterday wasn't a scheduled day for this class, no session is created. School-calendar holidays also auto-skip.",
      },
      {
        problem: "I can't mark a session. The buttons are missing.",
        solution:
          "Attendance can only be recorded on or after the session date, so a session for a future day opens read-only. Completed and cancelled sessions are locked too: no one can change their marks, on the website or in the mobile app, so mark everyone before you click \"Complete\".",
      },
    ],
    faq: [
      {
        question: "What's the difference between Absent and Excused?",
        answer:
          "Both are recorded distinctly, but note that the attendance-rate percentage counts only Present and Late as attended — an Excused absence still lowers the rate like an Absent does. Use Excused so the record shows the absence was documented (illness, school-approved travel, etc.).",
      },
      {
        question: "Can a parent see today's attendance live?",
        answer:
          "Yes — attendance records appear in the parent portal as soon as they're saved (you don't have to complete the session first).",
      },
      {
        question: "Can teachers take attendance on their phone?",
        answer:
          "Yes. Teachers can open their class sessions in the mobile app and mark attendance there. Marks saved in the app show up on the web right away.",
      },
    ],
    title: "Taking Daily Attendance",
    description:
      "Record student attendance for each class session with support for multiple statuses and bulk actions.",
    category: "attendance",
    audience: OFFICE_AND_TEACHERS,
    icon: "EventNote",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Attendance",
        description:
          'Click "Attendance" in the left sidebar (under Operations for office staff; teachers see it in their main list). Staff accounts land here when they sign in. The Attendance Dashboard has three tabs — Today\'s Sessions (with Total Sessions / Completed / Pending / Cancelled counts), By Class, and All Sessions.',
        screenshot: "/help/attendance-today.png",
      },
      {
        title: "Select a Session",
        description:
          "Click on a class session to open the attendance form. You'll see a list of all actively-enrolled students for that class. Sessions are created automatically every morning for the coming week; if one is missing, sync them from the class detail page (see 'Generating Attendance Sessions').",
        screenshot: "/help/attendance-session.png",
      },
      {
        title: "Mark Attendance",
        description:
          "For each student, select their status: Present, Absent, Late, Excused, or Early Leave.",
        screenshot: "/help/attendance-mark.png",
        tip: "Use \"Mark Remaining Present\" first, then adjust the students who were absent or late. After you confirm, it saves right away and leaves alone anyone you have already marked. If everyone already has a status, it offers \"Replace All\" instead, which sets the whole roster to Present.",
      },
      {
        title: "Save and Complete",
        description:
          'Unsaved marks show a banner with a "Save" button with the number of changes waiting (for example "Save 3 Changes"; on a phone, "Save Changes" under the count) — and if you try to leave with unsaved marks, you\'ll be asked to save or discard. When every student is marked, click "Complete" to finalize and lock the session (it auto-saves pending marks first). Use the Previous / Next buttons in the Session Navigator to move between sessions, and the All Sessions tab for history.',
        screenshot: "/help/attendance-history.png",
      },
      {
        title: "Cancel a Session",
        description:
          'If a class didn\'t meet (a snow day, a teacher out sick), open the session and click "Cancel". Pick a reason (Holiday, Teacher Absent, Weather, Emergency, or Other), add a note if you like, and click "Cancel Session". This can\'t be undone: the session stays locked and shows the reason (for example "Reason: Teacher Absent") to anyone who opens it later. A completed session can\'t be cancelled, and a cancelled one can\'t be completed.',
      },
    ],
  },
  {
    slug: "generate-sessions",
    lastUpdated: "2026-11-01",
    emptyState:
      "Empty sessions list? Every morning the system creates sessions for the next 7 days for every active class, so most schools rarely need this by hand. To create sessions further ahead, sync them yourself — pick a date range and the system creates one session per scheduled class day, automatically skipping calendar dates marked as affecting attendance.",
    faq: [
      {
        question: "Do I need to generate sessions manually at all?",
        answer:
          "Usually not — every morning the system creates the next 7 days of sessions for all active classes automatically. Manual sync is for creating sessions further ahead (e.g. the whole month) or right after building a new schedule.",
      },
      {
        question: "Can I generate for the whole year at once?",
        answer:
          "Yes — the dialog even has an 'Entire Class Period' quick-select. Re-running is safe: existing sessions are left untouched, so you'll never get duplicates. Note that holidays added to the calendar later don't auto-cancel already-generated sessions — cancel those one at a time from the session page (see 'Taking Daily Attendance').",
      },
      {
        question: "What happens when I remove a time slot from the schedule?",
        answer:
          "Removing a slot (the bin icon on the Class Schedules page, then Remove) stops it from today on. Past days stay on the class's record, so last month's reports don't change, and the slot isn't copied when you clone the class. Upcoming sessions for that slot that nobody has marked yet are cancelled (today's only if it hasn't started). A slot that hasn't started yet, or one on a class template, is simply deleted. If you add the same day and time again later, its cancelled sessions come back.",
      },
      {
        question: "I added a slot by mistake and the class never met then. How do I get rid of it completely?",
        answer:
          "In the remove dialog, tick \"It never met on this day — remove it from past months too\" and click \"Erase from all months\" (for a slot you already removed: turn on \"Show ended\" and use its erase button). The slot is deleted outright, so past months -- including ones already sent to parents -- no longer count those days. It can't be undone, and it isn't allowed once attendance has been taken in that slot: then remove it from today on instead.",
      },
      {
        question: "I removed a slot by mistake. Can I bring it back?",
        answer:
          "Yes. Right after removing it, click Undo on the message that appears: the slot carries on exactly as before (with its original end date) and the upcoming sessions its removal cancelled come back. Later on, turn on \"Show ended\" on the Class Schedules page and click \"Add back from today\" on the slot: it starts again today, and the days in between stay off the class's record. A slot that simply reached its end date can be started again the same way. Only an admin or principal can remove, erase or add back a slot.",
      },
    ],
    title: "Generating Attendance Sessions",
    description:
      "Bulk-create attendance sessions for a class based on its schedule, so teachers can take attendance each day.",
    category: "attendance",
    icon: "DateRange",
    estimatedTime: "2 min",
    steps: [
      {
        title: "Set up the schedule first",
        description:
          "Sessions come from the class's weekly schedule, so the class needs at least one schedule entry (and at least one enrolled student) before sessions can be created. Add schedules from the class detail page.",
        screenshot: "/help/class-detail.png",
      },
      {
        title: "Open the Sync dialog",
        description:
          'On the class detail page, click "Sync" (it appears once the class has a schedule, for admins, principals and office staff; teachers don\'t see it). The "Sync Attendance Sessions" dialog opens. You can also use "Generate Attendance Sessions" on the Class Schedules page.',
        screenshot: "/help/generate-sessions-dialog.png",
      },
      {
        title: "Set Date Range",
        description:
          'Pick the start and end dates — or use the quick-selects (This Month, Next Month, Entire Class Period). Click "Sync Sessions": one session is created for each scheduled class day in the range, skipping calendar dates marked as affecting attendance; days that already have a session are left unchanged.',
        screenshot: "/help/generate-sessions-range.png",
        tip: "Re-running the sync is always safe — it never duplicates or overwrites existing sessions.",
      },
    ],
  },
];

// =============================================
// HIFDH & QURAN
// =============================================

const hifdhGuides: Guide[] = [
  {
    slug: "hifdh-daily-rating",
    lastUpdated: "2026-10-31",
    emptyState:
      'No classes on the Hifdh page? Only classes whose type is "Hifdh (Quran Memorization)" and that you teach are listed. A student shows "No plan" until their plan is set.',
    faq: [
      {
        question: "Do I have to type pages and lines every day?",
        answer:
          "No. Each student's plan says how many new lines a day, how many pages of Sabqi and how much Manzil. The day's portions follow from the plan and yesterday's ratings, so most days you only listen and tap Good, Weak or Repeat.",
      },
      {
        question: "What do Good, Weak and Repeat do to tomorrow?",
        answer:
          "Good and Weak move the student on (a Weak part goes into the parents' \"Tonight at home\" to revise). Repeat brings the same portion back tomorrow. A Sabqi Repeat means no new Sabaq lines the next day. A part not heard moves nothing.",
      },
      {
        question: "What is in the day's Sabqi?",
        answer:
          "First the lines already learned on the page the student is still on (the last few lessons), then the newest finished pages. When more pages are finished than the plan's Sabqi count, half the count is the newest pages and the rest takes turns through the older pages of the juz (at 5 pages a day, each page comes back within about a week; with 1 page a day only the newest page and recent lines are heard -- give 2 or more for the turns). Only lines with ayat count: surah headers and the basmala line are never part of a lesson.",
      },
      {
        question: "How does Juz Amma from An-Nas work?",
        answer:
          'In the plan, choose "From An-Nas (Juz 30 first)" and switch on "Juz 28–30 surah by surah from the end". The student then learns An-Nas, Al-Falaq, Al-Ikhlas and on, each surah from its first ayah; a lesson ends at the end of a surah (two short surahs can make one lesson, and the last line or two of a surah is taken the same day; a long one such as An-Naba is learned in parts). Juz 29 and 28 follow the same way; every other juz runs from its first page. Each lesson names its surahs.',
      },
      {
        question: "Another teacher rated the same student. Will I overwrite them?",
        answer:
          "No. If a co-teacher changed a part or the day's note after you opened the student, your save is refused and the sheet shows what they saved, so you can check before saving again. A changed note keeps yours in the box, with theirs shown above it and a \"Use theirs\" button. The same goes for a month's comment and for a student's plan: a co-teacher's change is shown, never silently replaced.",
      },
      {
        question: "Why can't I change a day last month?",
        answer:
          "Once the principal publishes a month's report cards, that month's ratings are locked so the card parents saw stays true. Ask the principal to reopen the month.",
      },
    ],
    title: "Rating Hifdh each day",
    description:
      "Hear each student and rate Sabaq, Sabqi and Manzil Good, Weak or Repeat; set a student's plan once and the daily lesson follows.",
    category: "hifdh",
    audience: OFFICE_AND_TEACHERS,
    icon: "MenuBook",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open today's class",
        description:
          'Click "Hifdh Progress" in the sidebar, then "Open today" on a class. The roster (beside the sheet on a computer, behind the "Students" button on a tablet or phone) shows who is still "To hear", who is absent, what is left for a student heard in part (e.g. "Sabqi, Manzil left"), and the marks of everyone heard. Use the arrows or the Day field to rate another day.',
      },
      {
        title: "Set a student's plan once",
        description:
          'A student marked "No plan" needs one: click Set plan. Choose the new lines a day, how many pages of Sabqi and how much Manzil; tap the juz already memorized (switch "Mark as" to "Just finished (Sabqi)" for a juz finished recently); and set where the next Sabaq starts. To change only the amounts later, pick "Only the daily amounts" and the student carries on where they are.',
        tip: "A plan changed after the student was heard today starts tomorrow.",
      },
      {
        title: "Listen and rate",
        description:
          'Each card shows what is due today ("Brought back" marks a portion due again after a Repeat). Tap Good, Weak or Repeat; tap it again to clear. "All good" rates every part Good in one tap ("Rest good" when some are rated already; it never changes a rating). If the student recited more or less than planned, use + and −. For Weak or Repeat, "+1 mistake" counts mistakes.',
      },
      {
        title: "Save and move on",
        description:
          'Add a note for parents if you like, then "Save · next" saves and opens the next student still to hear. Unsaved changes are kept while you switch between students (a dot marks them in the roster, with "not saved" at the top), and leaving the page or changing the day asks first.',
      },
      {
        title: "See a student's progress",
        description:
          "Click a student's name for their month: quality, pace, days heard, the juz map, every day heard, and the month's report card, where you can write your comment for the parents before the principal publishes it.",
      },
    ],
  },
  {
    slug: "hifdh-month-report-cards",
    lastUpdated: "2026-10-28",
    emptyState:
      'No report cards for a month? A student needs a Hifdh plan that month to get one ("No plan" in the list). Cards can be published once the month is over.',
    faq: [
      {
        question: "What does publishing do?",
        answer:
          "Parents can open the card in Hifdh progress from then on (no alert is sent), and that month's ratings, comments and plans lock for those students, so the card stays true to what parents saw.",
      },
      {
        question: "How do I correct a published card?",
        answer:
          'On the student\'s page, click "Reopen this card" (or tick students on the class\'s report cards and click "Reopen" in the bar at the bottom). Parents stop seeing the card; correct the ratings or the comment, then publish it again. If a later month is also published, that month still locks the student\'s days until it is reopened too.',
      },
      {
        question: 'Why does a student say "Card from their other class"?',
        answer:
          "A student who moved classes during the month gets one card, from their main class that month: the class they were heard in most. That class writes the comment and publishes it; the other class shows it as \"Card from their other class\".",
      },
      {
        question: "Some ratings were changing while I published. What now?",
        answer:
          'If a teacher changes a student\'s ratings while you publish, that student is left out (so a card never mixes old and new ratings) and named. Click "Publish again" to include them.',
      },
      {
        question: "How do I turn off the parents' evening update?",
        answer:
          'On the Hifdh page, click "Hifdh settings" and turn off "Parents\' evening update". Parents can also turn it off for themselves in Notification settings.',
      },
      {
        question: "When do parents get the evening update?",
        answer:
          "Between 5 pm and 10 pm, once the class is over and the teacher has finished rating it (nothing changed for 30 minutes). A class still going at about 9:45 pm is sent what has been rated so far; the rest shows when parents open the day. A parent who has already looked at the finished day isn't sent it again; the other parents still are.",
      },
    ],
    title: "Publishing monthly Hifdh report cards",
    description:
      "See each class's month, check the teachers' comments, and publish the report cards to parents (principal and admin).",
    category: "hifdh",
    audience: OFFICE_AND_TEACHERS,
    icon: "MenuBook",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open a class's report cards",
        description:
          'On the Hifdh page, use the arrows to pick the month (in the first days of a month, a note points the principal to last month\'s cards), then click "Report cards" on a class. Teachers can also open it from the class day ("Report cards" at the top).',
      },
      {
        title: "Check the month",
        description:
          'Each student shows their grade, quality, class days heard, new lines and any juz finished, whether the teacher has written a comment, and whether the card is published. Use "Needs support", "No comment yet" and "Not published" to narrow the list, and "Preview" to see a card as parents will.',
      },
      {
        title: "Teachers: write your comments",
        description:
          'Click "Write the teacher\'s comment" on a student in the list, add a few words for the parents, and click "Save and next" to go straight to the next card still without one. Comments can be written while the month is still going.',
      },
      {
        title: "Principal: publish",
        description:
          'Once the month is over, click "Publish N report cards". If some cards have no teacher\'s comment yet, the confirmation names them and offers "Publish the N with comments". To publish only some, tick them (or "Select all shown") and click "Publish" in the bar at the bottom; a single card can also be published from the student\'s page.',
        tip: "Students without a plan that month have no card and are left out.",
      },
    ],
  },
  {
    slug: "hifdh-certificates",
    lastUpdated: "2026-11-01",
    emptyState:
      'Nothing ready to award? A juz shows here once the Sabaq that finishes it is rated Good or Weak. Juz a student had memorized before their plan began aren\'t counted: the principal can certify one with "Record a test" (a pass awards it).',
    faq: [
      {
        question: "Who can award a certificate?",
        answer:
          "A juz: the student's Hifdh teacher, the principal or an admin. The whole Quran (the khatm): only the principal or an admin, after a passing test.",
      },
      {
        question: "Is a test needed?",
        answer:
          'For a juz, it is optional: award it straight away, or click "Record test" first and a pass awards it. The khatm always needs a passing test. "Needs revision" is kept on the record without awarding; record a retake when the student is ready. Every test stays in the student\'s history.',
      },
      {
        question: "A student memorized juz before joining (or at another school). Can they get certificates?",
        answer:
          'Yes. The principal clicks "Record a test" under Certificates on the student\'s page, picks the juz and records the test: a pass certifies it (dated the day of the test and marked "Certified by test" for staff). When all 30 juz have certificates, the khatm becomes ready, after its own test.',
      },
      {
        question: "A name on a certificate is wrong. How do I fix it?",
        answer:
          'The principal clicks "Withdraw" on the certificate under Certificates on the student\'s page and gives a reason. Parents stop seeing it; staff still see it, struck through, with the reason. Then award it again with the right names. A certificate that came from a test is given again by recording the test again: the test dialog fills in the original examiner, date and result from the withdrawn certificate.',
      },
      {
        question: "How is the khatm certified?",
        answer:
          'By the principal\'s khatm test: click "Record khatm test" on the student\'s page. A pass awards the khatm certificate. Once every juz is finished in the record (or certified by a test), the khatm also shows under "Ready to award".',
      },
      {
        question: "A rating was corrected and the juz isn't finished any more. What happens?",
        answer:
          "A juz not yet awarded drops off the list until it is finished again. A certificate already awarded stays as it was (parents keep it); the student's page notes that the record no longer shows it finished.",
      },
      {
        question: "How do parents find out?",
        answer:
          'Parents see every certificate awarded under Milestones in Hifdh progress, where they can view and print it. Parents with the IlmIQ app also get an alert, between 8 am and 10 pm school time (one awarded later arrives the next morning); they can turn it off in Notification settings ("Hifdh certificates").',
      },
      {
        question: "Can we add our own words to the certificate?",
        answer:
          'Yes. The principal can add a short line (a dua or the school\'s motto, up to 200 characters) under "Certificate line" in Hifdh settings. It is printed on certificates awarded from then on.',
      },
    ],
    title: "Juz and khatm certificates",
    description:
      "Award a certificate when a student finishes a juz or the whole Quran, record tests, and print certificates for one student or a whole class.",
    category: "hifdh",
    audience: OFFICE_AND_TEACHERS,
    icon: "MenuBook",
    estimatedTime: "2 min",
    steps: [
      {
        title: "See who is ready",
        description:
          'A juz is finished when the Sabaq that completes it is rated Good or Weak. On the Hifdh page, each class shows "Ready to award" and how many juz were completed this month; the principal also sees the school\'s total.',
      },
      {
        title: "Award the certificate",
        description:
          'Open the student and go to Certificates. Under "Ready to award", click "Award", check the teacher\'s and principal\'s names to print under the signature lines, and click "Award certificate".',
        tip: "The certificate shows the completion date in both the Gregorian and Hijri calendars.",
      },
      {
        title: "Record a test (optional for a juz, required for the khatm)",
        description:
          'Click "Record test", enter who heard it, the date and the result (Passed, Passed with distinction or Needs revision). A pass awards the certificate at once and is printed on it.',
      },
      {
        title: "Print",
        description:
          'Click "View / Print" on an awarded certificate for one copy, or "Print certificates" on a class on the Hifdh page for every certificate the class awarded that month, one per page. Print from Chrome or Edge; in the print dialog keep Margins on Default. Use your browser\'s "Save as PDF" for a digital copy.',
      },
    ],
  },
  {
    slug: "hifdh-targets",
    lastUpdated: "2026-11-02",
    emptyState:
      'No target yet? On the student\'s Hifdh page, click "Set a target" under Target, or set one for the whole class from its card on the Hifdh page ("Set a class target").',
    faq: [
      {
        question: "How does IlmIQ know if a student is on track?",
        answer:
          "From the record: the lines still to memorize (from the next Sabaq to the end of the target, in the order the plan follows; Juz Amma from An-Nas), the class days left before the date (the class's scheduled days, less school closures and cancelled days), and the recent pace (new lines heard Good or Weak per class day over the last 4 weeks; a Repeat day or a missed day slows it). On track: the recent pace is at least the pace needed. Slightly behind: 80-99% of it. Behind: under 80%.",
      },
      {
        question: "Why does it say \"Too early to tell\"?",
        answer:
          "A new plan (begun within the last 4 weeks) needs 4 Sabaq days heard before there is enough to judge a pace; a student with an older plan who has had few Sabaq days lately is measured. It also shows when the class has no scheduled days to count by, or the record couldn't be read just now.",
      },
      {
        question: "What happens when the date passes?",
        answer:
          "If the target was reached it shows as Met, with the day it was reached. If not, it shows as Missed: the Hifdh page counts it as past its date and the class day shows \"Date passed\" until you set a new date; the missed target stays in the student's history. Parents never see a missed target.",
      },
      {
        question: "Can the principal stop a target being changed?",
        answer:
          'Yes. The principal clicks "Lock" on the student\'s target; only the principal can then change or remove it. A class target set by a teacher leaves a locked target as it is.',
      },
      {
        question: "What do parents see?",
        answer:
          'The current target and a gentle word on how it is going: "On track", or "Needs a little extra revision at home" when behind. The principal can hide targets from parents with "Show targets to parents" in Hifdh settings (the report card preview follows it). A target removed because it was set by mistake no longer shows on report cards already published.',
      },
    ],
    title: "Hifdh targets",
    description:
      "Set a \"finish by\" goal (a juz or the whole Quran by a date) and see whether each student is on track.",
    category: "hifdh",
    audience: OFFICE_AND_TEACHERS,
    icon: "MenuBook",
    estimatedTime: "2 min",
    steps: [
      {
        title: "Set a target",
        description:
          'On the student\'s Hifdh page, click "Set a target" under Target, choose a juz (or the whole Quran) and the date it should be finished by. A new target replaces the current one, which stays in the history.',
      },
      {
        title: "Or set one for the whole class",
        description:
          'On the Hifdh page, click "Set a class target" on the class: every student\'s own current juz, one juz, or the khatm, by a date (paused students included). Before you save, the dialog says how many class targets it replaces, how many students\' own targets it keeps and how many are locked. A student\'s own target is kept unless you tick "Also replace students\' own targets"; a locked target is always kept. Change any student\'s afterwards on their page.',
        tip: "A teacher of a split class sets it for her own students when \"My students\" is chosen, or for the whole class.",
      },
      {
        title: "Keep an eye on it",
        description:
          'The student\'s page shows the pace needed and the recent pace ("Needs about 9 lines a class day · recent pace 11"). The class day marks who is "Slightly behind", "Behind target" or "Date passed", the Hifdh page shows "N of M students behind target" for each class with how many are past their date, and the report card carries one line on the target.',
      },
    ],
  },
  {
    slug: "hifdh-parents",
    lastUpdated: "2026-10-28",
    emptyState:
      'Seeing "No Hifdh progress yet"? Your child shows here once their teacher sets their Hifdh plan. Ask the teacher or the school office if that should have happened already.',
    faq: [
      {
        question: "What do Good, Weak and Repeat mean?",
        answer:
          "Good: recited well, and the next portion follows. Weak: heard, but not yet strong; it moves on and shows in \"Tonight at home\" to revise. Repeat: the same portion comes back next class, so practice it again tonight.",
      },
      {
        question: "Does the teacher know I looked?",
        answer:
          "Yes. When you open a day your child was heard (or the teacher left a note), the teacher sees that a parent has seen it. If the teacher changes that day afterwards, opening it again tells them you have seen the change.",
      },
      {
        question: "How do I stop the evening alert?",
        answer:
          'Open Notification settings from your profile and turn off "Hifdh daily update". The page still shows each day whenever you open it. If your school doesn\'t send the evening update, the switch isn\'t shown.',
      },
      {
        question: "How is the grade worked out?",
        answer:
          "Each part heard counts: Good in full, Weak as half, Repeat as nothing. That gives the quality: 90% or more is Excellent, 80-89% Very good, 70-79% Good, and below 70% Needs support. A month needs at least 6 parts heard for a grade.",
      },
      {
        question: "Why is there no report card for last month yet?",
        answer:
          "Report cards show once the principal publishes them, after the month ends and the teachers have added their comments.",
      },
      {
        question: "Where are my child's juz certificates?",
        answer:
          'When your child finishes a juz (or the whole Quran) and the school awards the certificate, it shows under "Milestones" in Hifdh progress, where you can view and print it. The IlmIQ app also alerts you, between 8 am and 10 pm school time; you can turn that off in Notification settings ("Hifdh certificates").',
      },
      {
        question: "What is the target on my child's Hifdh page?",
        answer:
          'A goal the teacher set, such as "Finish Juz 29 by 15 Mar 2027", with how far along your child is. "Needs a little extra revision at home" means a little more practice of the day\'s lessons will help them reach it on time.',
      },
    ],
    title: "Your child's Hifdh progress",
    description:
      "See how each day went in class, what to practice at home tonight, how far through the Quran your child is, and the monthly report cards.",
    category: "hifdh",
    audience: OFFICE_AND_PARENTS,
    icon: "MenuBook",
    estimatedTime: "2 min",
    steps: [
      {
        title: "Open Hifdh progress",
        description:
          'Open "Hifdh Progress" from the menu or your dashboard. It opens on the latest day your child was heard in the last two weeks. With more than one child doing Hifdh, choose a child at the top; each shows when they were last heard.',
      },
      {
        title: "See how the day went",
        description:
          "Each part heard (Sabaq, the new lesson; Sabqi, recent review; Manzil, revision) shows the portion and the teacher's rating: Good, Weak or Repeat, with the number of mistakes for a Weak or Repeat. A note from the teacher shows below. Use the arrows to see earlier days heard.",
      },
      {
        title: "Practice at home tonight",
        description:
          '"Tonight at home" lists what to do, in order: a part to practice again (it comes back next class), a part to revise (it moved on but wasn\'t strong yet), and the next Sabaq to read through with someone listening.',
        tip: 'When your school sends the evening update, the IlmIQ app alerts you in the evening with how the day went and what to practice at home. You can turn it off in Notification settings ("Hifdh daily update").',
      },
      {
        title: "Follow the whole journey",
        description:
          '"Through the Quran" shows how many of the 30 juz your child has memorized and which juz they are on now. Finishing a juz is celebrated on the day.',
      },
      {
        title: "Read the monthly report card",
        description:
          "Under \"Monthly report cards\", open a month for its grade, how often your child was heard, how much they memorized, when they may finish the Quran at this pace, and the teacher's comment. Use Print for a paper or PDF copy.",
      },
    ],
  },
  {
    slug: "hifdh-overview",
    lastUpdated: "2026-10-30",
    emptyState:
      'No Hifdh classes? Set a class\'s type to "Hifdh (Quran Memorization)" on the class page: only Hifdh classes have a daily sheet and report cards.',
    faq: [
      {
        question: "Can a regular class also track Quran progress?",
        answer:
          "Not with Hifdh Progress: plans, daily ratings and Hifdh report cards are for Hifdh classes only. For occasional Quran work in a regular class, use assignments and lesson progress instead.",
      },
      {
        question: "Where did the old daily progress form and the 100-point score go?",
        answer:
          'Hifdh Progress replaced them. Teachers now rate each part of the day (Sabaq, Sabqi, Manzil) Good, Weak or Repeat, the next lesson follows from the plan, and each month gets a report card with a quality score and grade. Entries made in the old form stay where they were; see "Old Hifdh records".',
      },
    ],
    relatedGuides: ["hifdh-daily-rating", "hifdh-month-report-cards", "hifdh-parents"],
    title: "Getting Started with Hifdh",
    description:
      "Set up Hifdh Progress: mark your Hifdh classes, give each student a plan, then rate each day and publish the monthly report cards.",
    category: "hifdh",
    audience: OFFICE_AND_TEACHERS,
    icon: "MenuBook",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Mark the class as a Hifdh class",
        description:
          'When creating or editing a class, set the Class Type to "Hifdh (Quran Memorization)". Only Hifdh classes appear under Hifdh Progress and have a daily sheet.',
        screenshot: "/help/hifdh-class-type.png",
      },
      {
        title: "Give each student a plan",
        description:
          'Open Hifdh Progress in the sidebar, then "Open today" on the class. A student with no plan yet says "No plan": open the student, then "Set plan" -- how many lines of new Sabaq a day, how many pages of Sabqi, how much Manzil, and where the student stands now. Each day\'s lessons follow from the plan.',
      },
      {
        title: "Rate each day",
        description:
          'Open a class\'s sheet ("Open today"), hear each student, and rate each part Good, Weak or Repeat. See "Rating Hifdh each day". On a phone, rate in the IlmIQ app once it is updated to version 1.2.1 or later (it works with no signal too); older versions of the app save Hifdh where Hifdh Progress doesn\'t read it, so rate on the website until the app is updated.',
      },
      {
        title: "Publish the month's report cards",
        description:
          'After the month ends, the principal reviews each class\'s report cards, teachers add a comment, and the principal publishes them for parents. See "Publishing monthly Hifdh report cards".',
      },
    ],
  },
  {
    slug: "hifdh-reports",
    lastUpdated: "2026-10-30",
    emptyState:
      "Nothing here? The old Hifdh report only shows entries made with the old daily progress form, before Hifdh Progress.",
    faq: [
      {
        question: "Hifdh report card vs. academic report card?",
        answer:
          "Two different things. The Hifdh report card covers Quran memorization: how well each part was recited, how often the student was heard, how much they memorized. The academic report card has subject grades. Many schools issue both.",
      },
    ],
    relatedGuides: ["hifdh-overview", "hifdh-month-report-cards"],
    title: "Old Hifdh records",
    description:
      "Entries made with the old Hifdh daily progress form, from before Hifdh Progress: where they are, and where new work goes.",
    category: "hifdh",
    audience: OFFICE_AND_TEACHERS,
    icon: "Grading",
    estimatedTime: "1 min",
    steps: [
      {
        title: "New work happens in Hifdh Progress",
        description:
          'Daily ratings, plans and monthly report cards are all under Hifdh Progress in the sidebar. See "Getting Started with Hifdh", "Rating Hifdh each day" and "Publishing monthly Hifdh report cards".',
      },
      {
        title: "Old entries stay where they were",
        description:
          "Entries made with the old daily progress form, and the old monthly scores out of 100, are kept as they are and are not carried into Hifdh Progress, which starts from each student's plan. Admins and principals can still read the old monthly scores in the old Hifdh report (go to /reports/hifdh-progress in the address bar); the old Hifdh pages now open Hifdh Progress.",
      },
    ],
  },
];

// =============================================
// REPORTS & ANALYTICS
// =============================================

const reportsGuides: Guide[] = [
  {
    slug: "reports-overview",
    lastUpdated: "2026-10-14",
    relatedGuides: ["billed-vs-collected-report"],
    emptyState:
      "Reports can look thin at a brand-new school. They'll fill in once you have a few weeks of invoices, payments, and attendance data. Try the Enrollment Statistics report first — it reflects your roster from day one.",
    faq: [
      {
        question: "Can I schedule reports to email automatically?",
        answer:
          "No. Reports are run when you open them, and you can export them as CSV.",
      },
      {
        question: "Who can see which reports?",
        answer:
          "The Reports hub is for admins and principals. Outside the hub, a few report pages are shared more widely: staff can pull Family Statements, Class Rosters and Report Cards; teachers get Class Roster and Report Cards for their classes; and parents see Report Cards and Behavior for their own children.",
      },
      {
        question: "Why don't the financial reports match the Invoices page for us?",
        answer:
          "If your school has sponsor funding enabled, financial reports track family money: AR Aging buckets family-funded invoices and shows the sponsor receivable as a single separate figure, and Revenue/Collections exclude sponsor remittances. Voided and written-off invoices are also excluded from outstanding figures. The reports also count by different dates: Revenue by invoice date, Collections by payment date, and Billed vs Collected by the month a charge is for — so their totals for the same month can differ.",
      },
      {
        question: "How does Billed vs Collected split a payment across classes?",
        answer:
          "Payments and write-offs are recorded against a whole invoice. When an invoice has more than one charge (say a class fee and a partner institution's fee), Billed vs Collected shares them out in proportion to what each charge still owes — a $150 payment on a $200 charge and a $100 charge counts $100 and $50. Discounts stay on the charge they were given for. A credit memo for one charge stays on that charge, a removed late fee comes off the late fee, and other discounts and goodwill credits come off school fees first. Withdrawal, billing-correction and dispute credits are shared across every charge. Select any row to see the invoices behind it.",
      },
    ],
    title: "Navigating Reports",
    description:
      "Access specialized reports covering finance, academics, and operations to make data-driven decisions.",
    category: "reports",
    icon: "Assessment",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Reports",
        description:
          'Click "Reports" in the left sidebar. The Reports hub shows all available report cards — click any to open it. (Report Cards has its own sidebar entry.)',
        screenshot: "/help/reports-hub.png",
      },
      {
        title: "Financial Reports",
        description:
          "Revenue Report (invoiced vs. collected by period), Billed vs Collected (per class or fee type: billed, discounts, credits, collected, still owed and written off — switch to Partner to see how much of the fees you collect for a partner institution has come in), AR Aging (overdue invoices by age bucket, as of a chosen date), Collections Report (payments received by method; refunds are shown separately with the net), and Failed Payments (payment failures and reasons).",
        screenshot: "/help/reports-financial.png",
      },
      {
        title: "Academic Reports",
        description:
          'Enrollment Statistics (your roster at a glance), Class Roster (student list per class, with guardian contacts and medical info — tick "Include ended classes" to pull rosters for finished programs), Attendance Report (rates by class and period), and Hifdh Progress (Quran memorization metrics by month).',
        screenshot: "/help/reports-academic.png",
      },
      {
        title: "Operational Reports",
        description:
          "Discount Analysis (discount usage and financial impact over a date range), Autopay Enrollment (setup status across families, including expired cards), and Family Statement (per-family billing summary).",
        screenshot: "/help/reports-operational.png",
        tip: "Most reports export to CSV for further analysis; filters vary by report (date ranges where they make sense, an as-of date for AR Aging, a class for rosters).",
      },
    ],
  },
];

// =============================================
// SETTINGS & ADMIN
// =============================================

const settingsGuides: Guide[] = [
  {
    slug: "manage-users",
    lastUpdated: "2026-10-14",
    relatedGuides: ["signing-in"],
    emptyState:
      "First-time setup? You should already have your admin account. Add Principal, Staff, and Teacher accounts as your team grows. Admins can add any account; principals can add teacher accounts. Parent accounts are auto-created when applications are approved; for manually-added families, use \"Invite to Portal\" on the family page.",
    faq: [
      {
        question: "Can a person have multiple roles?",
        answer:
          "Today, one role per user account. A teacher who's also a parent of a student in your school typically has two accounts (one teacher, one parent) using different emails.",
      },
      {
        question: "What if a user forgets their password?",
        answer:
          'They can reset it themselves: "Forgot password?" on the sign-in page emails them a reset link. Or an admin can open their user detail page (or the row action on the Users list) and click "Reset Password". That emails them a new temporary password, which they must change when they sign in. It also unlocks an account that was locked after too many wrong passwords. Principals can do this for teacher accounts only, from the Users page or People > Teachers. Note: resetting an admin\'s password requires a platform admin.',
      },
      {
        question: "Why can't I create a user with this email?",
        answer:
          "Each email can only belong to one account — you'll get a clear conflict error if the address is already in use. Check the Users list (including deactivated users) for the existing account.",
      },
    ],
    title: "Managing Users and Roles",
    description:
      "Add staff members, assign roles, and control who has access to what in your school's system.",
    category: "settings",
    icon: "People",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Navigate to Users",
        description:
          'Click "Users" in the left sidebar (under Admin; admins and principals see it). You\'ll see all user accounts with their roles and status. Principals can view users. They can also add, edit, deactivate and reactivate teacher accounts and reset teachers\' passwords, so on any other account a principal doesn\'t see "Edit User", "Deactivate" or "Reset Password". Everything else, including other roles and role changes, is for admins. Principals also have the Audit Log in their sidebar (see "Using the Audit Log").',
        screenshot: "/help/users-list.png",
      },
      {
        title: "Add a New User",
        description:
          'Click "Add User" to create a new account. Enter their first name, last name, email, optional phone number, and select their role (defaults to Staff). Principals can only pick Teacher. Note: the email can\'t be changed after creation, and later role changes go through a dedicated, audited action on the user\'s page.',
        screenshot: "/help/user-form.png",
      },
      {
        title: "Understanding Roles",
        description:
          "Each role has different access levels:\n\n- **Admin**: Full access to all features including billing, settings, and user management\n- **Principal**: Manage classes, students and teacher accounts, and view reports\n- **Staff**: Manage students, attendance, and basic operations\n- **Teacher**: Take attendance, view assigned classes, and track Hifdh progress\n- **Parent**: View their children's information, invoices, make payments, and submit applications",
        screenshot: "/help/user-roles.png",
        tip: "Start with the least privileged role needed. You can always upgrade permissions later.",
      },
      {
        title: "User Receives Login Credentials",
        description:
          'When you create a user, they receive an email with their login and a temporary password to sign in with (and then change). If it goes missing, use "Reset Password" on the user detail page to issue a fresh one. Admins can also "Deactivate" a user, or "Activate" them again, from their detail page; principals can do this for teacher accounts. Nobody can deactivate their own account, and deactivating another admin requires a platform admin.',
        screenshot: "/help/user-created.png",
      },
    ],
  },
  {
    slug: "tenant-settings",
    lastUpdated: "2026-10-12",
    emptyState:
      "First time here? Walk through the tabs left-to-right: General → Billing & Late Fees → Discounts → Localization → AutoPay → Stripe. Most defaults are sensible; revisit when you have specific needs. Remember to click Save Settings at the bottom when you're done (the Stripe tab has its own Save Configuration button).",
    relatedGuides: ["understanding-autopay", "late-fees-and-dunning", "setup-notifications"],
    faq: [
      {
        question: "Where do I get my Stripe keys?",
        answer:
          "From the Stripe dashboard at dashboard.stripe.com → Developers → API keys. Use Test keys first to verify, then swap in Live keys when ready to charge real money.",
      },
      {
        question: "Why does each school connect its own Stripe account?",
        answer:
          "So each school's money stays separate. Every school connects its own Stripe account. We never see or store your money — it goes straight from parent to your Stripe, then settles to your bank.",
      },
    ],
    title: "Configuring School Settings",
    description:
      "Configure your school's identity, billing rules, late fees, Stripe integration, and AutoPay across 6 configuration tabs.",
    category: "settings",
    icon: "Settings",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Navigate to Settings",
        description:
          'Click "School Settings" under Admin in the left sidebar. This is the central hub for all school-wide configuration, organized into 6 tabs. Above the tabs sits the Billing Status master switch (Live vs. Setup), which saves immediately. So does the Auto-Enroll Families switch on the AutoPay tab. The Stripe tab has its own Save Configuration button. Everything else applies when you click Save Settings at the bottom.',
        screenshot: "/help/tenant-settings.png",
      },
      {
        title: "General Config",
        description:
          "Update your Organization Name and School Code. The School Code matters more than it looks: it prefixes student numbers (e.g. CODE-0001) and drives your school's login URL (yourcode.ilmiq.com) — changing it changes the address families use, so treat it as permanent once you've launched.",
        screenshot: "/help/settings-general.png",
      },
      {
        title: "Billing & Late Fees",
        description:
          "One tab, two cards. Billing: the Monthly Billing Day (1–28), the invoice due-date rule (days after billing), and the Late Fee Grace Period. Late Fees: enable them, choose Flat Amount or Percentage of Balance, and optionally set Minimum and Maximum Fee bounds. Late fees are applied by a daily job to overdue invoices past the grace period.",
        screenshot: "/help/settings-billing.png",
      },
      {
        title: "Stripe Integration",
        description:
          "Connect your Stripe account to accept online credit card and ACH payments. Enter your Publishable Key, Secret Key (write-only once saved), and optionally the Webhook Signing Secret, then click Save Configuration. This tab saves on its own, not with Save Settings. The page links to the Stripe dashboard and lists the Required Webhook Events to turn on there. Once saved, a Test Mode / Live Mode chip and a Test Connection button help you verify. Each school has its own Stripe account for complete financial isolation.",
        screenshot: "/help/settings-stripe.png",
        tip: "Use Stripe's test mode keys first to verify the integration works before switching to live keys.",
      },
      {
        title: "AutoPay Tab",
        description:
          "The AutoPay tab has two sections. (1) Auto-Enroll Families: when ON, every family is automatically enrolled in AutoPay after their first successful online payment, charging their default payment method thereafter. Auto-enrolled parents can't disable AutoPay themselves — only school staff can. (2) Retry Schedule: the days after the first failed charge to retry, edited as removable day chips. The default [3, 7, 14] retries on day 3, then day 7, then day 14, and pauses after that — the number of days listed is the cap on retry attempts.",        tip: "Auto-Enroll is the right default for most schools — it removes the 'parent forgot to enable AutoPay' failure mode entirely.",
      },
      {
        title: "Other Settings",
        description:
          "The Discounts tab sets how sibling discounts work (across all classes or within the same class only, whether discounts stack, and the amounts for the 2nd and the 3rd+ sibling) and the Mid-Month Enrollment Discount for students who start late in the month. Localization sets the timezone, currency, decimal places, and rounding. The timezone decides which day counts as your Monthly Billing Day. That day's invoices are created early in the morning (around 4–5 AM Eastern).",
        screenshot: "/help/settings-other.png",
      },
    ],
  },
  {
    slug: "setup-notifications",
    lastUpdated: "2026-10-13",
    emptyState:
      'Empty notifications queue? That\'s normal — most notifications are auto-triggered by system events (invoices, applications, behavior). Use "Send Notification" for a one-off message to a specific person; for group announcements, use Messaging (it creates one thread per family). Otherwise wait for activity to populate the history.',
    faq: [
      {
        question: "Why didn't a parent receive a notification?",
        answer:
          'Check History for the delivery status (a failure reason tooltip is shown). FAILED means sending failed; BOUNCED means their mail server rejected it — the family page will also show an "Email bouncing" chip next to that guardian. Important: after a hard bounce or spam complaint, the address is automatically suppressed and future emails to it are silently skipped — fix the address, then contact support to have the block lifted. If the status is SENT but they don\'t see it, have them check spam.',
      },
      {
        question: "Can parents opt out?",
        answer:
          "Partly. On the web, parents open Profile and click \"Notification settings\" (also in the account menu at the top right); in the IlmIQ app it's Profile → Notifications. There they can turn off email from IlmIQ (message emails and Send Notification emails), turn off just message emails (\"New messages\"), and, if they are the guardian the family's app alerts go to (normally the primary guardian), choose which phone alerts the app sends (new invoices, overdue invoices, AutoPay failed, card expiring). Other guardians don't see the alert switches, because those alerts never go to them. If a parent has turned email off, a message you send them from Send Notification isn't sent, and the page tells you \"Not sent: <name> has turned off email notifications\". Billing emails (invoices, payment receipts, statements, overdue reminders and AutoPay notices) are always sent to the family's primary guardian; parents can't opt out of those. Your own settings are in the same place: Profile → Notification settings.",
      },
    ],
    title: "Setting Up Notifications",
    description:
      "Send manual notifications and monitor the delivery queue and history.",
    category: "settings",
    icon: "Notifications",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Navigate to Notifications",
        description:
          'Click "Notifications" in the left sidebar. You\'ll land on the notification queue showing pending and recently sent messages, with a status filter (Pending / Sent / Failed / Bounced) and a refresh button.',
        screenshot: "/help/notifications-center.png",
      },
      {
        title: "Send a Notification",
        description:
          'Click "Send Notification" to send a one-off message to a single user: pick the recipient, choose the notification type, and add an optional custom message. It\'s emailed right away and shows in the queue as Sent or Failed. If nothing could be sent, the page says why: "Not sent: <name> has turned off email notifications" (nothing is added to the queue), or "Not sent: <address> is bouncing" (logged in the queue as Bounced). For messages to many families at once, use Messaging instead — its composer supports family, class, campus, and whole-school audiences.',
        screenshot: "/help/notification-form.png",
      },
      {
        title: "Monitor Queue and History",
        description:
          'The queue shows what\'s waiting to deliver; click "View History" to see everything previously sent with delivery status, timestamps, and date filters. Failure reasons appear as tooltips on failed or bounced rows.',
        screenshot: "/help/notification-history.png",
        tip: "Check History to confirm important notifications were delivered. A failed notification can't be re-sent from the queue — fix the cause (usually a bad address), then send again.",
      },
    ],
  },
  {
    slug: "staff-time-clock",
    lastUpdated: "2026-10-12",
    relatedGuides: ["manage-users", "tenant-settings"],
    emptyState:
      "No time records yet? Records appear as staff clock in and out from the Time Clock page. Every staff and teacher account has it in the sidebar (under Operations for office staff).",
    faq: [
      {
        question: "What does the \"auto closed\" status mean?",
        answer:
          "A shift that was left open (someone forgot to clock out) is closed automatically. The clock-out is set to 12 hours after clock-in or 11:59 PM that day, whichever is earlier, and a note says it was auto-closed. Those are the records admins most often fix — open Staff Timecard, find the record, and Edit the clock-out time.",
      },
      {
        question: "Can staff clock in on a holiday?",
        answer:
          "Yes — the Time Clock shows a warning banner when today is a school holiday, but clocking in still works and the time is recorded. Holiday hours are broken out separately in the summary report.",
      },
    ],
    title: "Staff Time Clock & Timecards",
    description:
      "Staff clock in and out to track work hours; admins review, correct, and summarize time records for payroll.",
    category: "settings",
    audience: OFFICE_AND_TEACHERS,
    icon: "Schedule",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Clock In and Out (staff)",
        description:
          'Open "Time Clock" in the sidebar. The big button toggles between Clock In and Clock Out, with your current status, today\'s total, and clock-in count shown above. Below, "My Time Records" lists your own history with This Week / This Month / Last Month presets.',
        tip: "Teachers can also clock in and out from the mobile app's home screen and see their own hours under My Hours. Reviewing and fixing records stays on the web (Staff Timecard).",
      },
      {
        title: "See Who's Clocked In (admin)",
        description:
          'Admins and principals open "Staff Timecard" under Admin. The "Currently Clocked In" card shows everyone on the clock right now with their running time.',
      },
      {
        title: "Review and Fix Records",
        description:
          "The All Records tab filters by date range, staff member, and status (Clocked In / Clocked Out / Auto Closed). Use Edit on a row to correct clock-in/out times or add notes — most commonly to fix auto-closed shifts — or Delete an erroneous record.",
      },
      {
        title: "Run the Summary Report",
        description:
          'The Summary Report tab totals hours by staff member for a date range — days worked, regular hours, holiday hours, and total — with an Export CSV for payroll.',
      },
    ],
  },
];

// =============================================
// CURRICULUM
// =============================================

const curriculumGuides: Guide[] = [
  {
    slug: "curriculum-overview",
    relatedGuides: ["build-unit-and-lessons", "lesson-resources-and-uploads", "link-subject-to-class"],
    lastUpdated: "2026-10-12",
    emptyState:
      "If your Curriculum hub shows zero subjects, start by adding 2-3 subjects you actually teach (e.g., Quran Memorization, Math, Arabic). Each subject can have units, units can have lessons, lessons can have resources. The hub fills out as you build.",
    faq: [
      {
        question: "Do I have to use units, or can I just have a flat list of lessons under a subject?",
        answer:
          "You need at least one unit per subject — lessons live inside units. If you don't want grouping, create a single unit per subject called \"All Lessons\" and add everything there.",
      },
      {
        question: "Can two classes share the same lessons?",
        answer:
          "Yes. Curriculum is school-wide. A subject (and its lessons) is built once and linked to as many classes as need it. Each class tracks its own lesson progress independently.",
      },
      {
        question: "What happens if I rename a subject after assignments are tied to it?",
        answer:
          "Nothing breaks. Existing assignments, grades, and report cards keep their reference to the subject by ID, so the new name is reflected everywhere.",
      },
    ],
    title: "Curriculum at a Glance",
    description:
      "Understand how subjects, units, and lessons fit together to form your school's course catalog.",
    category: "curriculum",
    audience: OFFICE_AND_TEACHERS,
    icon: "AutoStories",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open the Curriculum Hub",
        description:
          'Click "Curriculum" in the left sidebar. The hub shows live counts for Subjects, Grade Levels, Curriculum Units, Lessons, and Lesson Plan Templates across your school. Click any card to drill in.',
        screenshot: "/help/curriculum-index.png",
        tip: "Admins and principals build and edit the curriculum. Staff and teachers can browse every subject, unit, lesson and resource, but don't see the add, edit or archive buttons. Where a list is still empty, it says \"Your principal or administrator adds them.\"",
      },
      {
        title: "Subjects",
        description:
          "A subject is a course offered school-wide (Quran Memorization, Tajweed, Mathematics, etc.). Subjects are reusable across classes and grade levels - one Quran subject can be linked to Grade 3, Grade 4, and the Hifdh class. The faith tradition tag is inherited automatically from your school settings.",
        screenshot: "/help/curriculum-subjects.png",
        tip: "Nothing in the curriculum is hard-deleted — subjects, units, and lessons are archived and restorable. Use the \"Show archived\" switch to see (and restore) archived items.",
      },
      {
        title: "Units and Lessons",
        description:
          "Each subject is broken into units (Juz Amma, Algebraic Expressions, ...), and each unit contains lessons (Surah Al-Fatiha - Foundation, Solving Linear Equations, ...). Lessons can carry learning objectives and resources.",
        screenshot: "/help/curriculum-units-lessons.png",
      },
      {
        title: "School-Wide Browsers",
        description:
          'From the hub, the "Curriculum Units" and "Curriculum Lessons" pages show every unit/lesson across every subject in one paginated table. Search by name or filter by subject (and, for lessons, by unit) to find content quickly when planning a new class.',
        screenshot: "/help/curriculum-all-lessons.png",
      },
    ],
  },
  {
    slug: "build-unit-and-lessons",
    relatedGuides: ["curriculum-overview", "lesson-resources-and-uploads", "lesson-progress"],
    lastUpdated: "2026-10-12",
    emptyState:
      "If you don't see any subjects yet, create one first from Curriculum > Subjects. Units live inside subjects, so a subject is required before adding units.",
    faq: [
      {
        question: "What's a unit vs. a lesson?",
        answer:
          "A unit is a group of related lessons (e.g., \"Juz Amma\" is a unit of 37 lessons, one per surah). Lessons are the actual things teachers deliver day-to-day. Both are reusable across classes.",
      },
      {
        question: "Can I reorder units after they're created?",
        answer:
          "Yes — open the unit's row menu (⋮), choose Edit, and change its Sequence number. Units sort by sequence, lowest first. The same applies to lessons within a unit.",
      },
    ],
    title: "Building a Unit with Lessons",
    description:
      "Add a unit to a subject and fill it with lessons that teachers can deliver and parents can browse.",
    category: "curriculum",
    audience: OFFICE_AND_TEACHERS,
    icon: "Folder",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Open a Subject",
        description:
          "From Curriculum > Subjects, click the subject you want to add content to. The subject detail page lists every unit attached to it. Only admins and principals see \"Add Unit\", \"Add Lesson\" and the row menus; staff and teachers can open and read everything.",
        screenshot: "/help/subject-detail.png",
      },
      {
        title: "Add a Unit",
        description:
          'Click "Add Unit" and enter the unit name (e.g., "Juz Amma", "Fractions"), an optional description, an optional duration in weeks, and a sequence number (auto-appended if you leave it blank). Sequence controls the order the unit appears in.',
        screenshot: "/help/unit-form.png",
      },
      {
        title: "Add Lessons",
        description:
          'Click into the new unit, then click "Add Lesson" for each lesson you want. Each lesson takes a name, optional description, optional duration in minutes, and a sequence number. Add learning objectives — one per line — so teachers know exactly what success looks like; they appear on lesson plans and in the unit\'s lessons table.',
        screenshot: "/help/lesson-form.png",
      },
      {
        title: "Verify the Hierarchy",
        description:
          "Back at the unit detail page, your lessons should appear in sequence order, with an Objectives column and a paperclip Resources action per row. The Curriculum hub counts will update automatically. Lessons are now ready for resources, assignments, and progress tracking.",
        screenshot: "/help/unit-with-lessons.png",
      },
    ],
  },
  {
    slug: "lesson-resources-and-uploads",
    relatedGuides: ["build-unit-and-lessons", "curriculum-overview", "link-subject-to-class"],
    lastUpdated: "2026-10-12",
    emptyState:
      "If the Resources dialog is empty, that's normal — every lesson starts with no resources. Click Add Resource and pick the type that matches what you want to attach (link, video, PDF upload, etc.).",
    troubleshooting: [
      {
        problem: "The file upload progress bar stops at 100% but the file doesn't save.",
        solution:
          "100% means the upload to storage finished; the resource is only created after a final confirm step. If that step failed, close the dialog and try again. If it keeps happening, check that your file is under 100 MB and one of the allowed types.",
      },
      {
        problem: "I get \"File not allowed\" when uploading.",
        solution:
          "Accepted types: PDFs, Office docs, images (PNG/JPG/HEIC/GIF/WebP), audio (MP3/M4A/WAV/OGG), video (MP4/WebM/MOV/M4V), plain text, and Markdown. Convert anything else first — for example, .pages files need to be exported to PDF from the Mac Pages app.",
      },
      {
        problem: "Parents say the file download link isn't working.",
        solution:
          "Download links expire after 1 hour for security. Each click in the parent portal generates a fresh link, so they should always work from the portal — only stale links shared offline (in chat or email) fail. Tell parents to click the resource from the portal each time, not save the URL.",
      },
    ],
    faq: [
      {
        question: "Is the file public? Can anyone with the link see it?",
        answer:
          "No. Files are private to your school. Every download uses a link that works for 1 hour, created only for people allowed to view that lesson. There's no public link.",
      },
      {
        question: "Where are the files actually stored?",
        answer:
          "In private cloud storage that is walled off for your school. Files from one school can never be opened from another school's account, even with a guessed link.",
      },
      {
        question: "What's the size limit?",
        answer:
          "100 MB per file — short videos (MP4/WebM/MOV) upload fine within that. For longer videos, host on YouTube/Vimeo and use the Video resource type instead.",
      },
    ],
    title: "Attaching Resources and File Uploads",
    description:
      "Attach links, videos, notes, and uploaded files (PDFs, recitation audio, handouts) directly to a lesson.",
    category: "curriculum",
    audience: OFFICE_AND_TEACHERS,
    icon: "CloudUpload",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Open the Lesson",
        description:
          'From a unit detail page, click the paperclip / "Resources" icon on the lesson row. The Lesson Resources dialog opens, listing every resource already attached. Only admins and principals see "Add Resource" and can change resources; staff and teachers can open and download them.',
        screenshot: "/help/lesson-resources-dialog.png",
      },
      {
        title: "Pick a Resource Type",
        description:
          "Click Add Resource and choose the type:\n\n- **Upload File (PDF, image, audio)** - upload directly into the system\n- **Document URL (Google Drive, Dropbox)** - pointer to a file you host elsewhere\n- **Link** - external URL (article, website)\n- **Video** - YouTube / Vimeo link\n- **Note (text only)** - inline text note for the teacher\n\nThe type is permanent once created — to change a Link into an uploaded File, add a new resource and archive the old one (the bin icon).",
        screenshot: "/help/resource-type-picker.png",
      },
      {
        title: "Upload a File",
        description:
          'For "Upload File", a file picker appears. Drag and drop a file or click to browse. Allowed types: PDF, Office docs, images (PNG/JPG/HEIC/GIF/WebP), audio (MP3/M4A/WAV/OGG) for Quran recitations, video (MP4/WebM/MOV), plain text, and Markdown. Maximum size is 100 MB. A progress bar shows the upload; when it finishes, the system confirms the file and the resource appears in the list.',
        screenshot: "/help/resource-file-upload.png",
        tip: "Files are stored privately for your school - parents and students can only download them through short-lived links the system creates each time they click. Nothing is public.",
      },
      {
        title: "View and Download",
        description:
          "Once attached, the file shows up in the resources list. Clicking a file resource opens an in-app preview with its own Download button; the Download action creates a fresh link each time. Links expire after one hour, so links shared offline (in a chat or email) won't leak access.",
        screenshot: "/help/resource-download.png",
      },
    ],
  },
  {
    slug: "link-subject-to-class",
    relatedGuides: ["curriculum-overview", "lesson-progress", "assignments-and-gradebook"],
    lastUpdated: "2026-10-12",
    emptyState:
      "If your Subjects card is empty on the class detail page, you haven't linked any subjects yet. Click Add Subject and pick the ones this class teaches — typically 2-6 per class.",
    faq: [
      {
        question: "Do I have to set a grading scale per subject?",
        answer:
          "Each class-subject link has its own scale: Points (0–100) — the default — or Letter (A–F). Different subjects in the same class can use different scales. Once the first grade is entered, the scale LOCKS: the row shows a lock icon and only admins can change it after that.",
      },
      {
        question: "What happens if I unlink a subject after grades exist?",
        answer:
          "Only the link is removed. That subject's lessons stop showing on the class's Lesson Progress page, but its assignments and grades stay in the gradebook and on report cards. To take that work off them, archive the subject's assignments. If you link the subject again later, its old lesson progress comes back, but the link's own settings (grading scale, notes) start fresh.",
      },
    ],
    title: "Linking Subjects to a Class",
    description:
      "Connect curriculum subjects to a class so teachers can mark lesson progress and assign work.",
    category: "curriculum",
    icon: "Link",
    estimatedTime: "2 min",
    steps: [
      {
        title: "Open the Class Detail Page",
        description:
          'Navigate to Classes and pick the class you want to wire up. Scroll to the "Subjects" card on the class detail page.',
        screenshot: "/help/class-subjects-card.png",
      },
      {
        title: "Add Subjects",
        description:
          'Click "Add Subject", pick a subject from the dropdown (already-linked subjects are hidden), and choose its grading scale. Repeat for each subject the class teaches. If the dropdown is empty, the subjects need to be created in the curriculum catalog first. Admins and principals get a link there; staff are asked to have their principal or administrator add them.',
        screenshot: "/help/class-add-subject.png",
        tip: "Admins, principals and office staff can link or remove subjects; teachers can't. A class typically links 2-6 subjects. A Hifdh class might link only Quran Memorization and Tajweed; a Grade 5 class might link Math, Science, Quran, Arabic, and Islamic Studies.",
      },
      {
        title: "What This Unlocks",
        description:
          "Linked subjects power three things: lesson progress tracking (teachers mark which lessons they've delivered), assignments (each assignment can be tagged with a subject), and report cards (per-subject grade rows).",
        screenshot: "/help/class-subjects-linked.png",
      },
    ],
  },
];

// =============================================
// ACADEMICS
// =============================================

const academicsGuides: Guide[] = [
  {
    slug: "lesson-progress",
    relatedGuides: ["link-subject-to-class", "assignments-and-gradebook", "report-cards"],
    lastUpdated: "2026-10-12",
    emptyState:
      "Empty progress page? You need to link a subject to this class first (Class > Subjects card). Once a subject with lessons is linked, every lesson appears as a row to mark.",
    faq: [
      {
        question: "Does the parent see all my notes?",
        answer:
          "No. Teacher notes are staff-only — parents see lesson names, statuses, delivered dates, and attached resources, never your notes. Keep notes professional anyway; other staff can read them.",
      },
      {
        question: "What's the difference between Skipped and Not Started?",
        answer:
          "Not started = haven't taught it yet, will get to it. Skipped = intentionally not teaching it this term. Use Skipped + a note when you decide to drop a lesson, so the gap is documented.",
      },
    ],
    title: "Tracking Lesson Progress",
    description:
      "Mark which lessons your class has covered, paused, or skipped, with optional teacher notes.",
    category: "academics",
    audience: OFFICE_AND_TEACHERS,
    icon: "Checklist",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open Lesson Progress",
        description:
          'Click "Lesson Progress" in the left sidebar and pick a class, or click the "Lesson Progress" button in the Academics card on a class detail page. You\'ll see every lesson from the subjects linked to the class, grouped by unit. (Teachers can open this only for classes they\'re assigned to.)',
        screenshot: "/help/lesson-progress-page.png",
      },
      {
        title: "Mark a Lesson",
        description:
          'Three ways to mark: click the status chip for a quick pick (Not started, In progress, Completed, Skipped), hit the green check to "Mark complete (today)" in one click, or open the pencil editor to set the status, the "Delivered on" date, and a note. The status is per-class, so the same lesson can be Completed in Grade 5 - Boys and In Progress in Grade 6 - Girls.',
        screenshot: "/help/lesson-progress-mark.png",
        tip: "Add a short note when you skip a lesson - it explains the gap to whoever reads the report later (\"Skipped - guest speaker covered the same surah\").",
      },
      {
        title: "What Parents See",
        description:
          "Parents see this on the parent portal's Lesson Progress page: they pick the student and class, then get an overall class progress bar and the lessons grouped by unit with status and delivered date — plus a button to view any resources you attached.",
        screenshot: "/help/lesson-progress-parent-view.png",
      },
    ],
  },
  {
    slug: "assignments-and-gradebook",
    relatedGuides: ["lesson-progress", "assessments-quizzes", "report-cards"],
    lastUpdated: "2026-10-13",
    emptyState:
      "Empty gradebook? You need three things first: (1) link a subject to the class, (2) create at least one assignment, (3) have at least one student enrolled. The grid will populate automatically once those are in place.",
    troubleshooting: [
      {
        problem: "The grade I entered isn't saving.",
        solution:
          "Scores save when you press Enter or Tab, or when you click away from the cell; only Esc throws away what you typed. If you see an error instead, check the number: a score more than double the assignment's points (for example 500 on a 100-point assignment) is rejected as a likely typo. Extra credit up to double the points is allowed.",
      },
      {
        problem: "A student is missing from the gradebook.",
        solution:
          "Only currently-enrolled students show up. Check that the student's enrollment in this class is Active (not Paused or Withdrawn). Recently-enrolled students appear immediately.",
      },
      {
        problem: "I marked a row Excused but the average still includes it.",
        solution:
          "Excused (and Missing) rows are excluded from the average — only rows with a graded score count. Confirm you set the status on the right cell, and note that typing a score sets the status to Graded, which overrides an earlier Excused.",
      },
    ],
    faq: [
      {
        question: "Can I change a grade after the report card is frozen?",
        answer:
          "You can edit the grade in the gradebook, but the frozen report card keeps the original. To reflect the new grade on a card, re-generate the card (which replaces it — see the report cards guide).",
      },
      {
        question: "Which term does an assignment count toward?",
        answer:
          "Leave Term on Automatic and the assignment counts toward the term its due date falls in (or the day it was created, if it has no due date). Pick a term by name to file it there whatever the due date. If no term covers the due date, the form warns you: the grade won't reach any report card until you pick a term or change the date. The term shows under the due date in the assignment list.",
      },
      {
        question: "How are weighted grades calculated?",
        answer:
          "Each assignment has a weight (default 1). The class average is the weighted average across assignments where the student has a GRADED score — ungraded, Missing, Submitted, and Excused rows don't count (a Missing assignment does NOT count as zero). Set an assignment's weight to 0 to exclude it from averages entirely.",
      },
      {
        question: "Who can see a student's grades?",
        answer:
          "Admins, principals, staff, and the assigned class teachers. Parents see their own child's grades through the parent portal once an assignment is published.",
      },
    ],
    title: "Assignments and Gradebook",
    description:
      "Create assignments (homework, quizzes, projects, exams), enter grades in a spreadsheet-style grid, and track late or missing work.",
    category: "academics",
    audience: OFFICE_AND_TEACHERS,
    icon: "Grading",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Open Assignments",
        description:
          'From a class detail page, click "Assignments" in the Academics card. You\'ll see every assignment for the class with its kind, subject, due date, and points.',
        screenshot: "/help/class-assignments-list.png",
      },
      {
        title: "Create an Assignment",
        description:
          'Click Add Assignment. Pick the subject (only subjects linked to this class are listed), then fill in the name, kind (Homework, Classwork, Quiz, Project, Test, Exam, Participation, or Other), due date, points, and weight (default 1 — this is what drives averages). Term decides which report card the grade lands on: Automatic (the default) uses the term the due date falls in, and the menu shows which term that is; pick a term by name to override it. Toggle "Published (visible to students/parents)" when it\'s ready; leave it as a draft while you\'re still building it.',
        screenshot: "/help/assignment-form.png",
        tip: "Kind is a label for organizing; the weight field is what affects the average. Give a big exam weight 2-3, and weight 0 to practice work that shouldn't count.",
      },
      {
        title: "Open the Gradebook",
        description:
          'Click "Gradebook" (from the class detail Academics card, or the button on the Assignments page). You get a students-by-assignments grid — one row per actively-enrolled student, one column per assignment, with a "graded / total" progress chip and a "Next ungraded" jump button.',
        screenshot: "/help/gradebook-grid.png",
      },
      {
        title: "Bulk Entry with Keyboard",
        description:
          "Click any cell, type the score, and press Enter to save and move down to the next student; Tab moves right to the next assignment. Each cell's editor also has a status dropdown — Submitted, Late, Graded, Missing, Excused. Entering a score sets the status to Graded; Excused and Missing rows are left out of the average so the student isn't penalized.",
        screenshot: "/help/gradebook-keyboard.png",
        tip: "Scores save when you press Enter or Tab or click away. Press Esc to cancel a score you haven't saved yet. Extra credit is fine up to double the assignment's points; anything higher is treated as a typo and not saved.",
      },
    ],
  },
  {
    slug: "assessments-quizzes",
    relatedGuides: ["assignments-and-gradebook", "report-cards", "lesson-progress"],
    lastUpdated: "2026-10-13",
    emptyState:
      'First assessment? Click "New Assessment", pick the subject and set a Pass %, then click Questions on the new row and add 5-10 questions.',
    faq: [
      {
        question: "Do assessments count toward the class average?",
        answer:
          "Not by themselves. An assessment is the question set; the scores live in the gradebook. Add an assignment (kind Quiz) for it and enter each student's score there, and it counts toward the class average and report card like any other assignment.",
      },
      {
        question: "Can students take the quiz in the app?",
        answer:
          "No. Students and parents don't take assessments online. Give the quiz in class (print the questions or read them out), mark it, and record each student's score as an assignment grade.",
      },
      {
        question: "Can I change an assessment after creating it?",
        answer:
          "Yes. Click Settings on its row (or in the questions dialog) to change the title, description, time limit, and Pass %. The subject is fixed once it's created. Publish and Unpublish switch it between Published and Draft at any time.",
      },
    ],
    title: "Building an Assessment (Quiz)",
    description:
      "Build a multiple-choice question set for a class, give it in class, and record the scores in the gradebook.",
    category: "academics",
    audience: OFFICE_AND_TEACHERS,
    icon: "Quiz",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Open Assessments",
        description:
          'From a class detail page, click "Assessments" in the Academics card. Assessments are multiple-choice quizzes you build question by question. Students don\'t take them online: you give the quiz in class and record the scores as an assignment grade.',
        screenshot: "/help/assessments-list.png",
      },
      {
        title: "Create the Assessment",
        description:
          'Click "New Assessment". Set the subject (required), title, optional description, optional time limit (in minutes), and Pass % (e.g. 70). Switch on "Published" when the quiz is finished and ready to give; leave it off to keep it as a draft. You can change the settings later with Settings on the row, and Publish or Unpublish at any time.',
        screenshot: "/help/assessment-form.png",
      },
      {
        title: "Add MCQ Questions",
        description:
          "Click Questions on the assessment's row, then \"Add Question\". Enter the prompt and the answer options (start with two, add as many as you need), and switch on Correct for the right option, or several if more than one answer is right. Set the points per question (defaults to 1) and optionally an explanation for the answer key. The questions dialog also has Settings and Publish / Unpublish buttons.",
        screenshot: "/help/assessment-questions.png",
        tip: "Keep individual quizzes short (5-10 questions).",
      },
      {
        title: "Give the Quiz and Record Scores",
        description:
          'Give the quiz in class and mark it. Then open the class\'s Assignments page (the Assignments button on the Assessments page takes you there), add an assignment of kind Quiz, and enter each student\'s score in the gradebook. That grade counts toward the class average and the report card.',
      },
    ],
  },
  {
    slug: "behavior-incidents",
    relatedGuides: ["report-cards", "messaging-overview"],
    lastUpdated: "2026-10-13",
    emptyState:
      "If your behavior log is empty, that's OK — log a few positive incidents to start. Best practice: not just negatives. Logging praise, kindness, and effort gives a balanced picture for parent conferences.",
    faq: [
      {
        question: "Does the parent get notified automatically?",
        answer:
          "For serious incidents, yes: a NEGATIVE incident with severity 2 or higher automatically emails the student's guardians the moment it's saved — there is no opt-out toggle, so write the description knowing the parent will read it. Positive, neutral, and severity-1 incidents don't email automatically; use the envelope action on a row to record that you notified the parent yourself. Teachers see the envelope only on incidents logged in one of their own classes.",
      },
      {
        question: "Can I edit or delete an incident after submitting?",
        answer:
          "Admins and principals can archive an incident from its row menu (a soft-delete — it disappears from the log but is preserved). There's no in-app edit today, so proofread before saving; if the wording must change, ask an admin or principal to archive it, then log it again. Changes are captured in a database audit trail.",
      },
    ],
    title: "Logging Behavior Incidents",
    description:
      "Record positive, neutral, and negative behavior with severity, automatic parent notification for serious incidents, and a full audit trail.",
    category: "academics",
    audience: OFFICE_AND_TEACHERS,
    icon: "Mood",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open the Behavior Page",
        description:
          'Click "Behavior" in the left sidebar (under Academics). The Behavior Log shows incidents with student, type, severity, and date. Admins, principals, and staff see the whole school; teachers see incidents for students in their own classes; parents get a read-only view of their own children. Use "Filter by class" to see one class; the Behavior button on a class page opens the log already filtered to that class.',
        screenshot: "/help/behavior-incidents-list.png",
      },
      {
        title: "Log an Incident",
        description:
          'Click "Log Incident". Pick the Class first (it starts on the class the log is filtered to): teachers pick one of their own classes; admins, principals, and staff can leave it blank for something that happened outside a class. Then pick the student from that class\'s roster, which includes paused and completed students as well as active ones. Choose the type (Positive, Negative, Neutral — defaults to Positive), severity 1-5, and write a short description; category and actions taken are optional. The date defaults to today. Logging the exact same incident twice is de-duplicated automatically.',
        screenshot: "/help/behavior-incident-form.png",
      },
      {
        title: "Parent Notification",
        description:
          "Negative incidents with severity 2+ email the guardians automatically on save and are stamped as parent-notified — so write descriptions that are factual and parent-ready. For anything else, contact the parent yourself (Messaging works well) and use the row's envelope action to record that they were notified. Teachers can do this only on incidents logged in one of their own classes.",
        screenshot: "/help/behavior-notify-parent.png",
        tip: "Best practice: log positive incidents too. A behavior log full of negatives skews the picture. Logging positives also gives you nice data for end-of-term parent conferences.",
      },
      {
        title: "Audit Trail",
        description:
          "Every create, update, and archive is captured in a database audit table with the actor and timestamp, so the history of an incident is preserved even after archiving.",
        screenshot: "/help/behavior-audit.png",
      },
    ],
  },
  {
    slug: "report-cards",
    relatedGuides: ["assignments-and-gradebook", "lesson-progress", "behavior-incidents"],
    lastUpdated: "2026-10-13",
    emptyState:
      'Brand-new term? Report cards only exist after an admin or principal generates them. Click "Generate" at the top of the page and pick the class, student, and term to snapshot their grades.',
    troubleshooting: [
      {
        problem: "I generated a card but it has no grades.",
        solution:
          "The class had no graded assignments in that term. Open the class's Assignments page and check each assignment's Term (shown under its due date): an assignment counts toward the term its due date falls in unless a different term was picked. Fix the Term or enter the missing grades, Revoke the blank card (the warning on the card has a Revoke button), then generate again. A subject with no graded work in the term shows '—'.",
      },
      {
        problem: "I made grade edits after generating — why doesn't the card reflect them?",
        solution:
          "Frozen cards are immutable on purpose (so a parent who downloaded the PDF still sees what was sent). Re-generate the card to capture the latest grades — note this REPLACES the card's content, so the superseded snapshot isn't kept.",
      },
      {
        problem: "The Print preview cuts off the bottom of the page.",
        solution:
          "Use \"Save as PDF\" with Letter or A4 paper size and Default margins. The card layout is designed to fit one page per student in most cases; many subjects with long assignment lists may push to a second page.",
      },
    ],
    faq: [
      {
        question: "How is the overall letter grade calculated?",
        answer:
          "The overall percent is the weighted average across the student's graded assignments in the class for the chosen term (each assignment counts toward the term its due date falls in, unless a different term was picked for it), mapped to a letter using the standard A/B/C/D/F bands (90/80/70/60). These bands are fixed and can't be changed.",
      },
      {
        question: "Can I un-freeze a card to fix a typo?",
        answer:
          "No — by design. Fix the underlying grade and generate the card again; the new version replaces the old and is what parents see. If a card should be withdrawn entirely, use Revoke on its row. It is then marked Revoked and hidden from the default list (regenerating later un-revokes it).",
      },
    ],
    title: "Generating Frozen Report Cards",
    description:
      "Snapshot a student's grades into a permanent, printable report card per term.",
    category: "academics",
    audience: OFFICE_AND_TEACHERS,
    icon: "Article",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Open Report Cards",
        description:
          'Click "Report Cards" in the sidebar, or the Report Cards button on a class detail page, which opens the list filtered to that class. The page lists every report card you can see. Staff get a dropdown to include revoked ones and "Filter by class" to pick a different class or clear the filter. Only admins and principals see "Generate" and "Revoke". Staff and teachers see the list without them, and teachers see the cards for classes they teach; parents see their own children\'s cards, read-only, and only cards that haven\'t been revoked (they have no revoked filter).',
        screenshot: "/help/report-cards-list.png",
      },
      {
        title: "Generate a Card",
        description:
          'Click "Generate" at the top. Pick the Class (already filled in when the list is filtered to a class), then the Student from that class\'s active roster; the Term starts on the current term. The dialog shows how many of the class\'s assignments fall in that term and how many have grades. If none have grades, it warns that the card would be blank and asks you to tick a box before it will generate. The card snapshots the student\'s grades for the class\'s assignments in that term (each assignment counts toward the term its due date falls in, unless a different term was picked for it): per-subject averages with their assignments, plus the overall percent and letter. Once frozen, the card is immutable: even if grades change later, it reflects what was true at freeze time.',
        screenshot: "/help/report-card-generate.png",
        tip: "Parents can see a card as soon as it is generated, so make sure the term's grades are entered first. Cards are generated one student at a time. Re-generating the same student/class/term replaces the existing card.",
      },
      {
        title: "Print or Save as PDF",
        description:
          'Click the eye icon ("View snapshot") on a card\'s row to open the print-friendly view, then use "Print / Save as PDF" to send it home. The card shows the student, class and term, a per-subject grade table with the term\'s assignments, and the overall percent and letter grade.',
        screenshot: "/help/report-card-print.png",
      },
      {
        title: "Revoke When Needed",
        description:
          "If a card went out wrong, use Revoke on its row — it's marked Revoked, drops out of the default list, and parents can no longer see, open or print it. Fix the grades and generate again to issue the corrected card. Summary-level changes (overall grade, revocations) are tracked in a database audit table.",
        screenshot: "/help/report-card-audit.png",
      },
    ],
  },
];

// =============================================
// MESSAGING
// =============================================

const messagingGuides: Guide[] = [
  {
    slug: "teacher-portal-overview",
    title: "Teacher Portal: Your Daily Workflow",
    description:
      "Take attendance, mark lessons, grade assignments, and message parents — everything teachers do day-to-day, from one dashboard.",
    category: "messaging",
    audience: OFFICE_AND_TEACHERS,
    icon: "Badge",
    estimatedTime: "4 min",
    lastUpdated: "2026-10-13",
    relatedGuides: ["assignments-and-gradebook", "lesson-progress", "messaging-overview", "behavior-incidents", "staff-time-clock"],
    emptyState:
      "If your dashboard is empty, you haven't been assigned to any classes yet. Ask the school admin to add you as a teacher to your classes.",
    steps: [
      {
        title: "Open the teacher dashboard",
        description:
          "After login, you land on the Teacher Dashboard. Across the top are four summary cards — My Classes, My Students, Today's Classes, and Take Attendance. Below them, the Today's Classes card lists the classes that meet today (based on each class's schedule days), followed by a My Classes card and a Quick Actions row (My Classes, My Students, Take Attendance, Hifdh Progress).",
        screenshot: "/help/teacher-dashboard.png",
        tip: "On your phone? In the IlmIQ app, the Home screen lists Today's Sessions — tap one to take attendance. The Classes tab lists your classes, each with its roster, Attendance, and Hifdh Progress.",
      },
      {
        title: "Browse your classes",
        description:
          'Click "My Classes" in the sidebar to see every class you teach. Each class card has Roster, Attendance and View (which opens the full class page — gradebook, assignments, and lesson progress live there), and a Hifdh class also has Hifdh, which opens its daily sheet.',
        screenshot: "/help/teacher-my-classes.png",
        tip: "If you teach multiple sections of the same subject, the My Classes page is the fastest way to switch between them.",
      },
      {
        title: "View your students",
        description:
          'Click "My Students" to see your students grouped by class — one card per class, each with a table showing the student, their status, and their enrollment date. Search by name, or pick a class to narrow the list. Use the action button on a row to open the full student profile.',
        screenshot: "/help/teacher-my-students.png",
      },
      {
        title: "Take a class through the day",
        description:
          "Open a class (View) and use its Quick Actions to jump to each task: Attendance, Lesson Progress, Assignments, Gradebook, Assessments, Placement, Hifdh, Behavior, and Messages. Placement lets you record assessment scores and move students into the right class (see Class Rosters and Student Placement). Messages opens your inbox filtered to that class, and \"New message\" there starts with that class already picked.",
      },
      {
        title: "Clock in and out",
        description:
          'If your school tracks hours, click "Time Clock" in the sidebar and click "Clock In" when you arrive and "Clock Out" when you leave. In the IlmIQ app, the Clock In / Clock Out button is right on your Home screen, and "View my hours" shows your recent shifts.',
        tip: "Forgot to clock out? Ask the school office — an admin can correct your time record.",
      },
    ],
    faq: [
      {
        question: "Can I see other teachers' classes?",
        answer:
          "No. The Teacher Portal only shows classes you're assigned to as a teacher. Admins and principals can see everyone's classes from the regular Classes page.",
      },
      {
        question: "How do I see a student's parent contact?",
        answer:
          "From My Students, use the action button on the student's row (or open them from any class roster) to open the student profile, which shows the guardian's contact details.",
      },
      {
        question: "How do I stop (or start) message emails?",
        answer:
          "Open Profile and click \"Notification settings\" (also in the account menu at the top right). Switch off \"New messages\" to stop emails about new messages, or \"Email from IlmIQ\" to also stop notices the school sends you from IlmIQ. Each switch saves straight away.",
      },
    ],
  },
  {
    slug: "parent-portal-mobile",
    title: "Parent Portal on Your Phone",
    description:
      "How to use the parent portal from a phone — in the IlmIQ app or your phone's browser — viewing your child's progress, paying invoices, and managing payment methods.",
    category: "messaging",
    audience: OFFICE_AND_PARENTS,
    icon: "PhoneAndroid",
    estimatedTime: "3 min",
    lastUpdated: "2026-10-14",
    emptyState:
      "If the parent portal looks empty, your child hasn't been enrolled yet. Returning families can add a program for a child right from the portal (My Students → Apply for a Program, or Applications → Apply for a Program); otherwise reach out to the school office.",
    steps: [
      {
        title: "Open the portal on your phone",
        description:
          "You can use the IlmIQ app or your phone's browser. In the app, search for your school, then sign in with the email the school has on file. Its Home screen shows your students, your balance, and quick actions (Apply for Program, Make Payment, Hifdh Progress, Attendance). In a browser (Safari on iPhone, Chrome on Android), go to your school's portal URL and sign in. The dashboard shows your students, your outstanding balance, anything overdue, and a Pay Now shortcut, plus quick actions for applying for a program, attendance, Hifdh progress, invoices, payment history, and statements.",
        screenshot: "/help/mobile-parent-home.png",
        tip: "Messages, Lesson Progress, and Assignments & Grades are only on the web portal. Open the portal in your phone's browser for those.",
      },
      {
        title: "View your child's progress",
        description:
          "Open Lesson Progress from the menu, then pick the student and the class. You'll see an overall progress bar for the class and the lessons grouped by unit, in course order, with each lesson's status and delivery date.",
        screenshot: "/help/mobile-lesson-progress.png",
        tip: "Lessons with a paperclip icon have resources attached — tap it to view or download what the teacher shared.",
      },
      {
        title: "Add a program for your child",
        description:
          "Open My Students and tap Apply for a Program under your child's name (it's also the first quick action on your dashboard). Every program that's open for enrollment is listed, grouped by campus, and only the sections your child is eligible for are shown. Pick the program, review, and pay — enrollment is immediate for programs that collect payment up front. If the program is full, you can join its waitlist with no payment now; the school sends you a payment link if a place opens up. If it fills up just as you pay, your payment is refunded in full to the same card or bank (it can take 5–10 business days to appear) and your child is added to the waitlist.",
        screenshot: "/help/mobile-apply-program.png",
        tip: "Have more than one child? Each child's card has its own Apply for a Program button, so the right child is already selected. In the IlmIQ app, tap Apply for Program on the Home screen (or Apply for a Program on your child's page) — it opens the portal in your browser to finish.",
      },
      {
        title: "Pay and manage billing",
        description:
          "Invoices, Payments, Statements, and Payment Methods are in the menu. To pay, tap Pay Now on the dashboard, or Make a Payment on the Payments page. In the IlmIQ app, open the Billing tab, tap an invoice, then Pay Now; saved cards are under Profile → Payment Methods. In the browser, each invoice's Payment Summary shows the total, any credits from the school, what you've paid, and what's still owed. On Billing Statements, a refund appears as \"Refund to you\" under Payments & refunds, and the summary shows Total payments, Refunds and Net paid. Note that adding a card on Payment Methods only saves it for future use — to actually settle a balance, you still need to pay. If a sponsor program (like TEFA) covers your child, those invoices are marked \"Sponsor covered\" and don't count toward your balance — you don't pay them.",
      },
      {
        title: "Bookmark the portal",
        description:
          "Add the portal to your phone's bookmarks or home screen for one-tap access next time. On your own phone, tick \"Remember me\" when you sign in so you stay signed in. If you leave it unticked, you're signed out when you close the browser, or after 4 hours without activity.",
      },
    ],
    troubleshooting: [
      {
        problem: "My balance shows \"—\" instead of an amount",
        solution:
          "We couldn't load your invoices just now, so we don't show a number we can't confirm. Tap Retry (or Refresh) at the top of the dashboard. In the IlmIQ app it says \"Balance unavailable\" — tap the card to try again.",
      },
      {
        problem: "The app says \"Online payments not available\"",
        solution:
          "Your school hasn't turned on online card payments yet. Contact the school office to arrange payment.",
      },
      {
        problem: "I get too many (or too few) emails or app alerts",
        solution:
          "Open Profile and click \"Notification settings\" (also in the account menu at the top right). There you choose whether you get emails about new messages. App alerts about invoices, payments, AutoPay and cards go to one phone per family, normally the primary guardian's; if that's you, you can also choose which ones the IlmIQ app sends: new invoices, overdue invoices, AutoPay failed and card expiring. Each switch saves straight away. You can also change them in the IlmIQ app under Profile → Notifications. Billing emails (invoices, payment receipts, statements, overdue reminders and AutoPay notices) are always sent to your family's primary guardian, so these settings don't turn them off.",
      },
      {
        problem: "AutoPay is paused",
        solution:
          "AutoPay pauses when a charge fails after every retry (often an expired card or not enough funds) or after a payment dispute. Open Payment Methods, add or fix your card and set it as default, then click \"Disable\" and \"Enable AutoPay\" to turn it back on. If AutoPay shows \"Managed by school\", only the school office can resume it, so contact them.",
      },
    ],
    faq: [
      {
        question: "How do I turn AutoPay on or off?",
        answer:
          "Open Payment Methods. Save a card or bank account and set it as your default, then click \"Enable AutoPay\" under Automatic Payments; your default method is charged on each billing day. Click \"Disable\" to stop it. If it shows \"Managed by school\", your school turned it on by policy and only the school office can turn it off.",
      },
      {
        question: "I saved a card. Is my invoice paid?",
        answer:
          "No. Saving a card on Payment Methods only keeps it for later (and for AutoPay). To pay a balance now, tap Pay Now on the dashboard or Make a Payment on the Payments page.",
      },
    ],
    relatedGuides: ["signing-in", "messaging-overview"],
  },
  {
    slug: "messaging-overview",
    relatedGuides: ["behavior-incidents", "setup-notifications"],
    lastUpdated: "2026-10-13",
    emptyState:
      'Empty inbox? No conversation includes you yet. Click "New message" to start one: admins, principals, staff, and teachers pick an audience; parents pick the school office or one of their children\'s teachers. Group messages you send to a family, class, campus, or the whole school are under the Sent tab — each family\'s thread appears in your Inbox as soon as they reply.',
    faq: [
      {
        question: "Are messages private?",
        answer:
          "Yes — only the participants in a thread can read it. Messages are not visible to other staff or admins who aren't in the thread. A parent's message to the school office goes to all of the school's admins and principals, so whoever is free can reply.",
      },
      {
        question: "Will I get an email when someone replies?",
        answer:
          "Yes, unless you've turned these emails off. The email shows the sender, the subject, and the start of the message. Open Messages in the portal to read the full thread and reply. To turn them off, open Profile and click \"Notification settings\" (also in the account menu at the top right) and switch off \"New messages\". Switching off \"Email from IlmIQ\" (\"Email\" under Profile → Notifications in the IlmIQ app) also stops them, along with notices the school sends you from IlmIQ.",
      },
      {
        question: "Can parents start a new message?",
        answer:
          "Yes. Parents click \"New message\" and choose the school office or one of their children's teachers from a short list, and can pick which child the message is about. A teacher is listed once your child is in one of their classes.",
      },
    ],
    title: "Messaging Parents and Teachers",
    description:
      "Send threaded messages between teachers and parents directly inside the platform - no external email needed.",
    category: "messaging",
    audience: EVERYONE,
    icon: "Forum",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open the Messaging Inbox",
        description:
          'Click "Messages" in the left sidebar. The inbox is a two-pane view: thread list on the left, the open conversation on the right. Unread threads are bold with an unread-count badge next to the subject, and you can flip on the "Unread only" switch to focus the list. Tabs switch between Inbox, Closed, and (for admins, principals, staff, and teachers) Sent. Admins, principals, staff, and teachers can also filter the list by class; a class page\'s Messages button opens the inbox already filtered to that class.',
        screenshot: "/help/messaging-inbox.png",
      },
      {
        title: "Start a New Thread",
        description:
          'Click "New message" and choose the audience. Admins, principals, and staff can pick Specific people, a Family, a Class, a Campus, or the Whole school, and can reach anyone in the school. Teachers can pick Specific people, a Family, or a Class: they reach the families of students in their classes and the school\'s staff, and Class lists only the classes they teach. To find people or families, type at least 2 letters of a name, an email, or a student\'s name. Parents choose the school office or one of their children\'s teachers from a short list, and can say which child it\'s about. Enter a subject (required so threads stay organized), write your message, and click "Review" — before you click "Send", you\'ll see exactly how many families will receive it. Whole school also asks you to turn on "I understand this goes to every family." A single send can reach up to 500 families.',
        screenshot: "/help/messaging-new-thread.png",
        tip: "Group sends create one private thread per family, so each family's reply comes back only to you — and a parent with two children gets one thread, not two. The send appears under the Sent tab with how many families replied, and each reply arrives in your Inbox.",
      },
      {
        title: "Reply in a Thread",
        description:
          'Click any thread to open it. Messages appear oldest to newest, and the thread is marked read for you the moment you click it or reply (the thread shown first when the page loads stays unread until you do). Type in "Your message" and press Enter (or click the send button) to send; Shift+Enter starts a new line.',
        screenshot: "/help/messaging-thread.png",
        tip: 'Switching to another thread keeps your unfinished message with the conversation you wrote it in. If a send fails, your text stays in the box — click "Try again".',
      },
      {
        title: "Thread status",
        description:
          'The thread with the newest message is at the top of your inbox. Admins, principals, staff, and teachers can click "Close" on a finished conversation to move it to the Closed tab, and "Reopen" to bring it back. A new message in a closed conversation reopens it automatically. Parents can\'t close conversations.',
      },
    ],
  },
];

// =============================================
// NEW GUIDES B: AUDIT LOG, ROSTER & PLACEMENT, LESSON PLANS, GRADE LEVELS
// =============================================

const newGuidesB: Guide[] = [
  {
    slug: "audit-log",
    lastUpdated: "2026-10-13",
    relatedGuides: ["manage-users", "dashboard-overview", "tenant-settings"],
    emptyState:
      'Seeing "No audit log entries found"? Your filters may be too narrow. Set "Entity Type" back to "All Types" and "Event Type" back to "All Events", and clear the "Who made the change, or an ID", "Start Date" and "End Date" boxes.',
    title: "Using the Audit Log",
    description:
      "See who added, changed or removed records in your school, and when. Open the record straight from the log.",
    category: "settings",
    icon: "History",
    estimatedTime: "4 min",
    steps: [
      {
        title: "Open the Audit Log",
        description:
          'Click "Audit Log" under Admin in the left sidebar. Admins and principals can open it. Every entry is listed newest first. You can also get here by clicking a row in the Recent Activity feed on the dashboard.',
      },
      {
        title: "What gets recorded",
        description:
          "An entry is written automatically whenever someone adds, edits or removes a student, family, guardian, class, enrollment, application, invoice, payment, fee type, discount, discount given to a student, family's saved card or bank account, campus, user, campaign, potluck, AutoPay setting, or your school settings. Credit memos get an entry when they're issued or voided. A few actions get an entry of their own too, such as changing a user's role, resetting or changing a password, deactivating a user, changing AutoPay, voiding an invoice and issuing a refund. Passwords are never stored in the log.",
        tip: 'Changes the system makes on its own (for example, monthly invoicing or AutoPay) show "System" as the actor.',
      },
      {
        title: "Read a row",
        description:
          'Each row has four columns. "Timestamp" is shown in your own time zone. "Actor" is the person who made the change, with their role underneath. "Action" shows what happened ("Created", "Updated" or "Deleted", plus labels such as "Invoice voided", "Payment refunded" or "Role changed" for the actions that get their own entry) and the kind of record, such as "Student", "Invoice" or "Saved payment method". "Record" shows the record\'s name or number. When it\'s a link, click it to open that record.',
      },
      {
        title: "See exactly what changed",
        description:
          'Click the arrow at the start of a row to expand it. For an edit, "Changes" lists every field that changed with its "old" and "new" value. "Metadata" shows extra details, such as when the change happened and who made it.',
      },
      {
        title: "Filter and page through",
        description:
          'In "Who made the change, or an ID", type part of the name or email of the person who made the change, or paste a record\'s exact ID to see every entry about that record. It doesn\'t search record names, such as a student\'s or family\'s name. "Entity Type" shows one kind of record, such as "Students", "Invoices" or "Saved payment methods". "Event Type" shows "Created", "Updated" or "Deleted" entries, or one kind of action such as "Invoice voided", "Payment refunded" or "Password reset or changed". "Start Date" and "End Date" use your school\'s own days, and both dates are included. Use the rows-per-page control at the bottom to show 10, 20, 50 or 100 entries at a time (20 is the default). Your filters stay in place when you open a record and come back with your browser\'s Back button.',
      },
      {
        title: "Find who changed something",
        description:
          'To see what one person did, type their name or email in "Who made the change, or an ID". To see who changed a particular record, paste its ID into the same box, or set "Entity Type" (for example "Invoices") and a date range, then look down the "Record" column for the one you care about. Expand a row to see what changed. The "Actor" column tells you who did it.',
        tip: "Entries are newest first, so recent changes are on the first page.",
      },
    ],
    troubleshooting: [
      {
        problem: "I typed a student's or family's name in the search box and got nothing.",
        solution:
          'The search box finds the person who made the change (by name or email), or an exact record ID. To find changes to a student or family, set "Entity Type" to "Students" or "Families", add a date range if you know roughly when, and look down the "Record" column. Or paste the record\'s ID, which is the last part of the address when you open its page.',
      },
      {
        problem: "I can't find sign-ins or sign-outs.",
        solution:
          'Ordinary sign-ins and sign-outs aren\'t recorded. When an account is locked after too many wrong passwords, that shows up as an "Updated" entry for the user account.',
      },
      {
        problem: 'The "Record" column shows "—".',
        solution:
          "That entry isn't tied to a single record you can open. Expand the row to see its details.",
      },
    ],
    faq: [
      {
        question: "Who can see the Audit Log?",
        answer:
          "Admins and principals. Staff, teachers and parents don't have it in their sidebar.",
      },
      {
        question: "Can entries be edited or deleted?",
        answer:
          "No. There's no way to edit or delete audit entries from the app, so the log stays a reliable record of what happened.",
      },
    ],
  },
  {
    slug: "class-roster-and-placement",
    lastUpdated: "2026-10-13",
    relatedGuides: ["manage-enrollments", "manage-discounts", "create-class"],
    emptyState:
      'Roster shows "No enrollments found"? Enroll students in the class first (see Managing Enrollments). The Placement page only lists students who are actively enrolled in the class.',
    title: "Class Rosters and Student Placement",
    description:
      "See who is enrolled in a class, export the list, and move students to the right class after an assessment.",
    category: "classes",
    audience: OFFICE_AND_TEACHERS,
    icon: "Groups",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Open a class roster",
        description:
          'From the Classes list, open a class\'s row menu and choose "View Roster". From a class page, click "Roster" in the Academics card. Teachers can click "Roster" on a class in My Classes. The page is titled "<Class name> Roster" and shows the class dates. Admins, principals, staff and teachers can open it.',
      },
      {
        title: "Read and filter the roster",
        description:
          'Columns: "Student Number", "Student Name", "Status", "Enrolled At", "Unenrolled At" and "Unenroll Reason". Use the "Status" filter to show All, Active, Paused, Withdrawn, Completed or Transferred enrollments. A student moved out through placement shows as transferred, with the reason "Placement: moved between classes".',
      },
      {
        title: "Export the roster",
        description:
          'Click "Export CSV" to download every enrollment that matches the current "Status" filter, not just the page you\'re looking at. You only see this button if your role is allowed to export enrollments.',
      },
      {
        title: "Unenroll from the roster",
        description:
          'Open a row\'s menu (the three dots). "View Details" opens the enrollment. For an active enrollment, "Unenroll Student" asks for an "Effective Date" (today by default) and a "Reason", which is required. Then click "Unenroll". This option only appears if you can manage enrollments. See Managing Enrollments for how unenrolling affects billing.',
      },
      {
        title: "Set up a placement rubric (optional)",
        description:
          'On the class page, find the "Placement Rubric" card and click "Configure" (admins, principals and staff). Turn on the switch so it reads "Rubric enabled", enter a "Max score", then add score bands with "Add threshold" (each band has a "Min", a "Max" and a "Target class"). Click "Save", check the list of routes, and click "Save rubric". Without a rubric you simply pick each student\'s class by hand.',
        tip: "A band can point back at the same class, for students who should stay where they are.",
      },
      {
        title: "Open Placement",
        description:
          'On the class page, click "Placement" at the top. The button only shows while the class hasn\'t ended. Teachers open their class from My Classes (View) and click "Placement" in its Quick Actions. The page lists every active student in the class with a "Score", "Notes" and "Place into" box. "Place into" offers the active classes at the same campus, and the current class is marked "(stay)". A student who is also enrolled in another class can be placed too: only their enrollment in this class moves.',
      },
      {
        title: "Assess and place",
        description:
          'For each student, type a score and any notes, and choose a class in "Place into". With a rubric, typing a score fills in the suggested class for you; click the "↓" chip to use the suggestion if you\'ve already picked something else. Then click "Place all". Only rows you changed are saved. A row left on "(stay)" with a score or note records a stay-put decision without moving the student. Students you moved drop off this page, because they\'re no longer in this class. Students who stayed show "Placed". The class page\'s "Placement History" card keeps every decision.',
      },
      {
        title: "What a move does",
        description:
          "The student's enrollment in the old class closes as transferred, and a new active enrollment opens in the new class from today. A discount given for the old class moves with the student. If they already have the same discount on the new class, the larger one is kept. Placement doesn't create, change or credit any invoice. Invoices already issued for the old class stay as they are, so if the two classes charge different fees, review the family's invoices.",
      },
    ],
    troubleshooting: [
      {
        problem: '"Placed 3. 2 could not be placed — the reason is shown on each red row."',
        solution:
          "Students who moved have dropped off the list. Students who couldn't be placed stay on it as red rows marked \"Not placed\", with the reason underneath, and keep the score, notes and class you chose. Fix the cause and click \"Place all\" again. The usual reasons: the new class is full; it's for boys or girls only and the student doesn't match (or has no gender on file); the student is already active in that class; or the new class has ended or been archived.",
      },
      {
        problem: '"Nothing to place — fill in a score, note, or change a level."',
        solution:
          'You haven\'t changed any row since the page loaded. Enter a score or a note, or pick a different class in "Place into".',
      },
      {
        problem: 'The class I want isn\'t in "Place into".',
        solution:
          "Only active classes at the same campus are listed. Check that the class is active and at the right campus.",
      },
    ],
    faq: [
      {
        question: "Can teachers place students?",
        answer:
          "Yes, for the classes they teach. Open the class from My Classes (View) and click \"Placement\" in its Quick Actions. Teachers can place their students into any active class at the same campus.",
      },
      {
        question: "Can I undo a placement?",
        answer:
          'Yes. Open the new class\'s Placement page and place the student back into the original class. That records another move and opens a new enrollment in the original class from today. A class discount moves back with them.',
      },
    ],
  },
  {
    slug: "lesson-plans",
    lastUpdated: "2026-10-13",
    relatedGuides: ["lesson-progress", "build-unit-and-lessons", "teacher-portal-overview"],
    emptyState:
      'Blank week? Pick a class in the "Class" box, then click any "No plan" box to write your first plan. If you\'re a teacher and see "You don\'t have any active classes", ask an admin to assign you to a class.',
    title: "Weekly Lesson Plans and Plan Templates",
    description:
      "Plan each class week by week, link plans to curriculum lessons, share them with co-teachers, and reuse good plans as templates.",
    category: "academics",
    audience: OFFICE_AND_TEACHERS,
    icon: "EventNote",
    estimatedTime: "6 min",
    steps: [
      {
        title: "Open Lesson Plans",
        description:
          'Teachers click "Lesson Plans" in the sidebar. Admins, principals and staff find "Lesson Plans" under Academics. The first class is picked for you; choose another in the "Class" box. The week runs Monday to Sunday. Use the arrows to move between weeks, "Today" to jump back to this week, or the date box to go to any week.',
      },
      {
        title: "Write a plan",
        description:
          'Click a day\'s "No plan" box or its "+" button, or "Add plan" at the top for today. Fill in "Title *" and "Date *", and "Period" if you run a block schedule. Then fill in the sections you need: "Objectives", "Warm-up / Do Now", "Activities / Direct Instruction", "Assessment / Exit Ticket", "Homework", "Materials", "Differentiation" and "Notes". Click "Create".',
        tip: 'You can have one plan of your own per class, day and period. For two lessons on the same day, give each a different "Period". If you already have a plan on that day and period, nothing is saved or overwritten: a warning appears with "Open existing plan" to go to it, and on a new plan "Replace it with mine" to overwrite the existing plan with what you typed (it keeps its status). Or change the date or period and save again.',
      },
      {
        title: "Link a curriculum lesson",
        description:
          'In "Curriculum lesson (optional)", type at least two letters to search your school\'s lessons and pick one. Any empty fields are filled from the catalog: the title, the objectives, and sometimes the activities. Fields you\'ve already typed are left alone. To remove the link, click the x on the "Linked:" chip and save. Emptying a box and saving clears it too.',
        tip: "Linking a lesson doesn't mark it as taught. Mark it on the class's Lesson Progress page as well.",
      },
      {
        title: "Move a plan through its statuses",
        description:
          'New plans are saved as "Draft" (still working on it). To change the status, open the plan again and set "Status" to "Published" (final, ready to teach from or hand to a substitute) or "Delivered" (the lesson has been taught). Status only tracks progress: admins, principals, staff and the class\'s teachers can see a plan whatever its status.',
      },
      {
        title: "Share with co-teachers",
        description:
          'Teachers of the class can always see your plans. Switch on "Let co-teachers edit" to let them edit too. A co-teacher\'s plan without that switch opens view only. Admins, principals and staff can edit any plan. Only the plan\'s author, or an admin, principal or staff member, can archive it with the "Archive this plan" button. Archived plans disappear from the week.',
      },
      {
        title: "Copy last week and print",
        description:
          '"Copy last week" copies plans from the previous week into this week as drafts, on the same weekdays. Teachers copy only their own plans. Admins, principals and staff copy the whole class: IlmIQ first shows how many plans and teachers will be copied, and each copy keeps its teacher (plans whose teacher is no longer assigned to the class are copied under your name). Days that already have a plan are skipped, never overwritten, and the message lists them. "Print" gives a clean weekly handout you can print or save as a PDF, which is handy for a substitute.',
      },
      {
        title: "Save and use templates",
        description:
          'On a saved plan, click "Save as template". When creating a new plan, "Save & template" does both at once. Give the template a name and, if you like, a subject and grade level. To use one, click "From template" in the toolbar, or the bookmark button on a day. Pick a template, set the "Target date" (and "Period (optional)"), and click "Use template". If you already have a plan on that day and period, the template isn\'t used; click "Open existing plan" or change the date or period.',
      },
      {
        title: "Manage the template library",
        description:
          'Open the Curriculum page and click "Lesson Plan Templates". Search by name or title, or filter by "Subject", "Grade level" and "Visibility". Click "New template" to write one from scratch, or click a row to open and edit it (teachers can edit and save their own templates; admins, principals and staff can edit any). A teacher\'s templates are "Private": only that teacher, and admins, principals and staff, can see them. Admins, principals and staff can switch on "Share with the whole school", which marks a template "School-wide" so every teacher at every campus can use it.',
      },
    ],
    faq: [
      {
        question: "Can parents see lesson plans?",
        answer:
          "No. Lesson plans are for teachers and school staff only. Parents see lesson progress, not plans.",
      },
      {
        question: 'Why does "Copy last week" say there\'s nothing to copy?',
        answer:
          "It copies plans for the selected class from the week before the one you're viewing. Check that you're on the right class and week. Teachers copy only their own plans, so if a co-teacher wrote last week's plans, there's nothing of yours to copy. Admins, principals and staff copy every teacher's plans for the class.",
      },
      {
        question: "Can I edit a template someone else made?",
        answer:
          "Teachers can edit only their own templates. Admins, principals and staff can edit any template.",
      },
    ],
  },
  {
    slug: "grade-levels",
    lastUpdated: "2026-10-12",
    relatedGuides: ["create-class", "clone-class", "curriculum-overview"],
    emptyState:
      'No grade levels yet? Click "Apply standard K-12 template" to add Pre-K, Kindergarten and Grades 1 through 12 (14 levels) in one go, or "Add custom level" to build your own list (for example "Hifdh Year 1").',
    title: "Setting Up Grade Levels",
    description:
      "Create the ordered list of grade levels your school uses, and tag classes with the grades they serve.",
    category: "classes",
    icon: "Stairs",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open Grade Levels",
        description:
          'Click "Grade Levels" under Academics in the left sidebar (admins and principals). You can also open it from the "Grade Levels" card on the Curriculum page. The page lists your levels in order, with how many active classes and how many students are linked to each.',
      },
      {
        title: "Start from the standard list",
        description:
          'Click "Apply standard K-12 template", then "Apply". This adds Pre-K, Kindergarten and Grades 1 through 12. It\'s safe to run again: any level whose name or code you already have is skipped. As the note under the button says, you can rename, reorder or archive any of the levels afterwards: use "Edit" to change a name or its "Sequence", and "Archive" to retire one.',
      },
      {
        title: "Add or edit a level",
        description:
          'Click "Add Grade Level". Enter a "Name" (for example "Grade 8" or "Hifdh Year 1"). You can also add a "Short Code (optional)" for compact displays (it must be unique), a "Sequence (optional)" to set its place in the order (left blank, it goes to the end), and a "Description (optional)". Click "Add". To change a level later, open its row menu and choose "Edit".',
      },
      {
        title: "See which classes use a level",
        description:
          "Click a level's name to open its page, which lists the classes tagged with that level.",
      },
      {
        title: "Archive a level you no longer use",
        description:
          'Open the row menu and choose "Archive". Classes already tagged with it keep it; it just stops appearing in pickers. Switch on "Show archived" to see archived levels, and use "Restore" to bring one back. Levels can\'t be deleted.',
      },
      {
        title: "Tag classes with grade levels",
        description:
          'When you create or edit a class, pick one or more grade levels in "Grade Levels". Leave it empty for an ungraded class. On a class page, the "Grade Levels" card shows them; click "Edit" to change them. Cloning a class copies its grade levels too (the Clone Class page lets you switch that off).',
        tip: "Lesson plan templates can be tagged with a grade level too, so teachers can filter the template library by grade.",
      },
    ],
    faq: [
      {
        question: "Can a class serve more than one grade?",
        answer:
          'Yes. "Grade Levels" accepts several levels, which suits combined or multi-grade classrooms.',
      },
      {
        question: "Do grade levels affect billing or admissions?",
        answer:
          "No. Grade levels describe who a class is for and help you organize. They don't change fees, invoices, or who can apply or enroll.",
      },
      {
        question: "Who can change grade levels?",
        answer:
          "Admins and principals. Staff and teachers can see the list from the Grade Levels card on the Curriculum page, without the add, edit and archive buttons. If the list is empty, it tells them their principal or administrator sets the levels up. Staff can still tag a class with grade levels from the class page.",
      },
    ],
  },
];

// =============================================
// NEW GUIDES (A): invoice corrections, signing in, search, Billed vs Collected
// =============================================

const newGuidesA: Guide[] = [
  {
    slug: "correcting-invoices",
    lastUpdated: "2026-10-13",
    relatedGuides: ["issuing-refunds", "late-fees-and-dunning", "manage-discounts", "billed-vs-collected-report"],
    faq: [
      {
        question: "Can I write off an invoice?",
        answer:
          "There's no write-off button in IlmIQ. To forgive what a family owes, issue a credit memo for the balance with the category \"Goodwill / hardship\". You may still see \"Written Off\" as a status or a report column. That's for invoices marked that way outside the normal screens, and most schools never have any.",
      },
      {
        question: "Should I use a credit memo or a refund?",
        answer:
          "A credit memo lowers what the family still owes. A refund gives back money they already paid. If the invoice is fully paid, there's nothing left to credit, so refund the payment instead (see \"Issuing Refunds\").",
      },
      {
        question: "Is the family told about the change?",
        answer:
          "No email goes out when you issue a credit memo, apply a discount, remove a late fee, or void an invoice. The family sees the lower balance in the parent portal, where the invoice's Payment Summary has a \"Credits\" row, and credit memos show on their next family statement with the start of your reason. To send them the corrected invoice, use \"Resend Email\": it shows the invoice total, any credits and payments, and the current balance. Tell the family yourself if the reason matters.",
      },
      {
        question: "How do corrections show up in reports?",
        answer:
          "In the Billed vs Collected report, discounts you apply with \"Apply Discount\" and credit memos in the \"Discount correction\" category count as Discounts. All other credit memos count as Credits. Voided credit memos and voided invoices aren't counted at all.",
      },
    ],
    troubleshooting: [
      {
        problem: "I can't see \"Issue Credit Memo\", \"Apply Discount\" or \"Remove\"",
        solution:
          "These are admin-only. They also need an invoice that isn't void and still has a balance. On a fully paid invoice, refund the payment instead. \"Remove\" is also hidden on a written-off invoice, and once a late fee has been fully credited the line shows \"Removed\" instead.",
      },
      {
        problem: "There's no \"Void\" button on the invoice",
        solution:
          "Only admins can void invoices, and only an invoice that is \"Issued\" or \"Overdue\". Paid and already-void invoices have no \"Void\" button. Use a credit memo to lower what's owed, or refund the payment.",
      },
      {
        problem: "The \"Void\" button is greyed out",
        solution:
          "Hover over it (or tap it on a phone) to see why. If the invoice has ever had a payment, even one that was later fully refunded, it can't be voided, so issue a credit memo for the balance instead. If a credit memo is still active on the invoice, void the credit memos first, then void the invoice.",
      },
      {
        problem: "Voiding a credit memo was refused",
        solution:
          "The family has paid on the invoice since the credit was issued, so adding the credit back would make them owe more than the invoice total. Refund the extra amount from their payment instead.",
      },
    ],
    title: "Correcting an Invoice: Credits, Discounts, Voids, and Late Fees",
    description:
      "Fix an invoice without deleting anything: issue a credit memo, apply a discount, remove a late fee, void the invoice, or undo a credit memo you got wrong.",
    category: "billing",
    icon: "RequestQuote",
    estimatedTime: "6 min",
    steps: [
      {
        title: "Open the invoice",
        description:
          "Go to \"Invoices\" and click the invoice, or search for its number with Ctrl+K. Once an invoice is issued, you can't edit its charges. Every correction is added on top of it, so the full history stays visible. \"Apply Discount\", \"Issue Credit Memo\" and \"Void\" are at the top of the invoice page. \"Remove\" for a late fee is on the late-fee line, and \"Void\" for a credit memo is in the \"Discounts & Credit Memos\" list.",
      },
      {
        title: "Who can make corrections",
        description:
          "Only admins can issue credit memos, apply discounts, remove late fees, void credit memos, and void invoices. Principals and staff can open the invoice and use \"Download PDF\" and \"Resend Email\". They don't see the correction buttons.",
      },
      {
        title: "Issue a credit memo",
        description:
          "Click \"Issue Credit Memo\". The \"Amount\" starts at the full balance, so change it to the amount you want to credit. It can't be more than the balance shown at the top of the dialog. Pick a \"Category\": Student withdrawal, Discount correction, Billing correction, Goodwill / hardship, Dispute resolution, Late fee removal, Sponsor rate adjustment, Sponsor award revocation, or Other. To credit one charge, choose it under \"Specific line item (optional)\" and the amount fills in for you. Late fees aren't in that list; use \"Remove\" on the late fee instead. Write a short \"Reason\" (100 characters at most), then click \"Issue Credit Memo\". The balance drops straight away. If it reaches $0, the invoice shows \"Paid\". The credit is listed under \"Discounts & Credit Memos\".",
        tip: "Pick the closest category. Reports use it to tell discounts apart from other credits.",
      },
      {
        title: "Apply a discount to one invoice",
        description:
          "Click \"Apply Discount\" and choose one of your active discounts in \"Select Discount\". A percentage discount is worked out on what's still owed, not on the original total. A fixed discount is its set amount. Either way, it can't be more than the balance. Add \"Notes (Optional)\" if you like, but keep them short: the discount's name and your notes are saved together, and long notes are refused (about 85 characters in all). Then click \"Apply Discount\". It's saved as a credit memo with the discount's name. It changes this invoice only. For a discount that should apply every month, see \"Creating and Applying Discounts\".",
      },
      {
        title: "Remove a late fee",
        description:
          "Late fees show as a line with a red \"Late Fee\" chip. Click \"Remove\" next to it, type a short \"Reason for removal\" (82 characters at most), and click \"Remove Late Fee\". This adds a credit memo for the fee, or for the balance due if that's smaller. If the family has already paid part of the fee, the dialog says only the unpaid part can be credited; refund the rest from their payment. The original line stays on the invoice for the record, with a \"Removed\" chip once the whole fee is credited (\"Partly removed\" if only some of it is), and \"Remove\" no longer appears. Earlier removals count too, including credit memos with the \"Late fee removal\" category. Late fees can't be removed on a void or written-off invoice.",
      },
      {
        title: "Undo a credit memo",
        description:
          "Under \"Discounts & Credit Memos\", click \"Void\" on the credit you want to undo. Fill in \"Why are you voiding this credit memo?\" and click \"Void Credit Memo\". The amount goes back onto the balance. The credit stays in the list, crossed out, with a \"Voided\" chip and your reason. This also works for removed late fees and applied discounts, because both are credit memos.",
      },
      {
        title: "Void an invoice",
        description:
          "Void an invoice that should never have been sent, such as a duplicate. The \"Void\" button appears on \"Issued\" and \"Overdue\" invoices. It's greyed out, with the reason when you hover over it, if the invoice has ever had a payment (even one that was refunded) or still has an active credit memo (void the credit memo first). On the Invoices list, \"Void Invoice\" is in a row's menu only when the invoice can be voided. Click \"Void\", enter a \"Void Reason\", and click \"Void Invoice\". The invoice changes to \"Void\" and the family no longer owes anything on it. Voiding is permanent and can't be undone. If you voided the wrong invoice, bill the family again with \"Generate Invoice\". The monthly batch won't do it for you: it treats a family whose batch invoice was voided as settled for that month.",
      },
      {
        title: "What the family sees",
        description:
          "IlmIQ doesn't email the family about any of these changes. In the parent portal, the invoice's Payment Summary keeps the original \"Total\" (a removed late fee still shows under \"Late Fees\"), then lists the \"Credits\", the \"Amount Paid\" and the lower \"Outstanding Balance\". Credit memos appear on the family's next statement as \"Credit:\" followed by the start of the reason you typed, so write the reason with the family in mind. \"Resend Email\" sends the invoice with its credits, payments and current balance. On a paid invoice the email is headed \"Invoice Paid\" and asks for nothing, and while a payment is still processing it has no \"Pay Now\" button. \"Resend Email\" isn't offered on void or written-off invoices. If a correction needs explaining, contact the family yourself.",
      },
    ],
  },
  {
    slug: "signing-in",
    lastUpdated: "2026-10-14",
    relatedGuides: ["manage-users", "search-and-shortcuts", "dashboard-overview"],
    faq: [
      {
        question: "My account says it's locked. What do I do?",
        answer:
          "After 5 wrong passwords in a row, your account locks for 15 minutes. Wait 15 minutes, then sign in. If you can't wait, ask an admin to click \"Reset Password\" on your account (a principal can do this for a teacher's account). That lifts the lock and emails you a temporary password. Only a platform admin can do this for an admin's account. Resetting your own password with \"Forgot password?\" lifts the lock too, so you can sign in with the new password straight away.",
      },
      {
        question: "An admin or principal reset my password. What now?",
        answer:
          "You'll get an email with a temporary password. Sign in with it and you'll be taken straight to a page that says \"You must change your password before continuing.\" Choose your own password there. You then carry on, already signed in.",
      },
      {
        question: "Why was I signed out even though \"Remember me\" was ticked?",
        answer:
          "Changing your password signs you out on your other computers and phones (not the one you changed it on). Resetting it with \"Forgot password?\" signs you out everywhere. You're also asked to sign in again if you haven't used IlmIQ on that computer for 30 days, or if your account was turned off.",
      },
      {
        question: "Does signing out on one computer sign me out everywhere?",
        answer:
          "No. \"Logout\" signs out every IlmIQ tab in that browser only. To sign out on every other computer and phone, change your password.",
      },
    ],
    troubleshooting: [
      {
        problem: "The reset email never arrived",
        solution:
          "Check your spam or junk folder. Make sure you typed the email address your school has on file for you. The page says the email was sent even when no account uses that address, so a typo won't show an error. If nothing arrives, ask your school office to check your email address.",
      },
      {
        problem: "The reset link says it may be expired",
        solution:
          "Reset links work once and expire after 1 hour. Go back to \"Forgot password?\" and request a new one.",
      },
      {
        problem: "My new password is refused",
        solution:
          "Passwords need at least 8 characters with an uppercase letter, a lowercase letter, a number, and a special character such as ! @ # $ or ?. Every password form shows the rules and ticks them off as you type.",
      },
      {
        problem: "I keep seeing \"Can't reach the server\"",
        solution:
          "Check your internet connection, then click \"Try again\". If you're finished on this computer, click \"Sign out of this computer\" and sign in again later.",
      },
    ],
    title: "Signing In, Staying Signed In, and Your Password",
    description:
      "How \"Remember me\" works, why you might be signed out, what the \"Can't reach the server\" screen means, and how to reset or change your password.",
    category: "getting-started",
    audience: EVERYONE,
    icon: "Login",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Sign in",
        description:
          "On your school's sign-in page, enter your \"Email Address\" and \"Password\", then click \"Sign In\". If you have accounts at more than one school with the same email, click \"Have accounts at multiple schools?\" (when you see it) and enter your school's code in \"School Code (Optional)\". After signing in, you land on your own home page: the Dashboard for admins and principals, Attendance (today's sessions) for staff, the teacher portal for teachers, and the parent portal for parents.",
      },
      {
        title: "Choose \"Remember me\"",
        description:
          "\"Remember me\" is ticked to begin with. Ticked: you stay signed in on this computer, even after you close the browser. You're asked to sign in again if you haven't used IlmIQ on this computer for 30 days, or after your password is changed somewhere else or reset (see the questions below). Unticked: your sign-in ends when you close the browser, and you're signed out after 4 hours without activity (see the next step).",
        tip: "On a shared or front-desk computer, untick \"Remember me\", and use \"Logout\" when you're done.",
      },
      {
        title: "Signed out after 4 hours without activity",
        description:
          "This only applies if \"Remember me\" was unticked. After 4 hours with no activity in any IlmIQ tab, every tab signs out. Two minutes before that, a box asks \"Are you still there?\" and counts down. Click \"Stay signed in\" to carry on, or \"Sign out\". Working in any one tab keeps all your tabs signed in. If you come back soon after, the sign-in page says \"You were signed out after 4 hours without activity.\" Sign in and you're taken back to the page you were on, as long as your role can open it; otherwise you land on your own home page.",
      },
      {
        title: "New tabs and pasted links",
        description:
          "Once you're signed in, new tabs open already signed in. That includes a link you paste in or one a colleague sends you, as long as your role can open that page. If you aren't signed in, the link takes you to the sign-in page first. After you sign in, you go on to the page you wanted, as long as your role can open it; otherwise you land on your own home page.",
      },
      {
        title: "Sign out",
        description:
          "Click your initials in the top-right corner, then \"Logout\". Every IlmIQ tab in this browser signs out. Other computers and phones stay signed in.",
      },
      {
        title: "The \"Can't reach the server\" screen",
        description:
          "If \"Remember me\" was unticked and IlmIQ can't reach the server when a tab opens (for example, your internet is down), you see \"Can't reach the server\" instead of the sign-in page. Click \"Try again\" once your connection is back. If you're done on this computer, click \"Sign out of this computer\". The screen shows no names or data, because on a shared computer the sign-in might be someone else's.",
      },
      {
        title: "Forgot your password?",
        description:
          "On the sign-in page, click \"Forgot password?\". Enter your \"Email Address\" and click \"Send Reset Link\". Open the link in the email, enter a \"New Password\" and \"Confirm New Password\", then click \"Set Password\". The link works once and expires after 1 hour. Resetting your password signs you out everywhere, so sign in with the new one.",
      },
      {
        title: "Change your password or profile",
        description:
          "Click your initials in the top-right corner, then \"Profile\". On \"Profile Information\" you can change your \"First Name\", \"Last Name\" and \"Phone Number\". Click \"Save Changes\" when you're done. You can't change your email address here. On \"Security\", enter your \"Current Password\", a \"New Password\" and \"Confirm New Password\", then click \"Change Password\". A new password needs at least 8 characters, with an uppercase letter, a lowercase letter, a number, and a special character such as ! @ # $ or ?. A checklist under \"New Password\" ticks off each rule as you type. Changing it signs you out on your other computers and phones; you stay signed in here.",
      },
    ],
  },
  {
    slug: "search-and-shortcuts",
    lastUpdated: "2026-10-12",
    relatedGuides: ["navigating-with-detail-drawers", "dashboard-overview", "signing-in"],
    faq: [
      {
        question: "What does search look at?",
        answer:
          "Student names and student numbers; family names, plus each guardian's name, email address and phone number (so typing a parent's email or phone finds their family); and invoice numbers (an invoice can also be found by its family's name). It doesn't search notes.",
      },
      {
        question: "Does search only look in the campus I have selected?",
        answer:
          "No. Record search covers every campus, so you can find a student wherever they're enrolled.",
      },
      {
        question: "Can I change the keyboard shortcuts?",
        answer:
          "No, the shortcuts are fixed.",
      },
    ],
    troubleshooting: [
      {
        problem: "I can't find a student I know is there",
        solution:
          "Type at least 2 letters, or try the student number. If the list ends with a line like \"3 more students match\", type more of the name to narrow it down. Teachers only find students in their own classes.",
      },
      {
        problem: "Search says \"Keep typing to search\"",
        solution:
          "You've typed only 1 character. Record search starts at 2.",
      },
      {
        problem: "A G shortcut does nothing",
        solution:
          "Your role can't open that page, so the shortcut isn't offered. Press Ctrl+/ (⌘/ on a Mac) to see the ones you can use. Shortcuts also wait while you're typing in a box or while a drop-down list is open.",
      },
    ],
    title: "Search and Keyboard Shortcuts",
    description:
      "Find records and pages in a few keystrokes, and learn the keyboard shortcuts for your role.",
    category: "getting-started",
    audience: EVERYONE,
    icon: "Search",
    estimatedTime: "3 min",
    steps: [
      {
        title: "Open search",
        description:
          "Click the \"Search...\" box at the top of any page, or press Ctrl+K (⌘K on a Mac). On a phone, tap the magnifying glass. Ctrl+K works even while you're typing in another box, so you can jump away mid-task.",
      },
      {
        title: "Find a student, family or invoice",
        description:
          "Type at least 2 letters. Search finds students by name or student number, families by family name or by a guardian's name, email or phone number, and invoices by invoice number or family name. Each result has a short line to tell look-alikes apart: a student's number and family, how many students a family has, or an invoice's family, total and status. Up to 5 of each kind are listed. If more match, the list says so. Type more of the name to narrow it down.",
      },
      {
        title: "Jump to a page or start a task",
        description:
          "The same box finds pages, such as \"Invoices\" or \"Reports\", and quick actions: \"Add New Student\", \"Add New Family\", \"Create Enrollment\" and \"Record Payment\". Use the ↑ and ↓ arrow keys to move, Enter to open, and Esc to close.",
      },
      {
        title: "What you can find depends on your role",
        description:
          "The search box says what you can find. Admins, principals and staff see \"Search students, families, invoices and pages\" and can find all three, plus the office pages and quick actions. Pages your role can't open, such as \"Reports\" for staff, aren't listed. Teachers see \"Search students and pages\" and can find students in their own classes, plus their dashboard, \"My Classes\", \"My Students\" and \"Attendance\". Parents see \"Search pages\" and get shortcuts to their own pages, such as \"Invoices\", \"Payments\", \"Statements\" and \"Payment Methods\", plus \"Apply for a Program\" and \"Make a Payment\". Parents can't search for records.",
      },
      {
        title: "Keyboard shortcuts",
        description:
          "Press Ctrl+/ (⌘/ on a Mac) to see the full list. General: Ctrl+K \"Open search\", Ctrl+/ \"Show keyboard shortcuts\", Esc \"Close dialog / Clear selection\". Navigation: press G, then within a second press H \"Go to Home\", S \"Go to Students\", F \"Go to Families\", C \"Go to Classes\", I \"Go to Invoices\", P \"Go to Payments\", E \"Go to Enrollments\", or R \"Go to Reports\". G then H always takes you to your own home page. The list shows only the shortcuts your role can use. Letters work in upper or lower case, so Caps Lock doesn't matter. Single-letter shortcuts don't work while you're typing in a box or while a drop-down list is open.",
        tip: "Admins and principals can use every G shortcut. Staff can use all of them except Reports, and G then H takes staff to Attendance. Teachers get G then S (\"My Students\") and G then C (\"My Classes\"). Parents have only G then H.",
      },
    ],
  },
  {
    slug: "billed-vs-collected-report",
    lastUpdated: "2026-10-12",
    relatedGuides: ["reports-overview", "correcting-invoices", "issuing-refunds", "setup-fee-types"],
    emptyState:
      "If the table says \"Nothing billed in this range\", nothing matches what you picked. Try \"School year\" or a wider range of months, set \"Fees\" back to \"All\", clear the class and fee type filters, or check the campus selected at the top of the screen. Voided and sponsor-funded invoices are never counted.",
    faq: [
      {
        question: "How is one payment split across classes?",
        answer:
          "Payments and write-offs are recorded against a whole invoice. When an invoice has more than one charge, they're shared out in proportion to what each charge owes. For example, a $150 payment on a $200 class and a $100 class counts $100 and $50. Discounts stay on the charge they were given for. A credit memo for one charge stays on that charge, a removed late fee comes off the late fee, and other discounts and goodwill credits come off school fees first. Withdrawal, billing-correction and dispute credits are shared across every charge.",
      },
      {
        question: "Why don't the totals match the Revenue or Collections report?",
        answer:
          "Each report counts by a different date. Billed vs Collected counts charges in the month they're for. Revenue counts by invoice date and Collections by payment date. Payments here count whenever they arrived, up to the moment you ran the report.",
      },
      {
        question: "How are refunds shown?",
        answer:
          "Refunds and chargebacks are taken off \"Collected\". The charge the money was paid against owes it again, so \"Still owed\" goes up, unless the refund was for an overpayment. The CSV export lists them separately as \"Refunds & chargebacks (already in Collected)\".",
      },
      {
        question: "Why is \"Written off\" always $0?",
        answer:
          "There's no write-off button in IlmIQ, so most schools never have written-off invoices. To forgive a balance, issue a credit memo with the category \"Goodwill / hardship\". It shows under \"Credits\".",
      },
      {
        question: "What is the \"Unattributed adjustments\" row?",
        answer:
          "Amounts that couldn't be tied to a single charge, such as an overpayment. They get their own row so the totals still add up, and a note above the table says how many invoices are involved.",
      },
    ],
    troubleshooting: [
      {
        problem: "The report says \"Pick a valid range\"",
        solution:
          "The start month must be on or before the end month, and the range can't be longer than 24 months.",
      },
      {
        problem: "A class discount warning appears above the table",
        solution:
          "A discount was given for a class the report can't identify any more, for example after the class was renamed. The discount is shared across that student's classes, so the totals are still right, but the split between those classes is approximate.",
      },
    ],
    title: "Billed vs Collected: What Each Class Earned",
    description:
      "For each class or fee type, see what you billed for a set of months, what came off as discounts and credits, what's been collected, and what's still owed.",
    category: "reports",
    icon: "Assessment",
    estimatedTime: "5 min",
    steps: [
      {
        title: "Open the report",
        description:
          "Click \"Reports\" in the sidebar, then \"Billed vs Collected\". Admins and principals can open it. The report follows the campus selected at the top of the screen. When one is selected, a \"Campus:\" chip shows which.",
      },
      {
        title: "Pick the months",
        description:
          "Set \"From month\" and \"To month\", or click \"This month\", \"Last month\", \"Last 3 months\" or \"School year\". \"School year\" starts at the first month of your current academic year (or the most recent September if you haven't set one up). A month means the month a charge is for, not when the invoice was sent or paid. You can pick up to 24 months.",
      },
      {
        title: "Group and filter",
        description:
          "Under \"Group by\", choose \"Class\" or \"Fee type\". Under \"Fees\", choose \"All\", \"School\" or \"Partner\". Partner means fees you collect on behalf of a partner institution. School is everything else, including late fees. To focus on one class or fee type, use the \"Class\" or \"Fee type\" filters. Some rows aren't a class or fee type: \"Late fees\", \"Other charges (no class)\", \"Other charges (no fee type)\" and \"Unattributed adjustments\".",
      },
      {
        title: "Read the numbers",
        description:
          "The cards at the top show \"Net billed\", \"Collected\", \"Still owed\", \"Discounts\" (with credits underneath) and \"Written off\". The table has \"Billed\", \"Discounts\", \"Credits\", \"Net billed\", \"Collected\", \"Still owed\", \"Written off\" and \"Collection rate\". Everything adds up: Billed − Discounts − Credits = Net billed = Collected + Still owed + Written off. On a phone, some columns are hidden.",
        tip: "\"What the numbers mean\" at the bottom of the page defines every column.",
      },
      {
        title: "What counts as what",
        description:
          "Billed is the charges before any discount or credit, late fees included. Discounts are sibling discounts, mid-month proration, student discounts, and discounts applied to an invoice (with \"Apply Discount\" or a \"Discount correction\" credit memo). Credits are all other credit memos, such as billing corrections, goodwill, withdrawals and removed late fees. Collected is payments, after refunds and chargebacks. Still owed is what families owe today, including amounts not yet due. Collection rate is Collected ÷ (Collected + Still owed). Voided invoices, voided credit memos and sponsor-funded invoices aren't counted.",
      },
      {
        title: "See the invoices behind a row",
        description:
          "Click any row to list its invoices. A \"Showing:\" chip names your selection. Click the chip's x, or your browser's Back button, to return. The amounts are this row's share of each invoice, not the invoice total. Click an invoice to open it. Click \"Export invoices\" to download the list. If there are a lot of invoices, only the first ones are shown. Narrow the months to see the rest.",
      },
      {
        title: "Export or print",
        description:
          "\"Export CSV\" downloads the table plus a Total row, with \"Discounts\" split into \"Sibling discounts\", \"Mid-month proration\" and \"Other discounts\", and extra columns: \"Partner fee\", \"Refunds & chargebacks (already in Collected)\", \"Collection rate %\", \"Invoices\", \"Families\" and \"Students\". \"Print\" prints the whole report with every column. When more than one month in your range has charges, a \"By billing month\" table also appears (it isn't in the CSV).",
      },
    ],
  },
];

// =============================================
// Combine all guides
// =============================================

export const allGuides: Guide[] = [
  ...gettingStartedGuides,
  ...campusScheduleGuides,
  ...classesGuides,
  ...curriculumGuides,
  ...academicsGuides,
  ...messagingGuides,
  ...studentsGuides,
  ...admissionsGuides,
  ...billingGuides,
  ...attendanceGuides,
  ...hifdhGuides,
  ...reportsGuides,
  ...settingsGuides,
  ...newGuidesA,
  ...newGuidesB,
];

/** Every guide, grouped in the order the Help Center lists categories. */
export const guides: Guide[] = categories.flatMap((c) =>
  allGuides.filter((g) => g.category === c.key),
);

/** The roles a guide is written for (office roles unless it says otherwise). */
export function guideAudience(g: Guide): readonly UserRole[] {
  return g.audience ?? OFFICE_ROLES;
}

/**
 * Who a guide is for, in words a reader understands. Shown when someone
 * opens a guide their role can't use.
 */
export function audienceLabel(g: Guide): string {
  const a = guideAudience(g);
  // "the school office", as the guides call admins, principals and staff;
  // "school staff" would read as including teachers.
  if (a.includes("teacher") && a.includes("parent")) {
    return "teachers, parents and the school office";
  }
  if (a.includes("teacher")) return "teachers and the school office";
  if (a.includes("parent")) return "parents and the school office";
  return "the school office";
}

/**
 * Can someone with this role read the guide? An unknown or missing role
 * reads nothing, so a half-loaded session never flashes office guides.
 */
export function canRoleReadGuide(
  g: Guide,
  role: string | null | undefined,
): boolean {
  if (!role) return false;
  return (guideAudience(g) as readonly string[]).includes(role);
}

/** Every guide this role can read, in Help Center order. */
export function guidesForRole(role: string | null | undefined): Guide[] {
  return guides.filter((g) => canRoleReadGuide(g, role));
}

/** Categories that have at least one guide this role can read. */
export function categoriesForRole(
  role: string | null | undefined,
): GuideCategory[] {
  const readable = guidesForRole(role);
  return categories.filter((c) => readable.some((g) => g.category === c.key));
}

/**
 * Helper to get guides by category. Pass a role to list only the guides
 * that role can read.
 */
export function getGuidesByCategory(
  categoryKey: string,
  role?: string | null,
): Guide[] {
  const pool = role === undefined ? guides : guidesForRole(role);
  return pool.filter((g) => g.category === categoryKey);
}

/**
 * Helper to get a guide by slug
 */
export function getGuideBySlug(slug: string): Guide | undefined {
  return guides.find((g) => g.slug === slug);
}

/**
 * A guide's secondary text (step tips, FAQ, troubleshooting, empty-state
 * note) joined for search. Much of the detail people look for lives here.
 */
export function guideExtraText(g: Guide): string {
  return [
    ...g.steps.map((s) => s.tip ?? ""),
    ...(g.faq ?? []).flatMap((f) => [f.question, f.answer]),
    ...(g.troubleshooting ?? []).flatMap((t) => [t.problem, t.solution]),
    g.emptyState ?? "",
  ].join("\n");
}

/**
 * Fuzzy search: matches if all words in query appear somewhere in the text
 * (in any order), with typo tolerance for words 4+ chars.
 */
function fuzzyMatch(lower: string, query: string): boolean {
  const words = query.split(/\s+/).filter(Boolean);
  return words.every((word) => {
    if (lower.includes(word)) return true;
    // Typo tolerance: if word is 4+ chars, allow 1 char difference
    if (word.length >= 4) {
      const textWords = lower.split(/\s+/);
      return textWords.some(
        (tw) =>
          tw.startsWith(word.slice(0, 3)) &&
          levenshtein(tw.slice(0, word.length), word) <= 1,
      );
    }
    return false;
  });
}

/** Simple Levenshtein distance for short strings */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
  }
  return dp[m][n];
}

interface SearchText {
  title: string;
  description: string;
  steps: string;
  extra: string;
  category: string;
}

// Lower-cased once per guide, not on every keystroke.
const searchText = new Map<Guide, SearchText>();
function textOf(g: Guide): SearchText {
  let t = searchText.get(g);
  if (!t) {
    t = {
      title: g.title.toLowerCase(),
      description: g.description.toLowerCase(),
      steps: g.steps
        .map((s) => `${s.title} ${s.description}`)
        .join(" ")
        .toLowerCase(),
      extra: guideExtraText(g).toLowerCase(),
      category: (categories.find((c) => c.key === g.category)?.label ?? "").toLowerCase(),
    };
    searchText.set(g, t);
  }
  return t;
}

/**
 * Search guides, best match first: title, then description, steps, the
 * category name, and lastly step tips, FAQ and troubleshooting. Words may
 * come in any order, and a one-letter typo in a longer word still matches.
 * An empty query returns nothing. Pass a role to search only the guides
 * that role can read.
 */
export function searchGuides(query: string, role?: string | null): Guide[] {
  const lower = query.toLowerCase().trim();
  if (!lower) return [];
  const pool = role === undefined ? guides : guidesForRole(role);
  return pool
    .map((guide) => {
      const t = textOf(guide);
      let score = 0;
      if (t.title.includes(lower)) score += 100;
      else if (fuzzyMatch(t.title, lower)) score += 60;
      if (t.description.includes(lower)) score += 40;
      else if (fuzzyMatch(t.description, lower)) score += 20;
      if (t.steps.includes(lower) || fuzzyMatch(t.steps, lower)) score += 15;
      if (t.category.includes(lower)) score += 10;
      if (t.extra.includes(lower) || fuzzyMatch(t.extra, lower)) score += 5;
      return { guide, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((item) => item.guide);
}
